import { inject, Injectable } from "@angular/core";
import {
  or,
  and,
  doc,
  query,
  Query,
  where,
  docData,
  getDocs,
  Firestore,
  getDocsFromServer,
  getDocsFromCache,
  updateDoc,
  FieldPath,
  collection,
  onSnapshot,
  runTransaction,
  writeBatch,
  deleteField,
  DocumentData,
  WhereFilterOp,
  collectionData,
  CollectionReference,
} from "@angular/fire/firestore";
import {
  ref,
  listAll,
  Storage,
  getStorage,
  uploadBytes,
  deleteObject,
  getDownloadURL,
} from "@angular/fire/storage";
import { Store } from "@ngrx/store";
import {
  endAt,
  limit,
  addDoc,
  getDoc,
  setDoc,
  startAt,
  orderBy,
  deleteDoc,
  Timestamp,
  arrayUnion,
  FieldValue,
  startAfter,
  Unsubscribe,
  collectionGroup,
  QueryConstraint,
  serverTimestamp,
  DocumentReference,
  getCountFromServer,
  QueryDocumentSnapshot,
  documentId,
  increment,
} from "firebase/firestore";
import { getBytes, uploadBytesResumable } from "firebase/storage";
import {
  of,
  map,
  tap,
  from,
  take,
  timer,
  toArray,
  forkJoin,
  mergeMap,
  switchMap,
  catchError,
  Observable,
  throwError,
  bufferCount,
  combineLatest,
  firstValueFrom,
} from "rxjs";
import {
  ActionModel,
  AssetDetails,
  FindingModel,
  AssessmentModel,
  ActionAssessmentColorModel,
  AllFindingsAndActionsModel,
  FindingAssessmentColorModel,
  SiteAssessmentFindingsActionsModel,
} from "src/app/store/models/assesment.model";
import {
  ZoneMasterTypeModel,
  AddZoneTypeResponseModel,
  GetZoneTypesResponseModel,
  ProductLineMasterTypeModel,
  AddProductLineResponseModel,
  DeleteZoneTypeResponseModel,
  UpdateZoneTypeResponseModel,
  GetProductLinesResponseModel,
  DeleteProductLineResponseModel,
  UpdateProductLineResponseModel,
  AddTransportsExplosiveResponseModel,
  TransportsExplosivesMasterTypeModel,
  GetTransportsExplosivesResponseModel,
  DeleteTransportsExplosiveResponseModel,
  UpdateTransportsExplosiveResponseModel,
} from "src/app/store/models/site-master.model";
import {
  RoleModel,
  RoleTypeEnum,
  RoleNameEnum,
  RoleAccessModel,
  RoleMasterModel,
  RoleModuleAccessModel,
  RoleMasterAccessListModel,
} from "src/app/store/models/role.model";
import {
  UserModel,
  UserRolesModel,
  DeviceInfoModel,
  EditorUserModel,
  MapUsersByGroup,
  ProfileImageModel,
  PlantAssignedModel,
  CheckedInLocationModel,
  CountryAccessListModel,
} from "src/app/store/models/user.model";
import {
  SiteModel,
  AssetModel,
  ZoneNameModel,
  CoordinateModel,
  CostCenterModel,
  SiteFencingModel,
  ZoneFencingModel,
  AssetCategoryModel,
  SubAssetMasterModel,
  AssessmentAssetModel,
  CountryEmergencyContacts,
  ConfigureSiteZoneNameModel,
} from "src/app/store/models/asset.model";
import {
  EIUserModel,
  EIUserDataModel,
  EIRatingUpdateModel,
  EnergyIsolationProcedureModel,
} from "src/app/store/models/energy-isolation.model";
import { LanguageModel } from "src/app/store/models/language.model";
import { FeatureFlagModel } from "src/app/store/models/featureFlag.model";
import { DefectType, GuidanceItem } from "src/app/store/models/guidance.model";
import {
  BannedWasteItem,
  HazardsDocument,
  OCRLabelMapping,
  HazardsInventoryItem,
  MaterialRegistrationItem,
  MaterialControlConfigModel,
} from "src/app/store/models/material-master.model";
import {
  MM_StateInfo,
  MM_HazardItem,
  HazardsInfoDoc,
  MM_MaterialTypes,
  PdfInputHelpFile,
  MM_InventoryModel,
  MM_LinkedAssetModel,
  BannedWasteItemModel,
  MM_Hazard_HealthModel,
  OCRExtractedDataModel,
  ocrNotMappedDataModel,
  HazardDisplayItemModel,
  MMInventoryMasterModel,
  MM_Hazard_EnvironmentModel,
  MM_OCRPropertiesLevelMapping,
  MM_RiskLevelRequestUpdateInfo_Model,
} from "src/app/store/models/materialManagement.model";
import { AssessmentAssetDetectionModel } from "src/app/store/models/mmAssessment.model";
import {
  controlSubAssetItem,
  MM_ControlConfigModel,
  MM_ControlDuplicateModel,
  MM_ControlInventoryModel,
  SaveMMControlInventoryResponseModel,
} from "src/app/store/models/mmControlManagement.model";
import {
  ModulesModel,
  ModulesWithSubmodulesModel,
} from "src/app/store/models/module.model";
import { NotificationLoggerModel } from "src/app/store/models/notification.model";
import { SiteDownloadModel } from "src/app/store/models/offline-configuration.model";
import {
  PerformanceData,
  EmailHistoryModel,
  EmailHistoryPayload,
} from "src/app/store/models/performance-data.model";
import { ConnectionLocationMasterModel } from "src/app/store/models/sap.model";
import { StatusEnumModel } from "src/app/store/models/status.model";
import {
  convertTimestampToDate,
  updatedAtFormatToDDMMYYYY,
} from "src/app/utils/dateTime.utils";
import { v4 as uuidv4 } from "uuid";
import { environment } from "src/environments/environment";
import { TenantService } from "../tenant-service/tenant.service";
import { HttpService } from "../http-service/http-client.service";
import { StorageService } from "../storage-service/storage.service";
import { IAdminRoleData } from "src/app/store/models/adminRole.model";
import { GuidanceOfflineModel } from "src/app/store/models/offline-configuration.model";
import { OfflineManagerService } from "../offline-manager-service/offline-manager.service";
import { SiteSegmentModel } from "src/app/store/models/siteSegment.model";
import {
  SecurityPoiModel,
  SecurityCategoryModel,
} from "src/app/store/models/site-security-mapping.model";
import { SecurityPersonnelTotalsModel } from "src/app/store/models/security-mapping.model";
// ** A country with no site filled in yet reads as zero, not as a missing number.
const EMPTY_SECURITY_PERSONNEL_TOTALS: SecurityPersonnelTotalsModel = {
  totalFtes: 0,
  siteCount: 0,
  securityGuards: 0,
  otherPersonnel: 0,
  securityManagers: 0,
  publicSecurityGuards: 0,
  privateSecurityGuards: 0,
};

// ** A field left empty, or holding anything but a number, counts as none.
function toCount(value: number | null | undefined): number {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

export interface MMLockModel {
  userId: string;
  userName: string;
  controlId: string;
  timestamp: FieldValue | Timestamp;
}

export interface QueryCondition {
  field: string;
  operator: WhereFilterOp;
  value: string | boolean | number;
}

// One recipient entry under a location key in performance_report_email.
export interface ReportRecipientEntry {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  sender_name?: string | string[];
  subscribed_user?: boolean;
  shield_user?: boolean;
}

export interface FlameCategory {
  value: string;
  label: string;
}

// Users deactivated from admin carry status "inactive"; older docs may have no status
// at all, so only the explicit "inactive" value is excluded from user pickers.
export const isActiveUser = (user?: { status?: string }): boolean =>
  user?.status !== "inactive";

// How many users one Security mapping permission search returns, and the shortest
// term worth a query (one letter would match most of the collection)
const USER_SEARCH_LIMIT = 20;
const MIN_USER_SEARCH_LENGTH = 3;

// The country_security_expert entry exactly as it is stored in a user's roles[].
// Firestore matches an object inside an array only in full, so granting the role
// and querying for it must build it here, from this one definition.
export const countrySecurityExpertRole = (
  countryUniqueId: string,
): UserRolesModel => ({
  locationId: countryUniqueId,
  role: RoleTypeEnum.CountrySecurityExpert,
  roleName: RoleNameEnum.CountrySecurityExpert,
});

// A users document as the Security mapping permission modal needs it: the roles are
// kept (they are rewritten on save) and the country's role is flagged
const toCountryPermissionUser = (
  user: EditorUserModel,
  countryUniqueId: string,
): EditorUserModel => {
  const roles = user.roles ?? [];
  return {
    roles,
    id: user.id,
    email: user.email,
    adminRole: user.adminRole,
    profileImage: user.profileImage,
    updatedAt: updatedAtFormatToDDMMYYYY(user.updatedAt),
    name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
    isCountrySecurityExpert: roles.some(
      (role: UserRolesModel) =>
        role.role === RoleTypeEnum.CountrySecurityExpert &&
        role.locationId === countryUniqueId,
    ),
  };
};

@Injectable({ providedIn: "root" })
export class FirebaseService {
  private store = inject(Store);
  private readonly storage = inject(Storage);
  private tenantService = inject(TenantService);
  private readonly firestore = inject(Firestore);
  private storageService = inject(StorageService);
  private readonly httpService = inject(HttpService);
  private offlineService = inject(OfflineManagerService);

  private siteUnsub: Unsubscribe | null = null;

  constructor() {}

  formateDate(date: Timestamp) {
    if (!date) return "";
    return convertTimestampToDate(
      date as {
        nanoseconds: number;
        seconds: number;
      },
    );
  }

  private getTenantDoc(path: string): Observable<DocumentReference> {
    return from(this.tenantService.getMyDb()).pipe(map((db) => doc(db, path)));
  }

  private getTenantCollection(path: string): Observable<CollectionReference> {
    return from(this.tenantService.getMyDb()).pipe(
      map((db) => collection(db, path)),
    );
  }

  private getSubCollectionRef(
    docRef: DocumentReference,
    subCollectionName: string,
  ): CollectionReference {
    return collection(docRef, subCollectionName);
  }

  listenCheckInSites(user: UserModel): Observable<SiteModel[]> {
    return new Observable<SiteModel[]>((observer) => {
      this.getTenantCollection("sites").subscribe((sitesRef) => {
        const sitesQuery = query(sitesRef, where("isActive", "==", true));

        // Cleanup old subscription if already listening
        if (this.siteUnsub) this.siteUnsub();

        this.siteUnsub = onSnapshot(
          sitesQuery,
          (querySnapshot) => {
            const sites = querySnapshot.docs.map(
              (doc) =>
                ({
                  id: doc?.id,
                  ...doc?.data(),
                }) as SiteModel,
            );
            observer.next(sites);
          },
          (error) => observer.error(error),
        );
      });

      // Cleanup when unsubscribed
      return () => {
        if (this.siteUnsub) this.siteUnsub();
      };
    });
  }

  getUser(email: string): Observable<UserModel> {
    const userDocRef = this.getTenantDoc(`users/${email}`);

    return userDocRef.pipe(
      switchMap((ref) => docData(ref, { idField: "id" })),
      switchMap((userData) => {
        const user = userData as UserModel;

        /* ---------------- Selected Location ---------------- */
        const selectedLocationId =
          user?.checkedInLocation?.site?.locationId ??
          user?.defaultLocation?.site?.locationId ??
          "";

        /* ---------------- Admin Role ---------------- */
        const adminAccess$ = user?.adminRole
          ? this.getTenantDoc(`roles_master/${user?.adminRole}`).pipe(
              switchMap((ref) => docData(ref)),
              map((doc) => doc as RoleMasterModel),
            )
          : of(null);

        /* ---------------- User Roles ---------------- */
        const userRoles$ = user?.roles?.length
          ? combineLatest(
              user?.roles.map((userRole) =>
                this.getTenantDoc(`roles_master/${userRole?.role}`).pipe(
                  switchMap((ref) => docData(ref)),
                  map((roleDoc) => {
                    const roleData = roleDoc as RoleMasterModel | undefined;
                    return roleData
                      ? ({
                          ...roleData,
                          ...userRole,
                        } as RoleModel)
                      : null;
                  }),
                ),
              ),
            )
          : of([] as (RoleModel | null)[]);

        /* ---------------- Expert Check ---------------- */
        const expertRoleKeys = ["mm_expert", "ei_expert", "structural_expert", "flame_expert"];
        const isExpert = user?.roles?.some((role) =>
          expertRoleKeys.includes(role?.role),
        );

        /* ---------------- Base User Role ---------------- */
        const baseUserAccess$ = isExpert
          ? of(null)
          : this.getTenantDoc(`roles_master/user`).pipe(
              switchMap((ref) => docData(ref)),
              map((doc) => doc as RoleMasterModel),
            );

        return combineLatest([userRoles$, adminAccess$, baseUserAccess$]).pipe(
          map(([userRolesList, adminAccess, baseUserAccess]) => {
            /* =====================================================
             1️⃣ Split roles (Updated to include Experts)
          ===================================================== */
            const allRoles: RoleModel[] = (userRolesList ?? []).filter(
              (role): role is RoleModel => !!role,
            );

            const selectedLocationRoles = allRoles.filter(
              (role) =>
                String(role?.locationId ?? "") ===
                  String(selectedLocationId ?? "") ||
                role?.role === "pr_editor" ||
                role?.role === "pr_approver" ||
                role?.role === "platform_admin" ||
                role?.role === "platform_user" ||
                expertRoleKeys.includes(role?.role ?? ""), // Ensure expert roles are kept
            );

            /* =====================================================
             2️⃣ Normalize access
          ===================================================== */
            const normalizeAccess = (
              access?: Partial<RoleAccessModel>,
            ): RoleAccessModel => {
              const normalized = {} as RoleAccessModel;
              const accessKeys = Object.keys(
                access ?? {},
              ) as (keyof RoleAccessModel)[];
              for (const key of accessKeys) {
                normalized[key] = Boolean(access![key]);
              }
              return normalized;
            };

            /* =====================================================
             3️⃣ Merge ACCESS (selected location only)
          ===================================================== */
            const accessSources: RoleAccessModel[] = [
              normalizeAccess(baseUserAccess?.access),
              normalizeAccess(adminAccess?.access),
              ...selectedLocationRoles.map((role) =>
                normalizeAccess(role?.access),
              ),
            ];

            const mergedAccess: RoleAccessModel = accessSources.reduce(
              (merged, source) => {
                for (const key of Object.keys(source)) {
                  const k = key as keyof RoleAccessModel;
                  merged[k] = merged[k] || source[k];
                }
                return merged;
              },
              {} as RoleAccessModel,
            );

            //  4️⃣ Merge PERMISSIONS (ALL roles) - FIXED UNION
            // country_security_expert is country-scoped: its tree is left out of the
            // union (which is location-blind) so its edit doesn't leak to every
            // country. SecurityMappingAccessService adds it back for the country
            // that is checked in, from roles[].permissions below.
            const allPermissions: RoleModuleAccessModel[] = [
              ...(adminAccess?.permissions ?? []),
              ...(user?.adminRole ? [] : (baseUserAccess?.permissions ?? [])),
              ...allRoles
                .filter(
                  (role) => role?.role !== RoleTypeEnum.CountrySecurityExpert,
                )
                .flatMap((role) => role?.permissions ?? []),
            ];

            const mergedPermissions = this.mergeModulesById(allPermissions);

            /* =====================================================
             5️⃣ Each role keeps its own tree (index-aligned with the user doc's
                roles, so a role whose roles_master doc is missing stays put)
          ===================================================== */
            const enrichedRoles: UserRolesModel[] = (user?.roles ?? []).map(
              (userRole, index) => ({
                ...userRole,
                permissions: (userRolesList ?? [])[index]?.permissions ?? [],
              }),
            );

            /* =====================================================
             6️⃣ Enriched user
          ===================================================== */
            const enrichedUser: UserModel = {
              ...user,
              roles: enrichedRoles,
              adminAccess: {
                access: mergedAccess,
                permissions: mergedPermissions,
              },
            };

            return enrichedUser;
          }),
        );
      }),
    );
  }

  GetUserByEmail(email: string): Observable<UserModel | null> {
    if (!email) return of(null);

    return this.getTenantDoc(`users/${email}`).pipe(
      switchMap((ref) => from(getDoc(ref))),
      map((snapshot) => {
        if (!snapshot.exists()) return null;

        const data = snapshot.data() as UserModel;
        return {
          email: data?.email,
          lastName: data?.lastName,
          firstName: data?.firstName,
          person: data?.person,
          profileImage: data?.profileImage?.src,
          status: data?.status,
        } as UserModel;
      }),
    );
  }

  mergeModulesById(modules: RoleModuleAccessModel[]): RoleModuleAccessModel[] {
    const map = new Map<string, RoleModuleAccessModel>();

    for (const mod of modules) {
      if (!mod?.moduleId) continue;

      if (!map.has(mod.moduleId)) {
        map.set(mod.moduleId, {
          ...mod,
          submodules: this.mergeModulesById(mod.submodules ?? []),
        });
      } else {
        const existing = map.get(mod.moduleId)!;

        // merge access flags (logical OR)
        const mergedAccess: RoleMasterAccessListModel = {
          ...(existing.access ?? {}),
          ...(mod.access ?? {}),
        };

        const accessKeys = Object.keys(
          mergedAccess,
        ) as (keyof RoleMasterAccessListModel)[];
        for (const key of accessKeys) {
          mergedAccess[key] =
            (existing.access?.[key] ?? false) || (mod.access?.[key] ?? false);
        }

        // recursively merge submodules
        const mergedSubmodules = this.mergeModulesById([
          ...(existing.submodules ?? []),
          ...(mod.submodules ?? []),
        ]);

        map.set(mod.moduleId, {
          ...existing,
          ...mod,
          access: mergedAccess,
          submodules: mergedSubmodules,
        });
      }
    }

    return Array.from(map.values());
  }
  updateUser(email: string, token: string): Observable<void> {
    const userDocRef = this.getTenantDoc(`users/${email}`);
    return userDocRef.pipe(
      switchMap((userDocRef) => from(updateDoc(userDocRef, { token: token }))),
    );
  }

  GetCountryUsers(locationId: string) {
    if (!locationId) return of([] as UserModel[]);

    return this.getTenantCollection("users").pipe(
      switchMap((usersRef: CollectionReference) => {
        const userQuery = query(
          usersRef,
          where("defaultLocation.country.locationId", "==", locationId),
          // or(
          //   where("defaultLocation.country.locationId", "==", locationId),
          //   where("checkedInLocation.country.locationId", "==", locationId),
          // ),
        );

        return from(getDocs(userQuery)).pipe(
          map((snapshot) => {
            const users: UserModel[] = snapshot.docs
              .map((doc) => doc?.data() as UserModel)
              .filter(isActiveUser)
              .map((data) => {
                return {
                  email: data?.email,
                  lastName: data?.lastName,
                  firstName: data?.firstName,
                  person: data?.person,
                  profileImage: data?.profileImage?.src,
                } as UserModel;
              });

            return users;
          }),
        );
      }),
    );
  }

  GetUnitUsers(locationId: string) {
    if (!locationId) return of([] as UserModel[]);

    return this.getTenantCollection("users").pipe(
      switchMap((usersRef: CollectionReference) => {
        const userQuery = query(
          usersRef,
          where("defaultLocation.unit.locationId", "==", locationId),
          // or(
          //   where("defaultLocation.unit.locationId", "==", locationId),
          //   where("checkedInLocation.unit.locationId", "==", locationId),
          // ),
        );

        return from(getDocs(userQuery)).pipe(
          map((snapshot) => {
            const users: UserModel[] = snapshot.docs.map((doc) => {
              const data = doc?.data() as UserModel;
              return {
                email: data?.email,
                lastName: data?.lastName,
                firstName: data?.firstName,
                profileImage: data?.profileImage?.src,
              } as UserModel;
            });

            return users;
          }),
        );
      }),
    );
  }

  GetSiteUsers(locationId: string) {
    if (!locationId) return of([] as UserModel[]);

    return this.getTenantCollection("users").pipe(
      switchMap((usersRef: CollectionReference) => {
        const userQuery = query(
          usersRef,
          where("defaultLocation.site.locationId", "==", locationId),
          // or(
          //   where("defaultLocation.site.locationId", "==", locationId),
          //   where("checkedInLocation.site.locationId", "==", locationId),
          // ),
        );

        return from(getDocs(userQuery)).pipe(
          map((snapshot) => {
            const users: UserModel[] = snapshot.docs.map((doc) => {
              const data = doc?.data() as UserModel;
              return {
                email: data?.email,
                lastName: data?.lastName,
                firstName: data?.firstName,
                profileImage: data?.profileImage?.src,
              } as UserModel;
            });

            return users;
          }),
        );
      }),
    );
  }

  // ** Device Login Tracking ** //
  async RegisterUserDevice(
    email: string,
    device: DeviceInfoModel,
  ): Promise<void> {
    const usersRef = await firstValueFrom(this.getTenantCollection("users"));
    const userDocRef = doc(usersRef, email);
    const userSnap = await getDoc(userDocRef);

    const existingDevices: DeviceInfoModel[] =
      userSnap.data()?.["devices"] ?? [];

    const deviceIndex = existingDevices.findIndex(
      (d) => d?.deviceId === device?.deviceId,
    );

    if (deviceIndex !== -1) {
      existingDevices[deviceIndex] = device;
    } else {
      existingDevices.push(device);
    }

    await updateDoc(userDocRef, { devices: existingDevices });
  }
  // ** Device Login Tracking ** //

  getAllUsers(
    pageSize: number = 20,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
  ): Observable<{
    users: UserModel[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  }> {
    const usersColRef = this.getTenantCollection("users");

    return usersColRef.pipe(
      switchMap((usersColRef) => {
        const constraints: QueryConstraint[] = [
          orderBy("email"),
          limit(pageSize),
        ];
        if (lastDoc) {
          constraints.push(startAfter(lastDoc));
        }

        const q = query(usersColRef, ...constraints);

        return from(getDocs(q)).pipe(
          map((snapshot) => {
            const users = snapshot.docs
              .map((doc) => ({
                id: doc?.id,
                ...(doc?.data() as UserModel),
              }))
              .filter(
                (user) => !!user.firstName?.trim() && !!user.lastName?.trim(),
              );

            const newLastDoc =
              snapshot.docs.length > 0
                ? snapshot.docs[snapshot.docs.length - 1]
                : null;

            return { users, lastDoc: newLastDoc };
          }),
        );
      }),
    );
  }
  getUsersPaginated(
    pageSize: number,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
  ): Observable<{
    users: UserModel[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  }> {
    const usersColRef = this.getTenantCollection("users");

    return usersColRef.pipe(
      switchMap((ref) => {
        const constraints: QueryConstraint[] = [
          orderBy("email"),
          limit(pageSize),
        ];
        if (lastDoc) constraints.push(startAfter(lastDoc));

        return from(getDocs(query(ref, ...constraints))).pipe(
          map((snap) => {
            const users = snap.docs
              .map((doc) => ({
                id: doc?.id,
                ...(doc?.data() as UserModel),
              }))
              .filter(
                (user) => !!user.firstName?.trim() && !!user.lastName?.trim(),
              ); // ✅ filter here

            const newLastDoc =
              snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;

            return { users, lastDoc: newLastDoc };
          }),
        );
      }),
    );
  }

  searchUsers(
    searchTerm: string,
    pageSize: number = 20,
  ): Observable<UserModel[]> {
    const usersColRef = this.getTenantCollection("users");

    return usersColRef.pipe(
      switchMap((ref) => {
        const emailQuery = query(
          ref,
          orderBy("email"),
          where("email", ">=", searchTerm),
          where("email", "<=", searchTerm + "\uf8ff"),
          limit(pageSize),
        );

        const firstNameQuery = query(
          ref,
          orderBy("firstName"),
          where("firstName", ">=", searchTerm),
          where("firstName", "<=", searchTerm + "\uf8ff"),
          limit(pageSize),
        );

        return combineLatest([
          from(getDocs(emailQuery)),
          from(getDocs(firstNameQuery)),
        ]).pipe(
          map(([emailSnap, firstNameSnap]) => {
            const users = [
              ...emailSnap.docs.map((doc) => ({
                id: doc?.id,
                ...(doc?.data() as UserModel),
              })),
              ...firstNameSnap.docs.map((doc) => ({
                id: doc?.id,
                ...(doc?.data() as UserModel),
              })),
            ];

            // Deduplicate by id
            const unique = new Map(users?.map((user) => [user?.id, user]));
            return Array.from(unique?.values()).filter(
              (user) => !!user.firstName?.trim() && !!user?.lastName?.trim(),
            );
          }),
        );
      }),
    );
  }

  searchGlobalUsers(
    searchTerm: string,
    adminUser: UserModel,
    pageSize: number = 20,
  ): Observable<UserModel[]> {
    const usersColRef = this.getTenantCollection("users");
    const normalizedTerm = searchTerm.trim().toLowerCase();

    return usersColRef.pipe(
      switchMap((ref) => {
        const queries = this._buildSearchQueries(ref, normalizedTerm, pageSize);

        return combineLatest(queries.map((q) => from(getDocs(q)))).pipe(
          map((snapshots) => {
            const users = snapshots.flatMap((snap) =>
              snap.docs.map((doc) => ({
                id: doc?.id,
                ...(doc?.data() as UserModel),
              })),
            );

            const unique = new Map(users.map((user) => [user.id, user]));
            const deduped = Array.from(unique.values()).filter(
              (user) => !!user.firstName?.trim() && !!user?.lastName?.trim(),
            );

            const filtered = deduped.filter((user) =>
              this._matchesSearchTerm(user, normalizedTerm),
            );

            return this._filterUsersByAdminRole(filtered, adminUser);
          }),
        );
      }),
    );
  }

  private _buildSearchQueries(
    ref: CollectionReference,
    normalizedTerm: string,
    pageSize: number,
  ): Query[] {
    const queries: Query[] = [];

    const spaceParts = normalizedTerm.split(/\s+/).filter(Boolean);
    const dotParts = normalizedTerm.split(".").filter(Boolean);

    const firstToken = spaceParts[0] ?? dotParts[0] ?? normalizedTerm;

    queries.push(
      query(
        ref,
        orderBy("firstName"),
        where("firstName", ">=", firstToken),
        where("firstName", "<=", firstToken + "\uf8ff"),
        limit(pageSize),
      ),
    );
    queries.push(
      query(
        ref,
        orderBy("email"),
        where("email", ">=", firstToken),
        where("email", "<=", firstToken + "\uf8ff"),
        limit(pageSize),
      ),
    );

    return queries;
  }

  private _matchesSearchTerm(user: UserModel, normalizedTerm: string): boolean {
    const firstName = user.firstName?.trim().toLowerCase() ?? "";
    const lastName = user.lastName?.trim().toLowerCase() ?? "";
    const email = user.email?.trim().toLowerCase() ?? "";

    const hasDot = normalizedTerm.includes(".");
    const spaceParts = normalizedTerm.split(/\s+/).filter(Boolean);

    if (hasDot) {
      return email.startsWith(normalizedTerm);
    }

    if (spaceParts.length >= 2) {
      const [firstPart, secondPart] = spaceParts;

      const nameMatch =
        firstName.startsWith(firstPart) && lastName.startsWith(secondPart);

      const emailMatch =
        email.includes(firstPart) && email.includes(secondPart);

      return nameMatch || emailMatch;
    }

    return (
      firstName.startsWith(normalizedTerm) || email.startsWith(normalizedTerm)
    );
  }

  private _filterUsersByAdminRole(
    users: UserModel[],
    adminUser: UserModel,
  ): UserModel[] {
    const role = adminUser?.adminRole;

    if (role === "super_user") {
      return users;
    }

    if (role === "country_admin") {
      // We match against user.defaultLocation.country.locationName (case-insensitive)
      const allowedCountryNames = new Set(
        (adminUser.countryAccessList ?? [])
          .map((c: CountryAccessListModel) => c?.opco?.toLowerCase())
          .filter(Boolean),
      );

      return users.filter((user) => {
        const userCountry =
          user?.defaultLocation?.opco?.locationName?.toLowerCase()?.trim() ||
          user?.defaultLocation?.country?.locationName?.toLowerCase()?.trim();
        return !!userCountry && allowedCountryNames.has(userCountry);
      });
    }

    if (role === "admin") {
      // We match against user.defaultLocation.site.locationId
      const allowedSiteIds = new Set(
        (adminUser.plantAssigned ?? [])
          .map((p: PlantAssignedModel) => p?.unique_id?.trim())
          .filter(Boolean),
      );

      return users.filter((user) => {
        const userSiteId = user?.defaultLocation?.site?.locationId?.trim();
        return !!userSiteId && allowedSiteIds.has(userSiteId);
      });
    }

    // Unknown role — return nothing as a safe default
    return [];
  }

  searchUsersAssessment(
    searchTerm: string,
    pageSize: number = 20,
  ): Observable<UserModel[]> {
    const rawTerm = (searchTerm || "").trim();
    if (!rawTerm) return of([]);

    // Normalized search (lowercase, no spaces)
    const normalizedSearch = rawTerm.toLowerCase().replace(/\s+/g, "");

    const firstTokenOriginal = rawTerm.split(/\s+/)[0];
    const capitalizedToken =
      firstTokenOriginal.length > 0
        ? firstTokenOriginal[0].toUpperCase() +
          firstTokenOriginal.slice(1).toLowerCase()
        : "";

    // Take first few chars so queries are not too strict
    const queryPrefix = capitalizedToken.slice(0, 3);

    const usersColRef = this.getTenantCollection("users");

    return usersColRef.pipe(
      switchMap((ref) => {
        // Query by email using original term as prefix (optional)
        const emailQuery = query(
          ref,
          orderBy("email"),
          where("email", ">=", rawTerm),
          where("email", "<=", rawTerm + "\uf8ff"),
          limit(pageSize),
        );

        // First name prefix query
        const firstNameQuery = query(
          ref,
          orderBy("firstName"),
          where("firstName", ">=", queryPrefix),
          where("firstName", "<=", queryPrefix + "\uf8ff"),
          limit(pageSize),
        );

        // Last name prefix query
        const lastNameQuery = query(
          ref,
          orderBy("lastName"),
          where("lastName", ">=", queryPrefix),
          where("lastName", "<=", queryPrefix + "\uf8ff"),
          limit(pageSize),
        );

        return combineLatest([
          from(getDocs(emailQuery)),
          from(getDocs(firstNameQuery)),
          from(getDocs(lastNameQuery)),
        ]).pipe(
          map(([emailSnap, firstNameSnap, lastNameSnap]) => {
            const docs = [
              ...emailSnap.docs,
              ...firstNameSnap.docs,
              ...lastNameSnap.docs,
            ];

            const users = docs?.map((doc) => ({
              id: doc?.id,
              ...(doc?.data() as UserModel),
            }));

            // Deduplicate by id
            const unique = new Map<string, UserModel>(
              users?.map((user) => [user?.id, user]),
            );

            return Array.from(unique.values()).filter((user) => {
              if (!isActiveUser(user)) return false;

              const firstName = (user?.firstName || "").trim();
              const lastName = (user?.lastName || "").trim();

              // ✅ Only keep users that have at least first OR last name
              const hasName = !!(firstName || lastName);
              if (!hasName) return false;

              const first = firstName?.toLowerCase();
              const last = lastName?.toLowerCase();
              const email = (user?.email || "").toLowerCase();

              // Build normalized versions
              const firstKey = first?.replace(/\s+/g, "");
              const lastKey = last?.replace(/\s+/g, "");
              const fullKey = (first + last).replace(/\s+/g, "");
              const emailKey = email?.replace(/\s+/g, "");

              return (
                fullKey.includes(normalizedSearch) ||
                firstKey.includes(normalizedSearch) ||
                lastKey.includes(normalizedSearch) ||
                emailKey.includes(normalizedSearch)
              );
            });
          }),
        );
      }),
    );
  }

  //checkin preferences
  updateUserPreferences(email: string, preferences: any): Observable<void> {
    const userDocRef = this.getTenantDoc(`users/${email}`);
    return userDocRef.pipe(
      switchMap((userDocRef) => from(updateDoc(userDocRef, { preferences }))),
    );
  }
  sendRatingNotification(
    site: string,
    procedure_id: string,
    comments: string[],
    commentsDescription: string,
  ): Observable<void> {
    const triggerData = {
      site,
      procedure_id,
      comments,
      commentsDescription,
      createdDate: new Date().toISOString(),
      id: uuidv4(),
    };

    return this.getTenantCollection("ei_rating_notification_trigger").pipe(
      switchMap((colRef) => from(addDoc(colRef, triggerData))),
      map(() => void 0),
      catchError((err) => {
        console.error(err);
        return throwError(() => err);
      }),
    );
  }
  addNewEiExportInfo(
    region: string[],
    country: string[],
    sites: string[],
    viewerSites: string[],
    email: string,
  ): Observable<void> {
    const newData = {
      createdDate: new Date().toISOString(),
      id: uuidv4(),
      email: email,
      region: region,
      country: country,
      sites: sites,
      viewerSites: viewerSites,
    };

    return this.getTenantCollection("ei_export_info").pipe(
      switchMap((colRef) => from(addDoc(colRef, newData))),
      map(() => void 0),
      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }
  triggerGeneratePdf(id: string): Observable<void> {
    const triggerData = {
      id: id,
      createdDate: new Date().toISOString(),
    };

    return this.getTenantCollection("ei_pdf_trigger").pipe(
      switchMap((colRef) => from(addDoc(colRef, triggerData))),
      map(() => void 0),
      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }

  addFencing(siteId: string, coordinates: CoordinateModel): Observable<any> {
    const sitesDocRef = this.getTenantDoc(`sites/${siteId}`);

    return sitesDocRef.pipe(
      switchMap((ref) => from(getDoc(ref))),
      switchMap((siteSnapshot) => {
        if (!siteSnapshot.exists()) {
          throw new Error("Site not found");
        }

        const existingCoordinates =
          siteSnapshot.data()?.["locations"]?.site?.coordinate || {};

        let isSiteUpdated = false;
        let isZoneUpdated = false;

        let _site_coordinates = existingCoordinates.site_coordinates?.map(
          (cords: SiteFencingModel) => {
            let site_coordinates = coordinates.site_coordinates;
            const selected = site_coordinates?.find(
              (site) => site.id === cords.id,
            );
            if (selected) {
              isSiteUpdated = true;
              return selected;
            }

            return cords;
          },
        );

        let _zone_coordinates = Array.isArray(
          existingCoordinates.zone_coordinates,
        )
          ? existingCoordinates.zone_coordinates?.map(
              (cords: ZoneFencingModel) => {
                let zone_coordinates = coordinates?.zone_coordinates;
                const selected = zone_coordinates?.find(
                  (zone) => zone?.id === cords?.id,
                );
                if (selected) {
                  isZoneUpdated = true;
                  return selected;
                }
                return cords;
              },
            )
          : [];

        let _zone_names = existingCoordinates.zone_names?.map(
          (cords: ZoneNameModel) => {
            let zone_names = coordinates.zone_names;
            const selected = zone_names?.find((zone) => zone.id === cords.id);
            if (selected) {
              isZoneUpdated = true;
              return selected;
            }
            return cords;
          },
        );

        let loggedInUser: UserModel = {};
        this.store.select("user").subscribe({
          next: (user: UserModel) => {
            if (user) {
              loggedInUser = user;
            }
          },
        });

        let _site: SiteModel = siteSnapshot?.data() as SiteModel;

        if (coordinates.site_coordinates?.[0]?.coordinates) {
          const uniqueSiteId = _site.locations?.site?.unique_id as string;
          let fencing: {
            uniqueSiteId: string;
            id?: string;
            siteCoordinates: {
              lat: number;
              lng: number;
            }[];
          } = {
            uniqueSiteId: uniqueSiteId,
            siteCoordinates: coordinates.site_coordinates?.[0]?.coordinates as {
              lat: number;
              lng: number;
            }[],
          };

          if (coordinates.site_coordinates?.[0]?.id) {
            fencing.id = coordinates.site_coordinates?.[0]?.id;
          }
          return this.httpService.ConfigureSiteFencing(fencing).pipe(
            switchMap((response) => {
              if (response?.status) {
                let selectedSite: SiteModel = {};
                this.store.select("site").subscribe({
                  next: (site: SiteModel) => {
                    if (site) {
                      selectedSite = site;
                    }
                  },
                });

                let cords = response?.data ?? [];
                const updatedSite = {
                  ...selectedSite,
                  locations: {
                    ...selectedSite.locations,
                    site: {
                      ...selectedSite.locations?.site,
                      coordinate: {
                        ...selectedSite.locations?.site?.coordinate,
                        site_coordinates: cords?.site_coordinates,
                        zone_coordinates: cords?.zone_coordinates,
                        zone_names: [
                          ...cords?.zone_names,
                          coordinates.zone_names,
                        ],
                      },
                    },
                  },
                } as SiteModel;
                // this.store.dispatch(UPDATE_SITE({ ...updatedSite }));
                return of(response);
              } else {
                throw new Error("Failed to configure site fencing");
              }
            }),
          );
        }

        let zoneName = coordinates.zone_names?.[0] ?? {
          category: "",
          aliasName: "",
          subType: "",
          describeZone: "",
          zoneOwner: {},
          costCenter: "",
        };

        let zone = {
          savePolygon: [
            {
              coordinates:
                coordinates?.zone_coordinates?.[0]?.coordinates || [],
            },
          ],
          siteName: _site.locations?.site?.location,
          siteUniqueId: _site.locations?.site?.unique_id,
        };

        const hasAllProperties =
          (zoneName.hasOwnProperty("aliasName") &&
            zoneName.hasOwnProperty("costCenter") &&
            zoneName.hasOwnProperty("subType") &&
            zoneName.hasOwnProperty("describeZone") &&
            zoneName.hasOwnProperty("zoneOwner") &&
            zoneName.aliasName !== "" &&
            zoneName.costCenter !== "" &&
            zoneName.category !== "" &&
            zoneName.subType !== "" &&
            zoneName.describeZone !== "" &&
            Object.keys(zoneName.zoneOwner || {}).length > 0) ||
          false;

        if (
          hasAllProperties &&
          Object.keys(zone)?.length &&
          Object.keys(zoneName)?.length
        ) {
          return this.httpService
            .ConfigureZone(
              zone as {
                siteName: string;
                siteUniqueId: string;
                savePolygon: { coordinates: { lat: number; lng: number }[] }[];
              },
            )
            .pipe(
              switchMap((response) => {
                if (response?.status) {
                  let results = response?.results ?? [];
                  let zoneId = results[0]?.id;

                  if (results?.length && results[0]?.id) {
                    return this.httpService
                      .ConfigureSiteZoneName({
                        id: results[0]?.id,
                        user: loggedInUser?.email,
                        category: zoneName.category,
                        subType: zoneName.subType,
                        zoneName: zoneName.aliasName,
                        describeZone: zoneName.describeZone,
                        zoneOwner: zoneName.zoneOwner,
                        costCenter: zoneName.costCenter,
                        siteUniqueId: _site.locations?.site?.unique_id,
                      } as ConfigureSiteZoneNameModel)
                      .pipe(
                        switchMap((response) => {
                          if (response?.status) {
                            let selectedSite: SiteModel = {};
                            this.store.select("site").subscribe({
                              next: (site: SiteModel) => {
                                if (site) {
                                  selectedSite = site;
                                }
                              },
                            });

                            let cords = response?.data ?? [];
                            const updatedSite = {
                              ...selectedSite,
                              locations: {
                                ...selectedSite.locations,
                                site: {
                                  ...selectedSite.locations?.site,
                                  coordinate: {
                                    ...selectedSite.locations?.site?.coordinate,
                                    site_coordinates: cords?.site_coordinates,
                                    zone_coordinates: cords?.zone_coordinates,
                                    zone_names: cords?.zone_names,
                                  },
                                },
                              },
                            } as SiteModel;
                            // this.store.dispatch(
                            //   UPDATE_SITE({ ...updatedSite }),
                            // );
                            return of(response);
                          } else {
                            throw new Error(
                              "Failed to configure site zone name",
                            );
                          }
                        }),
                      );
                  }
                }
                throw new Error("Failed to configure zone");
              }),
            );
        }
        throw new Error("Invalid zone configuration");
      }),
    );
  }

  checkedInLocation(
    userId: string,
    checkedInLocation?: CheckedInLocationModel | null,
  ): Observable<void> {
    return this.getTenantDoc(`users/${userId}`).pipe(
      switchMap((userRef) =>
        from(
          updateDoc(
            userRef,
            checkedInLocation
              ? {
                  checkedInLocation: {
                    country: checkedInLocation?.country,
                    site: checkedInLocation?.site,
                    unit: checkedInLocation?.unit,
                    opco: checkedInLocation?.opco,
                    region: checkedInLocation?.region,
                  },
                }
              : { checkedInLocation: deleteField() },
          ),
        ),
      ),
    );
  }

  // ** Get Sites
  getSites(id: string, user: UserModel): Observable<SiteModel[]> {
    if (!user.roles || user.roles.length === 0) of([]);

    const siteLocationIds = user?.roles?.map((role) => role.locationId);

    // const sitesQuery = query(
    //   sitesRef,
    //   where("isActive", "==", true),
    //   where("locationKeys", "array-contains", id),
    //   where("locations.site.unique_id", "in", siteLocationIds),
    // );

    return this.getTenantCollection("sites").pipe(
      switchMap((sitesRef) => {
        const sitesQuery = query(
          sitesRef,
          where("isActive", "==", true),
          where("locationKeys", "array-contains", id),
          where("locations.site.unique_id", "in", siteLocationIds),
        );
        return collectionData(sitesQuery, { idField: "id" }) as Observable<
          SiteModel[]
        >;
      }),
    );
  }

  getUserSites(siteLocationIds: string[]): Observable<SiteModel[]> {
    const uniqueIds = [...new Set(siteLocationIds)];

    if (!uniqueIds.length) {
      return of([]);
    }

    const chunks: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += 30) {
      chunks.push(uniqueIds.slice(i, i + 30));
    }

    return this.getTenantCollection("sites").pipe(
      switchMap((sitesRef) =>
        forkJoin(
          chunks.map((chunk) =>
            from(
              getDocs(
                query(
                  sitesRef,
                  where("isActive", "==", true),
                  where("locations.site.unique_id", "in", chunk),
                ),
              ),
            ),
          ),
        ),
      ),
      map((snapshots) =>
        snapshots.flatMap((snapshot) =>
          snapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as SiteModel,
          ),
        ),
      ),
    );
  }

  getSitesByUnitIds(unitIds: string[]): Observable<SiteModel[]> {
    return this.getTenantCollection("sites").pipe(
      switchMap((sitesRef) => {
        const q = query(
          sitesRef,
          where("isActive", "==", true),
          where("locations.unit.unique_id", "in", unitIds),
        );
        return collectionData(q, { idField: "id" }) as Observable<SiteModel[]>;
      }),
    );
  }

  getAllActiveSites(): Observable<SiteModel[]> {
    return this.getTenantCollection("sites").pipe(
      switchMap((ref) => {
        const q = query(ref, where("isActive", "==", true));
        return from(
          getDocs(q).then((snap) =>
            snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SiteModel),
          ),
        );
      }),
    );
  }

  // ** Live: Firestore pushes a new list whenever a site of these countries is edited,
  // ** so the screens reading it (Security personnel totals) don't need an app reload.
  getSitesByCountryIds(countryIds: string[]): Observable<SiteModel[]> {
    if (!countryIds.length) return of([]);
    return this.getTenantCollection("sites").pipe(
      switchMap((ref) => {
        const chunks: string[][] = [];
        for (let i = 0; i < countryIds.length; i += 30)
          chunks.push(countryIds.slice(i, i + 30));
        return combineLatest(
          chunks.map(
            (chunk) =>
              collectionData(
                query(
                  ref,
                  where("isActive", "==", true),
                  where("locations.country.unique_id", "in", chunk),
                ),
                { idField: "id" },
              ) as Observable<SiteModel[]>,
          ),
        ).pipe(map((lists) => lists.flat()));
      }),
    );
  }

  // ** Security personnel of every active site of the country, added up per field.
  getSecurityPersonnelTotalsByCountry(
    countryId: string,
  ): Observable<SecurityPersonnelTotalsModel> {
    const id = (countryId ?? "").trim();
    if (!id) return of(EMPTY_SECURITY_PERSONNEL_TOTALS);

    return this.getSitesByCountryIds([id]).pipe(
      map((sites) =>
        sites.reduce<SecurityPersonnelTotalsModel>((totals, site) => {
          const personnel = site.securityPersonnel;
          if (!personnel) return totals;

          const managers = toCount(personnel.securityManagers);
          const guards = toCount(personnel.securityGuards);
          const other = toCount(personnel.otherPersonnel);
          // ** The guards of a site whose own toggle is off don't count.
          const privateGuards = personnel.privateSecurity
            ? toCount(personnel.privateSecurityGuards)
            : 0;
          const publicGuards = personnel.publicSecurity
            ? toCount(personnel.publicSecurityGuards)
            : 0;

          return {
            siteCount: totals.siteCount + 1,
            securityGuards: totals.securityGuards + guards,
            otherPersonnel: totals.otherPersonnel + other,
            securityManagers: totals.securityManagers + managers,
            totalFtes: totals.totalFtes + managers + guards + other,
            publicSecurityGuards: totals.publicSecurityGuards + publicGuards,
            privateSecurityGuards: totals.privateSecurityGuards + privateGuards,
          };
        }, EMPTY_SECURITY_PERSONNEL_TOTALS),
      ),
    );
  }

  getSitesByRegionIds(regionIds: string[]): Observable<SiteModel[]> {
    if (!regionIds.length) return of([]);
    return this.getTenantCollection("sites").pipe(
      switchMap(async (ref) => {
        const chunks: string[][] = [];
        for (let i = 0; i < regionIds.length; i += 30)
          chunks.push(regionIds.slice(i, i + 30));
        const snaps = await Promise.all(
          chunks.map((chunk) =>
            getDocs(
              query(
                ref,
                where("isActive", "==", true),
                where("locations.region.unique_id", "in", chunk),
              ),
            ),
          ),
        );
        return snaps.flatMap((snap) =>
          snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SiteModel),
        );
      }),
    );
  }

  getAssessmentsBySiteIds(siteIds: string[]): Observable<AssessmentModel[]> {
    return this.getTenantCollection("assessments").pipe(
      switchMap((ref) => {
        const q = query(ref, where("siteId", "in", siteIds));
        return collectionData(q, { idField: "id" }) as Observable<
          AssessmentModel[]
        >;
      }),
    );
  }

  getFindingsForAssessment(assessmentId: string): Observable<FindingModel[]> {
    return this.getTenantCollection(`assessments/${assessmentId}/finding`).pipe(
      switchMap(
        (ref) =>
          collectionData(ref, { idField: "id" }) as Observable<FindingModel[]>,
      ),
    );
  }

  getActionsForAssessment(assessmentId: string): Observable<ActionModel[]> {
    return this.getTenantCollection(`assessments/${assessmentId}/action`).pipe(
      switchMap(
        (ref) =>
          collectionData(ref, { idField: "id" }) as Observable<ActionModel[]>,
      ),
    );
  }

  getAssessmentSite(id: string): Observable<SiteModel> {
    const siteDocRef = this.getTenantDoc(`sites/${id}`);
    return siteDocRef.pipe(
      switchMap(
        (siteDocRef) =>
          docData(siteDocRef, { idField: "id" }) as Observable<SiteModel>,
      ),
    );
  }

  getAssessmentsByIds(ids: string[]): Observable<AssessmentModel[]> {
    if (!ids.length) return of([]);
    return this.getTenantCollection("assessments").pipe(
      switchMap(async (ref) => {
        const chunks: string[][] = [];
        for (let i = 0; i < ids.length; i += 30)
          chunks.push(ids.slice(i, i + 30));
        const snaps = await Promise.all(
          chunks.map((chunk) =>
            getDocs(query(ref, where(documentId(), "in", chunk))),
          ),
        );
        return snaps.flatMap((snap) =>
          snap.docs.map((d) => ({ id: d.id, ...d.data() }) as AssessmentModel),
        );
      }),
    );
  }

  getSitesByIds(ids: string[]): Observable<SiteModel[]> {
    if (!ids.length) return of([]);
    return this.getTenantCollection("sites").pipe(
      switchMap(async (ref) => {
        const chunks: string[][] = [];
        for (let i = 0; i < ids.length; i += 30)
          chunks.push(ids.slice(i, i + 30));
        const snaps = await Promise.all(
          chunks.map((chunk) =>
            getDocs(query(ref, where(documentId(), "in", chunk))),
          ),
        );
        return snaps.flatMap((snap) =>
          snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SiteModel),
        );
      }),
    );
  }

  getAssetsByIds(siteId: string, assetIds: string[]): Observable<AssetModel[]> {
    if (!assetIds.length) return of([]);
    return this.getTenantCollection(`sites/${siteId}/assets`).pipe(
      switchMap(async (ref) => {
        const chunks: string[][] = [];
        for (let i = 0; i < assetIds.length; i += 30)
          chunks.push(assetIds.slice(i, i + 30));
        const snaps = await Promise.all(
          chunks.map((chunk) =>
            getDocs(query(ref, where(documentId(), "in", chunk))),
          ),
        );
        return snaps.flatMap((snap) =>
          snap.docs.map((d) => ({ id: d.id, ...d.data() }) as AssetModel),
        );
      }),
    );
  }

  getSite(id: string): Observable<SiteModel> {
    if (!id) return of({} as SiteModel);

    return this.getTenantCollection("sites").pipe(
      map((sitesRef) =>
        query(
          sitesRef,
          where("isActive", "==", true),
          where("locationKeys", "array-contains", id),
        ),
      ),
      switchMap((sitesQuery) =>
        collectionData(sitesQuery, { idField: "id" }).pipe(
          map((sites) => (sites.length ? sites[0] : ({} as SiteModel))),
        ),
      ),
      switchMap((site) => {
        if (!site.id) return of(site);

        return this.getTenantCollection(`sites/${site.id}/assets`).pipe(
          map((assetsRef) => query(assetsRef, where("isActive", "==", true))),
          switchMap((assetQuery) =>
            collectionData(assetQuery, { idField: "id" }).pipe(
              map((assets) => ({ ...site, assets })),
            ),
          ),
        );
      }),
    );
  }

  getSiteByDocId(siteId: string): Observable<SiteModel> {
    if (!siteId) return of({} as SiteModel);

    return this.getTenantDoc(`sites/${siteId}`).pipe(
      switchMap((docRef) =>
        from(getDoc(docRef)).pipe(
          switchMap((snap) => {
            if (!snap.exists()) return of({} as SiteModel);

            const site = { ...(snap.data() as SiteModel), id: snap.id };

            return this.getTenantCollection(`sites/${siteId}/assets`).pipe(
              map((assetsRef) =>
                query(assetsRef, where("isActive", "==", true)),
              ),
              switchMap((assetQuery) =>
                collectionData(assetQuery, { idField: "id" }).pipe(
                  map((assets) => ({ ...site, assets })),
                ),
              ),
            );
          }),
        ),
      ),
    );
  }

  getSiteWithAssessmentFindingAction(
    id: string,
    offline: boolean = false,
  ): Observable<SiteAssessmentFindingsActionsModel> {
    if (!id) return of({} as SiteAssessmentFindingsActionsModel);
    if (offline) {
      return this.offlineService.getOfflineSiteData(id);
    }

    return this.getTenantCollection("sites").pipe(
      map((sitesRef) =>
        query(
          sitesRef,
          where("isActive", "==", true),
          where("locationKeys", "array-contains", id),
        ),
      ),
      switchMap((sitesQuery) =>
        collectionData(sitesQuery, { idField: "id" }).pipe(
          map((sites) => (sites.length ? sites[0] : null)),
        ),
      ),
      switchMap((site) => {
        if (!site || !site["id"]) {
          return of({} as SiteAssessmentFindingsActionsModel);
        }

        return this.getTenantCollection("assessments").pipe(
          map((assessmentsRef) =>
            query(assessmentsRef, where("siteId", "==", site["id"])),
          ),
          switchMap((assessmentQuery) =>
            collectionData(assessmentQuery, { idField: "id" }).pipe(
              switchMap((assessments) => {
                if (!assessments.length) {
                  return this.getTenantCollection(
                    `sites/${site["id"]}/assets`,
                  ).pipe(
                    map((assetsRef) =>
                      query(assetsRef, where("isActive", "==", true)),
                    ),
                    switchMap((assetQuery) =>
                      collectionData(assetQuery, { idField: "id" }).pipe(
                        map(
                          (assets) =>
                            ({
                              site: { ...site, assets },
                              assessments: [] as AssessmentModel[],
                              findings: [] as FindingModel[],
                              actions: [] as ActionModel[],
                            }) as SiteAssessmentFindingsActionsModel,
                        ),
                      ),
                    ),
                  );
                }

                // For each assessment, get findings and actions
                const findingsActions$ = (assessments as AssessmentModel[]).map(
                  (assessment) => {
                    const findings$ = this.getTenantCollection(
                      `assessments/${assessment.id}/finding`,
                    ).pipe(
                      switchMap(
                        (findingRef) =>
                          collectionData(findingRef, {
                            idField: "id",
                          }) as Observable<FindingModel[]>,
                      ),
                    );

                    const actions$ = this.getTenantCollection(
                      `assessments/${assessment.id}/action`,
                    ).pipe(
                      switchMap(
                        (actionRef) =>
                          collectionData(actionRef, {
                            idField: "id",
                          }) as Observable<ActionModel[]>,
                      ),
                    );

                    return combineLatest([findings$, actions$]).pipe(
                      map(([findings, actions]) => ({
                        findings,
                        actions,
                      })),
                    );
                  },
                );

                return combineLatest(findingsActions$).pipe(
                  switchMap((results) => {
                    const allFindings = results.flatMap(
                      (r) => r.findings,
                    ) as FindingModel[];
                    const allActions = results.flatMap(
                      (r) => r.actions,
                    ) as ActionModel[];
                    // Now get assets for the site and return the final object
                    return this.getTenantCollection(
                      `sites/${site["id"]}/assets`,
                    ).pipe(
                      map((assetsRef) =>
                        query(assetsRef, where("isActive", "==", true)),
                      ),
                      switchMap((assetQuery) =>
                        collectionData(assetQuery, { idField: "id" }).pipe(
                          map(
                            (assets) =>
                              ({
                                site: { ...site, assets },
                                assessments: assessments as AssessmentModel[],
                                findings: allFindings,
                                actions: allActions,
                              }) as SiteAssessmentFindingsActionsModel,
                          ),
                        ),
                      ),
                    );
                  }),
                );
              }),
            ),
          ),
        );
      }),
    );
  }

  // ** Get Site & Assets In Settings

  // * Get All Master Asset
  getAllMasterAssets(): Observable<SubAssetMasterModel[]> {
    return this.getTenantCollection("sub_asset_master").pipe(
      switchMap((masterAssetsRef) => {
        const masterAssetsQuery = query(
          masterAssetsRef,
          where("onlyParent", "==", false),
          where("onlyChild", "==", false),
        );
        return collectionData(masterAssetsQuery, {
          idField: "id",
        }) as Observable<SubAssetMasterModel[]>;
      }),
    );
  }
  getRawMasterAssets(): Observable<SubAssetMasterModel[]> {
    return this.getTenantCollection("sub_asset_master").pipe(
      switchMap(
        (collectionRef) =>
          collectionData(collectionRef, {
            idField: "id",
          }) as Observable<SubAssetMasterModel[]>,
      ),
    );
  }
  retrieveCheckInSites(user: UserModel): Observable<SiteModel[]> {
    return this.getTenantCollection("sites").pipe(
      switchMap((sitesRef) => {
        const sitesQuery = query(sitesRef, where("isActive", "==", true));

        return collectionData(sitesQuery, { idField: "id" }) as Observable<
          SiteModel[]
        >;
      }),
    );
  }

  getAssetById(siteId: string, assetId: string): Observable<AssetModel> {
    return this.getTenantDoc(`sites/${siteId}/assets/${assetId}`).pipe(
      switchMap(
        (ref) => docData(ref, { idField: "id" }) as Observable<AssetModel>,
      ),
    );
  }

  // ** Get Assets
  getAllAssets(
    id: string,
  ): Observable<{ site: SiteModel; assets: AssetModel[] }> {
    const sitePath = `sites/${id}`;
    const assetsPath = `sites/${id}/assets`;

    return this.getTenantDoc(sitePath).pipe(
      switchMap((siteRef) => {
        const site$ = docData(siteRef, {
          idField: "id",
        }) as Observable<SiteModel>;

        return this.getTenantCollection(assetsPath).pipe(
          switchMap((assetsRef) => {
            const activeAssetsQuery = query(
              assetsRef,
              where("isActive", "==", true),
            );

            const assets$ = collectionData(activeAssetsQuery, {
              idField: "id",
            }) as Observable<AssetModel[]>;

            return combineLatest([site$, assets$]).pipe(
              map(([site, assets]) => ({ site, assets })),
            );
          }),
        );
      }),
    );
  }

  // ** Get Assets For Assessment
  getAllAssetsForAssessment(
    id: string,
  ): Observable<{ site: SiteModel; assets: AssetModel[] }> {
    const sitePath = `sites/${id}`;
    const assetsPath = `sites/${id}/assets`;

    return this.getTenantDoc(sitePath).pipe(
      switchMap((siteRef) => {
        const site$ = docData(siteRef, {
          idField: "id",
        }) as Observable<SiteModel>;

        return this.getTenantCollection(assetsPath).pipe(
          switchMap((assetsRef) => {
            const assetQuery = query(
              assetsRef,
              where("isActive", "==", true),
              where("assetStatus.isRegistered", "==", true),
              where("assetStatus.status.assetRegistered", "==", true),
            );
            const assets$ = collectionData(assetQuery, {
              idField: "id",
            }) as Observable<AssetModel[]>;
            return combineLatest([site$, assets$]).pipe(
              map(([site, assets]) => {
                const availableZones =
                  site.locations?.site?.coordinate?.zone_coordinates?.filter(
                    (zone) =>
                      assets.some(
                        (asset) =>
                          asset.assetInfo?.assetZone?.area?.toLowerCase() ===
                          zone.id?.toLowerCase(),
                      ),
                  );
                const _site = {
                  ...site,
                  locations: {
                    ...site.locations,
                    site: {
                      ...site.locations?.site,
                      coordinate: {
                        ...site.locations?.site?.coordinate,
                        zones: availableZones,
                      },
                    },
                  },
                } as SiteModel;

                return { site: _site, assets };
              }),
            );
          }),
        );
      }),
    );
  }

  // ** Get All Incomplete Assets
  getIncompleteAssets(id: string): Observable<AssetModel[]> {
    const assetsPath = `sites/${id}/assets`;

    return this.getTenantCollection(assetsPath).pipe(
      switchMap((assetsRef) => {
        const assetsQuery = query(assetsRef, where("isActive", "==", true));

        // Get asset data and filter for incomplete assets
        return collectionData(assetsQuery, { idField: "id" }).pipe(
          map((assets: AssetModel[]) =>
            assets.filter(
              (asset) =>
                !asset.pid && asset.totalAdded !== asset.totalRecommended,
            ),
          ),
          catchError((error) => of([])), // Handle any error and return an empty array
        );
      }),
    );
  }

  getAllSites(id: string, user: UserModel): Observable<SiteModel[]> {
    const sitesRef = this.getTenantCollection("sites");
    const siteLocationIds = user?.roles?.map((role) => role.locationId) || [];

    // const sitesQuery = query(
    //   sitesRef,
    //   where("isActive", "==", true),
    //   where("locationKeys", "array-contains", id),
    //   where("locations.site.unique_id", "in", siteLocationIds),
    // );
    return sitesRef.pipe(
      switchMap((ref) => {
        const sitesQuery = query(
          ref,
          where("isActive", "==", true),
          where("locationKeys", "array-contains", id),
          where("locations.site.unique_id", "in", siteLocationIds),
        );

        return from(getDocs(sitesQuery)).pipe(
          map((snapshot) =>
            snapshot.docs.map((doc) => doc?.data() as SiteModel),
          ),
          catchError(() => of([])),
        );
      }),
    );
  }

  getAssetsForSite(
    siteId: string,
  ): Observable<{ incompleteAssets: number; siteId: string }> {
    return this.getTenantCollection(`sites/${siteId}/assets`).pipe(
      switchMap((assetsRef) => {
        const assetsQuery = query(assetsRef, where("isActive", "==", true));
        return collectionData(assetsQuery, { idField: "id" }) as Observable<
          AssetModel[]
        >;
      }),
      map((assets) => {
        const incompleteAssets = assets.filter(
          (asset) => !asset.pid && asset.totalAdded !== asset.totalRecommended,
        ).length;
        return { siteId, incompleteAssets };
      }),
      catchError(() => of({ siteId, incompleteAssets: 0 })),
    );
  }

  // Findings of every assessment of a site, read once (Mapping site view: finding pins in zones)
  getSiteAssessmentFindings(siteId: string): Observable<FindingModel[]> {
    return this.getTenantCollection("assessments").pipe(
      switchMap((assessmentsRef) =>
        from(getDocs(query(assessmentsRef, where("siteId", "==", siteId)))),
      ),
      switchMap((assessments) => {
        if (assessments.empty) return of([] as FindingModel[]);
        return forkJoin(
          assessments.docs.map((assessmentDoc) =>
            this.getTenantCollection(`assessments/${assessmentDoc.id}/finding`).pipe(
              switchMap((findingsRef) => from(getDocs(findingsRef))),
              map((snapshot) =>
                snapshot.docs.map(
                  (findingDoc) =>
                    ({ ...(findingDoc.data() as FindingModel), id: findingDoc.id }) as FindingModel,
                ),
              ),
              take(1),
            ),
          ),
        ).pipe(map((lists) => lists.flat()));
      }),
      take(1),
    );
  }

  // Active assets of a site (Mapping site view: counts per zone, asset pins)
  getActiveAssetsBySite(siteId: string): Observable<AssetModel[]> {
    return this.getTenantCollection(`sites/${siteId}/assets`).pipe(
      switchMap((assetsRef) => {
        const assetsQuery = query(assetsRef, where("isActive", "==", true));
        return collectionData(assetsQuery, { idField: "id" }) as Observable<
          AssetModel[]
        >;
      }),
    );
  }

  // ** Get Pending Assets
  getAssetsPending(
    id: string,
    conditions: QueryCondition[] = [],
  ): Observable<AssetModel[]> {
    const assetsPath = `sites/${id}/assets`;

    return this.getTenantCollection(assetsPath).pipe(
      switchMap((ref) => {
        const whereConditions = conditions.map((condition) =>
          where(condition.field, condition.operator, condition.value),
        );
        const assetsQuery = query(ref, or(...whereConditions));

        return collectionData(assetsQuery, { idField: "id" }) as Observable<
          AssetModel[]
        >;
      }),
    );
  }

  // ** Get documents
  getDocuments(
    collectionName: string,
    filterOp: "OR" | "AND" = "AND",
    conditions: QueryCondition[] = [],
  ): Observable<DocumentData[]> {
    return this.getTenantCollection(collectionName).pipe(
      switchMap((collectionRef) => {
        if (conditions.length === 0) {
          return collectionData(collectionRef, { idField: "id" });
        }

        const whereConditions = conditions.map((condition) =>
          where(condition.field, condition.operator, condition.value),
        );

        const documentsQuery =
          filterOp === "OR"
            ? query(collectionRef, or(...whereConditions))
            : query(collectionRef, and(...whereConditions));

        return collectionData(documentsQuery, { idField: "id" });
      }),
    );
  }

  // ** Get sub documents
  getSubDocuments(
    docId: string,
    collectionName: string,
    subCollectionName: string,
    filterOp: "OR" | "AND" = "AND",
    conditions?: QueryCondition[],
  ): Observable<DocumentData[]> {
    return this.getTenantCollection(
      `${collectionName}/${docId}/${subCollectionName}`,
    ).pipe(
      switchMap((collectionRef) => {
        let documentsQuery: Query<DocumentData>;

        if (conditions && conditions.length > 0) {
          const whereConditions = conditions.map((condition) =>
            where(condition.field, condition.operator, condition.value),
          );

          documentsQuery =
            filterOp === "OR"
              ? query(collectionRef, or(...whereConditions))
              : query(collectionRef, and(...whereConditions));
        } else {
          documentsQuery = collectionRef;
        }

        return collectionData(documentsQuery, { idField: "id" });
      }),
    );
  }

  // ** Get Findings
  // ! will be removed
  getFindings(
    siteId: string,
    assessmentId: string,
  ): Observable<AssetDetails[]> {
    // Query to get registered assets
    const assets$ = this.getTenantCollection(`sites/${siteId}/assets`).pipe(
      switchMap((assetCollectionRef) => {
        const assetQuery = query(
          assetCollectionRef,
          where("assetStatus.isRegistered", "==", true),
          where("assetStatus.status.assetRegistered", "==", true),
        );
        return collectionData(assetQuery, { idField: "id" }) as Observable<
          AssetModel[]
        >;
      }),
    );

    // Query to get findings
    const findings$ = this.getTenantCollection(
      `assessments/${assessmentId}/finding`,
    ).pipe(
      switchMap((findingCollectionRef) => {
        const findingQuery = query(
          findingCollectionRef,
          where("assessmentId", "==", assessmentId),
        );
        return collectionData(findingQuery, { idField: "id" }) as Observable<
          FindingModel[]
        >;
      }),
    );

    // Query to get actions
    const actions$ = this.getTenantCollection(
      `assessments/${assessmentId}/action`,
    ).pipe(
      switchMap((actionCollectionRef) => {
        const actionQuery = query(
          actionCollectionRef,
          where("assessmentId", "==", assessmentId),
        );
        return collectionData(actionQuery, { idField: "id" }) as Observable<
          ActionModel[]
        >;
      }),
    );

    // Combine results from assets$, findings$, and actions$
    return combineLatest([assets$, findings$, actions$]).pipe(
      map(([assets, findings, actions]) => {
        if (assets.length === 0) return [];

        return assets.map((asset: AssetModel) => {
          // Find related findings for the asset
          const assetFindings = findings
            .filter((finding: FindingModel) => finding.assetId === asset.id)
            .map((finding) => {
              // Find related actions for each finding
              const findingActions = actions.filter(
                (action: ActionModel) => action.findingId === finding.id,
              );
              return {
                ...finding,
                action: findingActions,
              };
            });

          return {
            id: asset.id,
            pid: asset.pid,
            uid: asset.uid,
            assetInfo: asset.assetInfo,
            findings: assetFindings,
          } as AssetDetails;
        });
      }),
    );
  }

  getFindingsActionsForAssessment(
    siteId: string,
    assessmentId: string,
    offline: boolean = false,
  ): Observable<{ findings: FindingModel[]; actions: ActionModel[] }> {
    // Query to get registered assets
    if (offline) {
      return this.offlineService.getOfflineFindingsActionsForAssessment(
        siteId,
        assessmentId,
      );
    }
    const assets$ = this.getTenantCollection(`sites/${siteId}/assets`).pipe(
      switchMap((assetCollectionRef) => {
        const assetQuery = query(
          assetCollectionRef,
          where("assetStatus.isRegistered", "==", true),
          where("assetStatus.status.assetRegistered", "==", true),
        );
        return collectionData(assetQuery, { idField: "id" }) as Observable<
          AssetModel[]
        >;
      }),
    );

    // Query to get findings
    const findings$ = this.getTenantCollection(
      `assessments/${assessmentId}/finding`,
    ).pipe(
      switchMap((findingCollectionRef) => {
        const findingQuery = query(
          findingCollectionRef,
          where("assessmentId", "==", assessmentId),
        );
        return collectionData(findingQuery, { idField: "id" }) as Observable<
          FindingModel[]
        >;
      }),
    );

    // Query to get actions
    const actions$ = this.getTenantCollection(
      `assessments/${assessmentId}/action`,
    ).pipe(
      switchMap((actionCollectionRef) => {
        const actionQuery = query(
          actionCollectionRef,
          where("assessmentId", "==", assessmentId),
        );
        return collectionData(actionQuery, { idField: "id" }) as Observable<
          ActionModel[]
        >;
      }),
    );

    // Combine results from findings$ and actions$
    return combineLatest([findings$, actions$]).pipe(
      map(([findings, actions]) => {
        return {
          findings,
          actions,
        };
      }),
    );
  }

  getFindingsActionsForMapAssessment(
    siteId: string,
    assessmentId: string,
  ): Observable<{ findings: FindingModel[]; actions: ActionModel[] }> {
    // Query to get registered assets
    const assets$ = this.getTenantCollection(`sites/${siteId}/assets`).pipe(
      switchMap((assetCollectionRef) => {
        const assetQuery = query(
          assetCollectionRef,
          where("assetStatus.isRegistered", "==", true),
          where("assetStatus.status.assetRegistered", "==", true),
        );
        return collectionData(assetQuery, { idField: "id" }) as Observable<
          AssetModel[]
        >;
      }),
    );

    // Query to get findings
    const findings$ = this.getTenantCollection(
      `assessments/${assessmentId}/finding`,
    ).pipe(
      switchMap((findingCollectionRef) => {
        const findingQuery = query(
          findingCollectionRef,
          where("assessmentId", "==", assessmentId),
        );
        return collectionData(findingQuery, { idField: "id" }) as Observable<
          FindingModel[]
        >;
      }),
    );

    // Query to get actions
    const actions$ = this.getTenantCollection(
      `assessments/${assessmentId}/action`,
    ).pipe(
      switchMap((actionCollectionRef) => {
        const actionQuery = query(
          actionCollectionRef,
          where("assessmentId", "==", assessmentId),
        );
        return collectionData(actionQuery, { idField: "id" }) as Observable<
          ActionModel[]
        >;
      }),
    );

    // Combine results from findings$ and actions$
    return combineLatest([findings$, actions$]).pipe(
      map(([findings, actions]) => {
        const findingsResult = findings;
        const actionsResult = actions;

        //! yellow = any finding / action is open
        //! red = any finding / action is overdue
        //! blue = initial / noting happened / not assigned
        //! green = all findings are closed / all actions are closed

        const updatedFindings = findings?.map((finding: FindingModel) => {
          let red = false;
          let green = false;
          let yellow = false;

          const date = finding?.info?.dateToBeRectified
            ? this.formateDate(finding?.info?.dateToBeRectified)
            : "";
          const currentDate = new Date().getTime();

          //! green = all findings are closed / all actions are closed
          if (finding?.closed) green = true;
          //! yellow = any finding / action is open
          if (!finding?.closed) yellow = true;
          //! red = any finding / action is overdue
          if (date && Number(date) < currentDate) red = true;

          let finalColor = {
            red: false,
            green: false,
            yellow: false,
          };

          if (red) {
            finalColor = {
              red: true,
              green: false,
              yellow: false,
            };
          } else if (green) {
            finalColor = {
              red: false,
              green: true,
              yellow: false,
            };
          } else if (yellow) {
            finalColor = {
              red: false,
              green: false,
              yellow: true,
            };
          }

          return {
            findingId: finding.id,
            assetId: finding.assetId,
            assessmentId: finding.assessmentId,
            color: finalColor,
          };
        });

        const updatedActions = actions?.map((action: ActionModel) => {
          let red = false;
          let green = false;
          let yellow = false;

          const date = action?.dueDate ? this.formateDate(action?.dueDate) : "";
          const currentDate = new Date().getTime();

          //! green = all findings are closed / all actions are closed
          if (action?.closed) green = true;
          //! yellow = any finding / action is open
          if (!action?.closed) yellow = true;
          //! red = any finding / action is overdue
          if (date && Number(date) < currentDate) red = true;

          let finalColor = {
            red: false,
            green: false,
            yellow: false,
          };

          if (red) {
            finalColor = {
              red: true,
              green: false,
              yellow: false,
            };
          } else if (green) {
            finalColor = {
              red: false,
              green: true,
              yellow: false,
            };
          } else if (yellow) {
            finalColor = {
              red: false,
              green: false,
              yellow: true,
            };
          }

          return {
            actionId: action.id,
            assetId: action.assetId,
            assessmentId: action.assessmentId,
            color: finalColor,
          };
        });

        return {
          findings: updatedFindings as FindingAssessmentColorModel[],
          actions: updatedActions as ActionAssessmentColorModel[],
        };
      }),
    );
  }
  // ** Get Assessments
  getAssessments(
    id: string | undefined,
    user: UserModel,
  ): Observable<AssessmentModel[]> {
    if (!id) {
      // Handle the case where `id` is undefined by returning an empty observable or throwing an error
      return of([]); // Return an empty array as an observable if `id` is undefined
    }

    return this.getTenantCollection("assessments").pipe(
      switchMap((assessmentsRef) => {
        const assessmentsQuery = query(
          assessmentsRef,
          where("siteId", "==", id),
        );

        return collectionData(assessmentsQuery, {
          idField: "id",
        }) as Observable<AssessmentModel[]>;
      }),
    );
  }
  // getAllActionsFindingsByAssesmentId(
  //   assessmentId: string,
  //   user: UserModel,
  // ): Observable<any> {
  //   // Query to get findings
  //   const findingCollectionRef = collection(
  //     this.firestore,
  //     `assessments/${assessmentId}/finding`,
  //   );

  //   const findingQuery = query(
  //     findingCollectionRef,
  //     where("assessmentId", "==", assessmentId),
  //   );

  //   const findings$ = collectionData(findingQuery, {
  //     idField: "id",
  //   }) as Observable<FindingModel[]>;

  //   const findingQueryByUser = query(
  //     findingCollectionRef,
  //     where("assessmentId", "==", assessmentId),
  //     where("info.assignedTo.email", "==", user.email),
  //   );

  //   const findingsByUser$ = collectionData(findingQueryByUser, {
  //     idField: "id",
  //   }) as Observable<FindingModel[]>;

  //   // Query to get actions
  //   const actionCollectionRef = collection(
  //     this.firestore,
  //     `assessments/${assessmentId}/action`,
  //   );

  //   const reassignedActionQuery = query(
  //     actionCollectionRef,
  //     where("assessmentId", "==", assessmentId),
  //     where("reassignedTo.email", "==", user.email),
  //   );

  //   const assignedActionQuery = query(
  //     actionCollectionRef,
  //     where("assessmentId", "==", assessmentId),
  //     where("assignedTo.email", "==", user.email),
  //   );

  //   const actionsByUser$ = combineLatest([
  //     collectionData(reassignedActionQuery, { idField: "id" }),
  //     collectionData(assignedActionQuery, { idField: "id" }),
  //   ]).pipe(
  //     map(([reassignedActions, assignedActions]) => [
  //       ...reassignedActions,
  //       ...assignedActions,
  //     ]),
  //     // Apply the filtering condition using bracket notation
  //     map((actions) =>
  //       actions.filter((userAction) =>
  //         userAction["reassignedTo"]?.["email"]
  //           ? userAction["reassignedTo"]["email"] === user.email
  //           : userAction["assignedTo"]?.["email"] === user.email,
  //       ),
  //     ),
  //   ) as Observable<ActionModel[]>;

  //   const actions$ = collectionData(actionCollectionRef, {
  //     idField: "id",
  //   }) as Observable<ActionModel[]>;

  //   // Combine findings and actions
  //   return combineLatest([
  //     findingsByUser$,
  //     findings$,
  //     actionsByUser$,
  //     actions$,
  //   ]).pipe(
  //     map(([findingsByUser, findings, userActions, actions]) => {
  //       const GlobalFindings = findings.map((finding: FindingModel) => {
  //         const relatedActions = actions.filter(
  //           (action: ActionModel) => action.findingId === finding.id,
  //         );
  //         return { ...finding, actions: relatedActions };
  //       });

  //       const userFindings = findingsByUser.map((userFinding: FindingModel) => {
  //         const relatedFindingsUserActions = actions.filter(
  //           (action: ActionModel) => action.findingId === userFinding.id,
  //         );
  //         return { ...userFinding, actions: relatedFindingsUserActions };
  //       });

  //       const GlobalActions = actions.map((action: ActionModel) => {
  //         const parentFinding =
  //           findings.find(
  //             (finding: FindingModel) => action.findingId === finding.id,
  //           ) || null;
  //         return { ...action, finding: parentFinding };
  //       });

  //       const userActionsWithFindings = userActions.map(
  //         (userAction: ActionModel) => {
  //           const parentFinding =
  //             findings.find((finding) => {
  //               return (
  //                 userAction.assessmentId === finding.assessmentId &&
  //                 userAction.findingId === finding.id
  //               );
  //             }) || null;

  //           return { ...userAction, finding: parentFinding };
  //         },
  //       );

  //       return {
  //         findings: {
  //           toDo: userFindings,
  //           followUp: GlobalFindings,
  //         },
  //         actions: {
  //           toDo: userActionsWithFindings,
  //           followUp: GlobalActions,
  //         },
  //       };
  //     }),
  //   );
  // }

  // getAllActionsFindingsByAssesmentId(
  //   assessmentId: string,
  //   user: UserModel,
  // ): Observable<any> {
  //   if (!assessmentId) return of({});

  //   const findingCollectionRef = collection(
  //     this.firestore,
  //     `assessments/${assessmentId}/finding`,
  //   );

  //   const allFindingsQuery = query(
  //     findingCollectionRef,
  //     where("assessmentId", "==", assessmentId),
  //   );

  //   const userFindingsQuery = query(
  //     findingCollectionRef,
  //     where("assessmentId", "==", assessmentId),
  //     where("info.assignedTo.email", "==", user.email),
  //   );

  //   const allFindings$ = collectionData(allFindingsQuery, {
  //     idField: "id",
  //   }) as Observable<FindingModel[]>;
  //   const userFindings$ = collectionData(userFindingsQuery, {
  //     idField: "id",
  //   }) as Observable<FindingModel[]>;

  //   return combineLatest([allFindings$, userFindings$]).pipe(
  //     switchMap(([allFindings, userFindings]) => {
  //       const actionCollectionRef = collection(
  //         this.firestore,
  //         `assessments/${assessmentId}/action`,
  //       );

  //       const reassignedQuery = query(
  //         actionCollectionRef,
  //         where("assessmentId", "==", assessmentId),
  //         where("reassignedTo.email", "==", user.email),
  //       );

  //       const assignedQuery = query(
  //         actionCollectionRef,
  //         where("assessmentId", "==", assessmentId),
  //         where("assignedTo.email", "==", user.email),
  //       );

  //       const allActions$ = collectionData(actionCollectionRef, {
  //         idField: "id",
  //       }) as Observable<ActionModel[]>;
  //       const userActions$ = combineLatest([
  //         collectionData(reassignedQuery, { idField: "id" }),
  //         collectionData(assignedQuery, { idField: "id" }),
  //       ]).pipe(
  //         map(([reassigned, assigned]) => [...reassigned, ...assigned]),
  //         map((actions) =>
  //           actions.filter((a: any) =>
  //             a["reassignedTo"]?.["email"]
  //               ? a["reassignedTo"]["email"] === user.email
  //               : a["assignedTo"]?.["email"] === user.email,
  //           ),
  //         ),
  //       ) as Observable<ActionModel[]>;

  //       return combineLatest([
  //         of(allFindings),
  //         of(userFindings),
  //         allActions$,
  //         userActions$,
  //       ]);
  //     }),
  //     map(([allFindings, userFindings, allActions, userActions]) => {
  //       const GlobalFindings = allFindings.map((finding: FindingModel) => ({
  //         ...finding,
  //         actions: allActions.filter((a) => a.findingId === finding.id),
  //       }));

  //       const userFindingsWithActions = userFindings.map((f: FindingModel) => ({
  //         ...f,
  //         actions: allActions.filter((a) => a.findingId === f.id),
  //       }));

  //       const GlobalActions = allActions.map((action: ActionModel) => {
  //         const finding =
  //           allFindings.find((f) => f.id === action.findingId) || null;
  //         return { ...action, finding };
  //       });

  //       const userActionsWithFindings = userActions.map(
  //         (action: ActionModel) => {
  //           const finding =
  //             allFindings.find(
  //               (f) =>
  //                 f.id === action.findingId &&
  //                 f.assessmentId === action.assessmentId,
  //             ) || null;
  //           return { ...action, finding };
  //         },
  //       );

  //       return {
  //         findings: {
  //           toDo: userFindingsWithActions,
  //           followUp: GlobalFindings,
  //         },
  //         actions: {
  //           toDo: userActionsWithFindings,
  //           followUp: GlobalActions,
  //         },
  //       };
  //     }),
  //   );
  // }

  GetASubDocument(collectionName: string): Observable<any[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) =>
        collectionData(collectionGroup(db, collectionName), {
          idField: "id",
        }),
      ),
    );
  }

  getRecentFindings(limitCount: number): Observable<FindingModel[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, "finding"),
          orderBy("createdAt", "desc"),
          limit(limitCount),
        );
        return collectionData(q, { idField: "id" }) as Observable<
          FindingModel[]
        >;
      }),
    );
  }

  getMyFindings(email: string): Observable<FindingModel[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, "finding"),
          where("info.assignedTo.email", "==", email),
          orderBy("createdAt", "desc"),
        );
        return collectionData(q, { idField: "id" }) as Observable<
          FindingModel[]
        >;
      }),
    );
  }

  getFollowUpFindings(email: string): Observable<FindingModel[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, "finding"),
          where("createdBy", "==", email),
          orderBy("createdAt", "desc"),
        );
        return collectionData(q, { idField: "id" }) as Observable<
          FindingModel[]
        >;
      }),
    );
  }

  getRecentFindingsCount(): Observable<number> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap(async (db) => {
        const q = query(
          collectionGroup(db, "finding"),
          orderBy("createdAt", "desc"),
        );
        const snap = await getCountFromServer(q);
        return snap.data().count;
      }),
    );
  }

  getRecentFindingsPaged(
    pageSize: number,
    cursor?: QueryDocumentSnapshot,
  ): Observable<{
    findings: FindingModel[];
    lastDoc: QueryDocumentSnapshot | null;
  }> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap(async (db) => {
        const q = cursor
          ? query(
              collectionGroup(db, "finding"),
              orderBy("createdAt", "desc"),
              startAfter(cursor),
              limit(pageSize),
            )
          : query(
              collectionGroup(db, "finding"),
              orderBy("createdAt", "desc"),
              limit(pageSize),
            );
        const snapshot = await getDocs(q);
        const findings = snapshot.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as FindingModel,
        );
        const lastDoc = snapshot.docs[snapshot.docs.length - 1] ?? null;
        return { findings, lastDoc };
      }),
    );
  }

  async checkAssessmentIsApproved(assessmentId: string): Promise<boolean> {
    const db = await this.tenantService.getMyDb();
    const assessmentDocRef = doc(db, `assessments/${assessmentId}`);
    const assessmentSnapshot = await getDoc(assessmentDocRef);
    const assessmentData = assessmentSnapshot.data() as AssessmentModel;
    return assessmentData?.assessmentStatus?.isApproved ?? false;
  }

  getAllActionsFindings(user: UserModel): Observable<any> {
    const findings$ = this.GetASubDocument("finding");
    const actions$ = this.GetASubDocument("action");

    return combineLatest([findings$, actions$]).pipe(
      switchMap(
        ([allFindings, allActions]: [FindingModel[], ActionModel[]]) => {
          // Collect unique assessmentIds from all actions
          const uniqueAssessmentIds = [
            ...new Set(
              allActions
                ?.map((action: ActionModel) => action?.assessmentId)
                .filter(Boolean) as string[],
            ),
          ];

          // Resolve all approval checks in parallel → { assessmentId: boolean }
          const approvalMap$ =
            uniqueAssessmentIds?.length > 0
              ? forkJoin(
                  uniqueAssessmentIds.reduce(
                    (acc: Record<string, Observable<boolean>>, id: string) => {
                      acc[id] = from(this.checkAssessmentIsApproved(id));
                      return acc;
                    },
                    {},
                  ),
                )
              : of({} as Record<string, boolean>);

          return approvalMap$.pipe(
            map((approvalMap: Record<string, boolean>) => {
              const userFindings = allFindings?.filter(
                (finding: any) =>
                  finding?.info?.assignedTo?.email === user?.email &&
                  approvalMap[finding?.assessmentId] === true,
              );

              const userActions = allActions?.filter(
                (action: ActionModel) =>
                  (action?.reassignedTo?.email
                    ? action.reassignedTo.email === user?.email
                    : action?.assignedTo?.email === user?.email) &&
                  !action?.closed &&
                  action?.isActive &&
                  approvalMap[action?.assessmentId!] === true,
              );

              /* ---------------- FINDINGS WITH ACTIONS ---------------- */
              const GlobalFindings = allFindings
                ?.filter(
                  (finding: FindingModel) =>
                    finding?.createdBy === user?.email &&
                    approvalMap[finding?.assessmentId!] === true,
                )
                ?.map((finding: FindingModel) => ({
                  ...finding,
                  actions: allActions.filter(
                    (action: ActionModel) =>
                      action?.findingId === finding?.id &&
                      action?.assessmentId === finding?.assessmentId,
                  ),
                }));

              const userFindingsWithActions = userFindings?.map(
                (finding: FindingModel) => ({
                  ...finding,
                  actions: userActions?.filter(
                    (action: ActionModel) =>
                      action?.findingId === finding?.id &&
                      action?.assessmentId === finding?.assessmentId,
                  ),
                }),
              );

              /* ---------------- ACTIONS WITH FINDINGS ---------------- */
              const GlobalActions = allActions
                ?.filter(
                  (action: ActionModel) =>
                    (action.assignedBy?.email === user?.email ||
                      (action.reassignedTo?.email !== user?.email &&
                        action?.reassignedBy?.email)) &&
                    !action.closed &&
                    action.isActive &&
                    approvalMap[action?.assessmentId!] === true,
                )
                ?.map((action: ActionModel) => ({
                  ...action,
                  finding:
                    allFindings?.find(
                      (finding: FindingModel) =>
                        finding?.id === action?.findingId,
                    ) || null,
                }));

              const userActionsWithFindings = userActions?.map(
                (action: ActionModel) => ({
                  ...action,
                  finding:
                    userFindings?.find(
                      (finding: FindingModel) =>
                        finding?.id === action?.findingId,
                    ) || null,
                }),
              );

              return {
                findings: {
                  toDo: userFindingsWithActions,
                  followUp: GlobalFindings,
                },
                actions: {
                  toDo: userActionsWithFindings,
                  followUp: GlobalActions,
                },
              };
            }),
          );
        },
      ),
    );
  }

  async GetSubDocsCount(id: string): Promise<number> {
    const db = await this.tenantService.getMyDb();
    const assetsRef = collection(db, `sites/${id}/assets`);

    const assetsQuery = query(
      assetsRef,
      where("assetStatus.isRegistered", "==", true),
      where("assetStatus.status.assetRegistered", "==", true),
    );

    const querySnapshot = await getDocs(assetsQuery);
    return querySnapshot.size;
  }

  // Notification Service
  // getNotifications(email: string): Observable<NotificationLoggerModel[]> {
  //   const notificationsRef = doc(this.firestore, `notifications/${email}`);
  //   return from(getDoc(notificationsRef)).pipe(
  //     map((docSnapshot) => {
  //       if (docSnapshot.exists()) {
  //         return docSnapshot.data()?.["send"] || [];
  //       } else {
  //         return [];
  //       }
  //     }),
  //   );
  // }

  getNotifications(
    email: string,
    lastIndex: number = 0,
    limitCount: number = 10,
  ): Observable<{
    notifications: NotificationLoggerModel[];
    lastIndex: number;
  }> {
    // Use getTenantDoc to get the document reference
    return this.getTenantDoc(`notifications/${email}`).pipe(
      switchMap((notificationsDocRef) =>
        from(getDoc(notificationsDocRef)).pipe(
          map((docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = docSnapshot.data();
              const notifications: NotificationLoggerModel[] =
                data?.["send"] || [];

              const sortedNotifications = [...notifications].sort((a, b) => {
                const parseDate = (dateStr: string) => {
                  if (!dateStr) return new Date(0);
                  const parts = dateStr.split(/[-: ]+/);
                  if (parts.length >= 7) {
                    const [day, month, year, hour, minute, second, ampm] =
                      parts;
                    return new Date(
                      `${month}/${day}/${year} ${hour}:${minute}:${second} ${ampm}`,
                    );
                  }
                  return new Date(dateStr);
                };

                const dateA = parseDate(a.date!);
                const dateB = parseDate(b.date!);
                return dateB.getTime() - dateA.getTime();
              });

              const paginatedNotifications = sortedNotifications.slice(
                lastIndex,
                lastIndex + limitCount,
              );

              return {
                notifications: paginatedNotifications,
                lastIndex: lastIndex + paginatedNotifications.length,
              };
            } else {
              return {
                notifications: [],
                lastIndex: 0,
              };
            }
          }),
        ),
      ),
    );
  }

  updateNotification(
    email: string,
    updatedNotification: NotificationLoggerModel,
  ): Observable<void> {
    updatedNotification.read = true;

    // Use getTenantDoc to get the document reference
    return this.getTenantDoc(`notifications/${email}`).pipe(
      switchMap((notificationsRef) =>
        from(getDoc(notificationsRef)).pipe(
          switchMap((docSnapshot) => {
            if (!docSnapshot.exists()) {
              throw new Error("No Notification Found");
            }

            const notifications = docSnapshot.data()?.["send"] || [];
            const updatedNotifications = notifications.map(
              (notification: NotificationLoggerModel) =>
                notification.id === updatedNotification.id
                  ? { ...notification, ...updatedNotification }
                  : notification,
            );

            // Update the document using updateDoc
            return from(
              updateDoc(notificationsRef, { send: updatedNotifications }),
            );
          }),
        ),
      ),
    );
  }

  /**
   * Fetches the download URL of an image from Firebase Storage
   * @param path The path of the image in Firebase Storage
   * @returns An Observable that emits the image URL
   */
  GetImageUrl(path: string): Observable<string> {
    return from(this.tenantService.getUserStorage()).pipe(
      switchMap((storage) => {
        const storageRef = ref(storage, path);
        return from(getDownloadURL(storageRef));
      }),
    );
  }

  GetImageBlob(path: string, fileType: string): Observable<string> {
    const mimeType = this.isProperImageFileType(fileType)
      ? `image/${fileType}`
      : "image/webp";

    return from(this.tenantService.getUserStorage()).pipe(
      switchMap((storage) => {
        const storageRef = ref(storage, path);

        return from(getBytes(storageRef));
      }),
      map((buffer: ArrayBuffer) => this.arrayBufferToDataUrl(buffer, mimeType)),
    );
  }

  private isProperImageFileType(fileType: string): boolean {
    const allowedTypes = ["jpg", "jpeg", "png", "webp"];
    return allowedTypes.includes(fileType.toLowerCase());
  }

  private arrayBufferToDataUrl(buffer: ArrayBuffer, mimeType: string): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return `data:${mimeType};base64,${btoa(binary)}`;
  }

  GetImageUrls(
    images: Array<{ id: string; src: string; fileType: string }>,
  ): Observable<Array<{ id: string; src: string; fileType: string }>> {
    const _images = images.map((image) =>
      this.GetImageUrl(image.id).pipe(
        map((url) => ({ ...image, src: url })), // Replace `src` with the `url`
      ),
    );
    return forkJoin(_images);
  }

  // Master Collections
  GetCategories(): Observable<AssetCategoryModel[]> {
    const categoriesRef$ = this.getTenantCollection("input_help");

    const categories$ = categoriesRef$.pipe(
      switchMap(
        (categoriesRef) =>
          collectionData(
            query(categoriesRef, where("type", "==", "Category")),
            { idField: "id" },
          ) as Observable<AssetCategoryModel[]>,
      ),
    );

    return categories$;
  }

  GetFeatureFlags(): Observable<FeatureFlagModel[]> {
    const featureFlagsRef$ = this.getTenantCollection("input_help");

    const featureFlags$ = featureFlagsRef$.pipe(
      switchMap(
        (featureFlagsRef) =>
          collectionData(
            query(featureFlagsRef, where("type", "==", "feature_flag")),
            { idField: "id" },
          ) as Observable<FeatureFlagModel[]>,
      ),
    );

    return featureFlags$;
  }
  GetNationalEmergencyContacts(): Observable<CountryEmergencyContacts[]> {
    const emergencyLineRef$ = this.getTenantCollection("input_help");

    const emergencyLines$ = emergencyLineRef$.pipe(
      switchMap((languagesRef: CollectionReference) => {
        const q = query(
          languagesRef,
          where("type", "==", "national_emergency_contact"),
        );
        return collectionData(q, { idField: "id" });
      }),
      map((items) => {
        if (items && items?.length > 0 && items?.[0]?.["data"]) {
          return items?.[0]?.["data"] as CountryEmergencyContacts[];
        }
        return [];
      }),
    );

    return emergencyLines$;
  }

  // Master Language Collections
  GetLanguages(): Observable<LanguageModel[]> {
    const languagesRef$ = this.getTenantCollection("input_help");

    const languages$ = languagesRef$.pipe(
      switchMap(
        (languagesRef) =>
          collectionData(query(languagesRef, where("type", "==", "language")), {
            idField: "id",
          }) as Observable<LanguageModel[]>,
      ),
    );

    return languages$;
  }

  GetCostCenters(): Observable<CostCenterModel[]> {
    const costCentersRef$ = this.getTenantCollection("input_help");
    return costCentersRef$.pipe(
      switchMap((ref) => {
        const q = query(ref, where("type", "==", "Cost Center Department"));
        return collectionData(q, { idField: "id" }) as Observable<
          CostCenterModel[]
        >;
      }),
    );
  }

  GetSiteSecurityCategories(): Observable<SecurityCategoryModel[]> {
    const categoriesRef$ = this.getTenantCollection(
      "site_security_category_master",
    );

    const categories$ = categoriesRef$.pipe(
      switchMap(
        (categoriesRef) =>
          collectionData(query(categoriesRef, orderBy("name")), {
            idField: "id",
          }) as Observable<SecurityCategoryModel[]>,
      ),
    );

    return categories$;
  }

  GetSiteSecurityPois(): Observable<SecurityPoiModel[]> {
    const poisRef$ = this.getTenantCollection("site_security_poi_master");

    const pois$ = poisRef$.pipe(
      switchMap(
        (poisRef) =>
          collectionData(query(poisRef, orderBy("name")), {
            idField: "id",
          }) as Observable<SecurityPoiModel[]>,
      ),
    );

    return pois$;
  }

  GetSiteSegments(): Observable<SiteSegmentModel[]> {
    const siteSegmentsRef$ = this.getTenantCollection("input_help");

    const siteSegments$ = siteSegmentsRef$.pipe(
      switchMap(
        (siteSegmentsRef) =>
          collectionData(
            query(siteSegmentsRef, where("type", "==", "segment")),
            { idField: "id" },
          ) as Observable<SiteSegmentModel[]>,
      ),
    );

    return siteSegments$;
  }

  // GetCostCenters(): Observable<CostCenterModel[]> {
  //   const costCentersRef = this.getTenantCollection("input_help");

  //   const costCenters$ = costCentersRef.pipe(
  //     switchMap(
  //       (ref) =>
  //         collectionData(
  //           query(ref, where("type", "==", "Cost Center Department")),
  //           { idField: "id" },
  //         ) as Observable<CostCenterModel[]>,
  //     ),
  //   );

  //   return costCenters$;
  // }

  // Master Collections

  getAllActionsFindingsByAssesmentIdWithoutUserFilter(
    assessmentId: string,
    siteId: string = "",
    offline: boolean = false,
  ): Observable<AllFindingsAndActionsModel> {
    // Get findings collection using tenantCollection

    if (offline) {
      return this.offlineService.getAllActionsFindingsByAssesmentIdWithoutUserFilterOffline(
        assessmentId,
        siteId,
      );
    }
    const assessmentPath = `assessments/${assessmentId}`;
    const assessment$ = this.getTenantDoc(assessmentPath).pipe(
      switchMap((assessmentRef) => {
        return docData(assessmentRef, {
          idField: "id",
        }) as Observable<AssessmentModel>;
      }),
    );

    const findingsPath = `assessments/${assessmentId}/finding`;
    const findings$ = this.getTenantCollection(findingsPath).pipe(
      switchMap((findingCollectionRef) => {
        const findingQuery = query(
          findingCollectionRef,
          where("assessmentId", "==", assessmentId),
          where("isActive", "==", true),
        );
        return collectionData(findingQuery, { idField: "id" }) as Observable<
          FindingModel[]
        >;
      }),
    );

    // Get actions collection using tenantCollection
    const actionsPath = `assessments/${assessmentId}/action`;
    const actions$ = this.getTenantCollection(actionsPath).pipe(
      switchMap((actionCollectionRef) => {
        const actionQuery = query(
          actionCollectionRef,
          where("isActive", "==", true),
        );
        return collectionData(actionQuery, {
          idField: "id",
        }) as Observable<ActionModel[]>;
      }),
    );

    return combineLatest([findings$, actions$, assessment$]).pipe(
      map(([findings, actions, assessment]) => {
        const assets = assessment.assets?.map((asset) => asset.id);

        const _findings = findings?.filter((finding) =>
          assets?.includes(finding.assetId),
        );

        const GlobalFindingsWithActions = _findings.map(
          (finding: FindingModel) => {
            const relatedActions = actions.filter(
              (action: ActionModel) => action.findingId === finding.id,
            );
            return { ...finding, actions: relatedActions };
          },
        );

        const GlobalActionsWithFindings = actions.map((action: ActionModel) => {
          const parentFinding =
            _findings.find(
              (finding: FindingModel) => action.findingId === finding.id,
            ) || null;
          return { ...action, finding: parentFinding };
        });

        // const GlobalFindings = GlobalFindingsWithActions.map(
        //   ({ actions, ...rest }) => rest,
        // );
        // const GlobalActions = GlobalActionsWithFindings.map(
        //   ({ finding, ...rest }) => rest,
        // );

        return {
          findings: GlobalFindingsWithActions,
          actions: GlobalActionsWithFindings,
        };
      }),
    );
  }

  // getAnAssessmentById(assessmentId: string): Observable<AssessmentModel> {
  //   const assessmentRef = doc(this.firestore, `assessments/${assessmentId}`);

  //   return docData(assessmentRef, { idField: 'id' }) as Observable<AssessmentModel>;
  // }

  getAnAssessmentById(
    assessmentId: string,
    offline: boolean = false,
  ): Observable<AssessmentModel> {
    if (offline) {
      return this.offlineService
        .getAssessmentByIdOffline(assessmentId)
        .pipe(map((assessment) => assessment ?? ({} as AssessmentModel)));
    }
    return this.getTenantDoc(`assessments/${assessmentId}`).pipe(
      switchMap(
        (assessmentRef) =>
          docData(assessmentRef, {
            idField: "id",
          }) as Observable<AssessmentModel>,
      ),
    );
  }

  updateSite(siteId: string, updatedSite: SiteModel): Observable<SiteModel> {
    return this.getTenantDoc(`sites/${siteId}`).pipe(
      switchMap((siteRef) => {
        return from(updateDoc(siteRef, { ...updatedSite })).pipe(
          switchMap(() => docData(siteRef, { idField: "id" })),
        );
      }),
    ) as Observable<SiteModel>;
  }

  // getLastDeploymentInfo(): Observable<any> {
  //   let deployment_infoRef = this.getTenantCollection(`deployment_info`);

  //   const deploymentInfo$ = deployment_infoRef.pipe(
  //     switchMap(
  //       (ref) =>
  //         collectionData(
  //           query(ref, orderBy("last_deployment", "desc"), limit(1)),
  //           {
  //             idField: "id",
  //           },
  //         ) as Observable<any[]>,
  //     ),
  //   );

  //   return deploymentInfo$;
  // }

  getDocumentsWithCondition(
    collectionPath: string,
    field: string,
    operator: WhereFilterOp,
    value: string,
  ): Observable<UserModel[]> {
    return this.getTenantCollection(collectionPath).pipe(
      switchMap((colRef) => {
        const users = query(colRef, where(field, operator, value));
        return collectionData(users, { idField: "id" });
      }),
    );
  }
  getSubAssetMasterData(): Observable<SubAssetMasterModel[]> {
    return this.getTenantCollection(`sub_asset_master`).pipe(
      switchMap(
        (collectionRef) =>
          collectionData(collectionRef, { idField: "id" }) as Observable<
            SubAssetMasterModel[]
          >,
      ),
    );
  }
  getGuidanceDataByTypeSubType(
    materialType: string,
    status: string,
    offline: boolean = false,
  ): Observable<GuidanceItem[]> {
    if (offline) {
      return from(this.storageService.get("guidanceInformation")).pipe(
        map((guidancePart) => {
          const guidance = guidancePart?.guidanceSubtype ?? [];

          return guidance.filter(
            (guidance: GuidanceItem) =>
              guidance?.materialType === materialType &&
              guidance?.status === status,
          );
        }),
      );
    }
    const guidanceRef$ = this.getTenantCollection("guidance");

    return guidanceRef$.pipe(
      switchMap(
        (ref) =>
          collectionData(
            query(
              ref,
              where("materialType", "==", materialType),
              where("status", "==", status),
            ),
            { idField: "id" },
          ) as Observable<GuidanceItem[]>,
      ),
    );
  }
  getDefectTypeByName(
    defectTypeName: string,
    offline: boolean = false,
  ): Observable<DefectType[]> {
    if (!defectTypeName) return of([]);

    if (offline) {
      return this.offlineService.getOfflineDefectTypeByName(defectTypeName);
    }
    const defectRef$ = this.getTenantCollection("defect_type_master");

    return defectRef$.pipe(
      switchMap(
        (ref) =>
          collectionData(
            query(ref, where("defectTypeName", "==", defectTypeName)),
            { idField: "id" },
          ) as Observable<DefectType[]>,
      ),
    );
  }

  // ** Setup Completion Flags **
  completeFieldSetup(
    siteId: string,
    isFieldSetupCompleted: boolean,
  ): Observable<void> {
    const siteDocRef = this.getTenantDoc(`sites/${siteId}`);
    return siteDocRef.pipe(
      switchMap((siteDocRef) =>
        from(
          updateDoc(siteDocRef, {
            isFieldSetupCompleted: isFieldSetupCompleted,
          }),
        ),
      ),
    );
  }

  completeStructuralSetup(
    siteId: string,
    isStructuralSetupCompleted: boolean,
  ): Observable<void> {
    const siteDocRef = this.getTenantDoc(`sites/${siteId}`);
    return siteDocRef.pipe(
      switchMap((siteDocRef) =>
        from(
          updateDoc(siteDocRef, {
            isStructuralSetupCompleted: isStructuralSetupCompleted,
          }),
        ),
      ),
    );
  }
  // ** End of Setup Completion Flags **

  getContactByEmail(
    email: string,
  ): Observable<{ countryDialCode: string; phoneNumber: string } | null> {
    return this.getTenantCollection("users").pipe(
      switchMap((colRef) => {
        const q = query(colRef, where("id", "==", email));
        return from(getDocs(q));
      }),
      map((usersSnap) => {
        if (!usersSnap.empty) {
          const userDoc = usersSnap.docs[0];
          const data = userDoc?.data();
          return data?.["contact"] ?? null;
        }
        return null;
      }),
    );
  }

  uploadAssetImageToAssessment(
    imageSrc: string,
    assessmentId: string,
    assetId: string,
  ): Observable<void> {
    return this.getTenantDoc(`assessments/${assessmentId}`).pipe(
      switchMap((assessmentRef) =>
        from(getDoc(assessmentRef)).pipe(
          switchMap((docSnap) => {
            if (!docSnap.exists()) {
              throw new Error("Assessment not found");
            }

            const data = docSnap.data();
            const assets: AssessmentAssetModel[] = Array.isArray(data["assets"])
              ? [...data["assets"]]
              : [];

            const assetIndex = assets.findIndex(
              (asset) => asset.id === assetId,
            );

            if (assetIndex === -1) {
              const newAsset: AssessmentAssetModel = {
                id: assetId,
                isAssessmentCompleted: false,
                noFinding: false,
                totalFindings: 0,
                assetImage: imageSrc,
              };
              assets.push(newAsset);
            } else {
              assets[assetIndex] = {
                ...assets[assetIndex],
                assetImage: imageSrc,
              };
            }

            return from(updateDoc(assessmentRef, { assets }));
          }),
        ),
      ),
    );
  }

  getAssetImageFromAssessment(
    assessmentId: string,
    assetId: string,
    siteId: string,
    offline: boolean = false,
  ): Observable<string | undefined> {
    if (offline) {
      return this.offlineService.getAssetImageFromAssessmentOffline(
        assessmentId,
        assetId,
        siteId,
      );
    }
    return this.getTenantDoc(`assessments/${assessmentId}`).pipe(
      switchMap((assessmentRef) =>
        from(getDoc(assessmentRef)).pipe(
          map((docSnap) => {
            if (!docSnap.exists()) {
              throw new Error("Assessment not found");
            }
            const data = docSnap.data();
            const assets: AssessmentAssetModel[] = Array.isArray(data["assets"])
              ? data["assets"]
              : [];
            const asset = assets.find((a) => a.id === assetId);
            return asset?.assetImage;
          }),
        ),
      ),
    );
  }

  getAdminRoleById(roleId: string): Observable<IAdminRoleData> {
    return this.getTenantDoc(`roles_admin/${roleId}`).pipe(
      switchMap(
        (assessmentRef) =>
          docData(assessmentRef, {
            idField: "id",
          }) as Observable<IAdminRoleData>,
      ),
    );
  }

  getAllAdminRole(): Observable<IAdminRoleData[]> {
    return this.getTenantCollection("roles_admin").pipe(
      switchMap((rolesRef) => {
        const allRolesQuery = query(rolesRef);
        return collectionData(allRolesQuery, {
          idField: "id",
        }) as Observable<IAdminRoleData[]>;
      }),
    );
  }

  getActiveMMInventoryBySite(
    data: {
      siteId: string;
    },
    offline: boolean = false,
  ): Observable<MM_InventoryModel[] | null> {
    if (offline) {
      return this.offlineService.getActiveMMInventoryBySiteOffline(data);
    }
    const collectionRef$ = this.getTenantCollection("mm_inventory");
    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        let q = query(
          collRef,
          where("siteId", "==", data.siteId),
          where("status", "==", "Published"),
        );
        return collectionData(q, { idField: "id" }).pipe(
          map((docs) =>
            (docs as MM_InventoryModel[]).filter(
              (doc) =>
                doc?.identification?.statusSection?.status !== "SIMULATION",
            ),
          ),
        );
      }),
    );
  }

  // updateLinkedAssetsMMInventory(
  //   inventoryId: string,
  //   asset: AssetModel,
  //   action: "add" | "remove",
  // ): Observable<void> {
  //   return this.getTenantDoc(`mm_inventory/${inventoryId}`).pipe(
  //     switchMap((docRef) =>
  //       from(getDoc(docRef)).pipe(
  //         switchMap((snap) => {
  //           if (!snap.exists()) {
  //             throw new Error("Inventory not found");
  //           }

  //           if (!asset.uid) {
  //             throw new Error("Asset UID missing");
  //           }

  //           const data = snap.data() as MM_InventoryModel;

  //           const existingAssets: MM_LinkedAssetModel[] =
  //             data.assetLink?.assets ?? [];

  //           let updatedAssets: MM_LinkedAssetModel[] = existingAssets;

  //           /* ===================== ADD ===================== */
  //           if (action === "add") {
  //             const alreadyLinked = existingAssets.some(
  //               (a) => a.uid === asset.uid,
  //             );

  //             if (!alreadyLinked) {
  //               const newLinkedAsset: MM_LinkedAssetModel = {
  //                 id: asset?.id ?? crypto.randomUUID(),
  //                 uid: asset.uid,
  //                 isLinked: true,
  //                 assetZone: {
  //                   area: asset.assetInfo?.assetZone?.area ?? "",
  //                   coordinates: {
  //                     lat: asset.assetInfo?.assetZone?.coordinates?.lat ?? 0,
  //                     lng: asset.assetInfo?.assetZone?.coordinates?.lng ?? 0,
  //                   },
  //                 },
  //               };

  //               updatedAssets = [...existingAssets, newLinkedAsset];

  //             }
  //           }

  //           /* ===================== REMOVE ===================== */
  //           if (action === "remove") {
  //             updatedAssets = existingAssets.filter((a) => a.uid !== asset.uid);
  //           }

  //           return from(
  //             updateDoc(docRef, {
  //               "assetLink.assets": updatedAssets,
  //               updatedDate: new Date().toISOString(),
  //             }),
  //           );
  //         }),
  //       ),
  //     ),
  //   );
  // }
  updateLinkedAssetsMMInventory(
    inventoryId: string,
    assets: AssetModel | AssetModel[],
    action: "add" | "remove",
  ): Observable<void> {
    const assetList = Array.isArray(assets) ? assets : [assets];
    return this.getTenantDoc(`mm_inventory/${inventoryId}`).pipe(
      switchMap((docRef) =>
        from(getDoc(docRef)).pipe(
          switchMap((snap) => {
            if (!snap.exists()) {
              throw new Error("Inventory not found");
            }

            const data = snap.data() as MM_InventoryModel;

            const existingAssets: MM_LinkedAssetModel[] =
              data.assetLink?.assets ?? [];

            let updatedAssets = [...existingAssets];

            /* ===================== ADD ===================== */
            if (action === "add") {
              const existingUidSet = new Set(existingAssets.map((a) => a.uid));

              const newAssets = assetList
                .filter((asset) => asset.uid && !existingUidSet.has(asset.uid))
                .map((asset) => ({
                  id: asset.id ?? crypto.randomUUID(),
                  uid: asset.uid!,
                  isLinked: true,
                  assetZone: {
                    area: asset.assetInfo?.assetZone?.area ?? "",
                    coordinates: {
                      lat: asset.assetInfo?.assetZone?.coordinates?.lat ?? 0,
                      lng: asset.assetInfo?.assetZone?.coordinates?.lng ?? 0,
                    },
                  },
                }));

              updatedAssets = [...existingAssets, ...newAssets];
            }

            /* ===================== REMOVE ===================== */
            if (action === "remove") {
              const removeUidSet = new Set(
                assetList
                  .filter((asset) => asset.uid)
                  .map((asset) => asset.uid as string),
              );

              updatedAssets = existingAssets.filter(
                (asset) => !removeUidSet.has(asset.uid),
              );
            }

            return from(
              updateDoc(docRef, {
                "assetLink.assets": updatedAssets,
                updatedDate: new Date().toISOString(),
              }),
            );
          }),
        ),
      ),
    );
  }

  // Material Control Management
  getMMControlConfig(): Observable<MM_ControlConfigModel> {
    const collectionRef$ = this.getTenantCollection("mm_control_config");

    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) =>
        collectionData(collRef, { idField: "id" }).pipe(
          map((docs: any[]) => {
            // Assuming you only store one config doc in mm_control_config
            return docs[0] as MM_ControlConfigModel;
          }),
        ),
      ),
    );
  }

  SaveMMControlInventory(
    bodyData: MM_ControlInventoryModel,
  ): Observable<SaveMMControlInventoryResponseModel> {
    const collectionRef$ = this.getTenantCollection?.("mm_control_inventory");

    return collectionRef$?.pipe(
      switchMap((collRef: CollectionReference) => {
        // Ensure ID exists
        if (!bodyData?.id) {
          bodyData.id = uuidv4?.();
          bodyData.createdDate = new Date()?.toISOString();
        }

        const docRef = doc?.(collRef, bodyData?.id);

        // Check if doc exists
        const q = query?.(collRef, where?.("id", "==", bodyData?.id));
        return collectionData?.(q, { idField: "id" })?.pipe(
          take(1),
          switchMap((existing: any[]) => {
            if (existing?.length > 0) {
              // Update existing
              bodyData.updatedDate = new Date()?.toISOString();
              const updatedData: any = { ...existing[0], ...bodyData };
              // If the payload explicitly omits sync, delete it from Firestore
              // (expert has reviewed and saved the control)
              if (!("sync" in bodyData)) {
                updatedData.sync = deleteField();
              }
              return from?.(updateDoc?.(docRef, updatedData))?.pipe(
                map(() => ({
                  success: true,
                  message: "Data updated successfully",
                  id: bodyData?.id as string,
                })),
              );
            } else {
              // Add new
              bodyData.createdDate = new Date()?.toISOString();
              return from?.(setDoc?.(docRef, bodyData))?.pipe(
                map(() => ({
                  success: true,
                  message: "Data saved successfully",
                  id: bodyData?.id as string,
                })),
              );
            }
          }),
        );
      }),
    );
  }

  // getMMInventoryListBySiteId(
  //   siteId: string,
  //   isApprovalList: boolean = false,
  // ): Observable<MM_InventoryModel[] | null> {
  //   const collectionRef$ = this.getTenantCollection("mm_inventory");
  //   return collectionRef$.pipe(
  //     switchMap((collRef: CollectionReference) => {
  //       // Filter: only documents where "asset" array contains assetName
  //       // Base query filters
  //       const filters: QueryConstraint[] = [
  //         where("siteId", "==", siteId),
  //         orderBy("createdDate", "desc"),
  //       ];

  //       // When approval list → exclude drafts
  //       if (isApprovalList) {
  //         filters.splice(1, 0, where("status", "!=", "Draft")); // insert before orderBy
  //       }
  //       // Construct query dynamically
  //       const q = query(collRef, ...filters);

  //       return collectionData(q, { idField: "id" }).pipe(
  //         map((docs) =>
  //           (docs as MM_InventoryModel[]).filter((doc) => {
  //             if (isApprovalList) {
  //               const reqStatus =
  //                 doc?.riskLevelRequestUpdateInfo?.requestStatus;
  //               // Exclude only "Pending Approval" for approval list
  //               return reqStatus !== StatusEnumModel.PENDING_APPROVAL;
  //             }
  //             // If not approval list → include all documents
  //             return true;
  //           }),
  //         ),
  //       );
  //     }),
  //   );
  // }

  getMMInventoryListBySiteId(
    siteId: string,
    isApprovalList: boolean = false,
  ): Observable<MM_InventoryModel[]> {
    const collectionRef$ = this.getTenantCollection("mm_inventory");
    const reqUpdateRef$ = this.getTenantCollection("mm_inventory_req_update");

    return combineLatest([collectionRef$, reqUpdateRef$]).pipe(
      switchMap(
        ([invColl, reqColl]: [CollectionReference, CollectionReference]) => {
          const typedInvColl =
            invColl as CollectionReference<MM_InventoryModel>;

          const filters: QueryConstraint[] = [
            where("siteId", "==", siteId),
            // where("isGlobalMaterial", "in", [false, null]),
            orderBy("createdDate", "desc"),
          ];

          // If approval list → exclude drafts
          if (isApprovalList) {
            filters.unshift(where("status", "!=", StatusEnumModel.DRAFT)); // add before other filters
          }

          // Build the final query
          const invQuery = query(typedInvColl, ...filters);
          return collectionData<MM_InventoryModel>(invQuery, {
            idField: "id",
          }).pipe(
            switchMap((inventoryDocs: MM_InventoryModel[]) => {
              if (!isApprovalList)
                return of(inventoryDocs as MM_InventoryModel[]);
              const typedReqColl =
                reqColl as CollectionReference<MM_RiskLevelRequestUpdateInfo_Model>;
              // Typed request update query
              const reqQuery = query(
                typedReqColl,
                where("requestStatus", "==", StatusEnumModel.PENDING_APPROVAL),
              );

              return collectionData<MM_RiskLevelRequestUpdateInfo_Model>(
                reqQuery,
                { idField: "id" },
              ).pipe(
                map((pendingRequests) => {
                  const pendingMaterialIds = pendingRequests?.map(
                    (material) => material?.materialId,
                  );
                  return inventoryDocs?.filter(
                    (inv) => !pendingMaterialIds?.includes(inv?.id),
                  );
                }),
              );
            }),
          );
        },
      ),
    );
  }

  getUpperBound(prefix: string): string {
    const lastChar = prefix.charCodeAt(prefix?.length - 1);
    const nextChar = String.fromCharCode(lastChar + 1);
    return prefix.slice(0, -1) + nextChar;
  }

  getMMInventoryMasterData(
    searchString: string,
    isExpertFlow: boolean,
  ): Observable<MM_InventoryModel[] | null> {
    const collectionRef$ = this.getTenantCollection(
      "mm_inventory_master_materials",
    );

    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        if (searchString) {
          const prefix = searchString.toLowerCase().trim();
          const upperBound = this.getUpperBound(prefix);
          let queries = [
            query(
              collRef,
              where("filterData.materialName", ">=", prefix),
              where("filterData.materialName", "<", upperBound),
            ),
            query(
              collRef,
              where("filterData.state", ">=", prefix),
              where("filterData.state", "<", upperBound),
            ),
            query(
              collRef,
              where("filterData.type", ">=", prefix),
              where("filterData.type", "<", upperBound),
            ),
          ];
          if (isExpertFlow) {
            queries = queries.map((queryIndex) => {
              return query(
                queryIndex,
                where("isCertified", "==", true),
                where("status", "in", ["Draft", "Published"]),
              );
            });
          } else {
            queries = queries.map((queryIndex) =>
              query(queryIndex, where("status", "==", "Published")),
            );
          }

          return combineLatest(
            queries.map((queryIndex) =>
              collectionData(queryIndex, { idField: "id" }).pipe(
                map((docs) => docs as MM_InventoryModel[]),
              ),
            ),
          ).pipe(
            map((resultsArray) => {
              const allResults = resultsArray.flat();
              const uniqueResults = Array.from(
                new Map(allResults.map((item) => [item.id, item])).values(),
              );
              return uniqueResults;
            }),
          );
        } else {
          let constraints: QueryConstraint[] = [orderBy("createdDate", "desc")];

          if (isExpertFlow) {
            constraints.push(where("isCertified", "==", true));
            constraints.push(where("status", "in", ["Draft", "Published"]));
          } else {
            constraints.unshift(where("status", "==", "Published"));
          }

          const q = query(collRef, ...constraints);
          return collectionData(q, { idField: "id" }).pipe(
            map((docs) => docs as MM_InventoryModel[]),
          );
        }
      }),
    );
  }
  getGlobalMMInventory(
    searchString: string,
    siteId: string,
    filterSiteIds?: string[],
  ): Observable<MM_InventoryModel[] | null> {
    const collectionRef$ = this.getTenantCollection("mm_inventory");
    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        if (searchString) {
          const prefix = searchString.toLowerCase().trim();
          const upperBound = this.getUpperBound(prefix);
          let queries = [
            query(
              collRef,
              where("filterData.materialName", ">=", prefix),
              where("filterData.materialName", "<", upperBound),
              where("status", "==", StatusEnumModel.PUBLISHED),
            ),
            query(
              collRef,
              where("filterData.state", ">=", prefix),
              where("filterData.state", "<", upperBound),
              where("status", "==", StatusEnumModel.PUBLISHED),
            ),
            query(
              collRef,
              where("filterData.type", ">=", prefix),
              where("filterData.type", "<", upperBound),
              where("status", "==", StatusEnumModel.PUBLISHED),
            ),
            query(
              collRef,
              where("filterData.subType", ">=", prefix),
              where("filterData.subType", "<", upperBound),
              where("status", "==", StatusEnumModel.PUBLISHED),
            ),
          ];
          return combineLatest(
            queries.map((queryIndex) =>
              collectionData(queryIndex, { idField: "id" }).pipe(
                map((docs) => docs as MM_InventoryModel[]),
              ),
            ),
          ).pipe(
            map((resultsArray) => {
              const allResults = resultsArray.flat();
              const uniqueResults = Array.from(
                new Map(allResults.map((item) => [item.id, item])).values(),
              );
              let filteredResults = uniqueResults?.filter(
                (mmInv) => mmInv?.siteId !== siteId,
              );
              return filteredResults;
            }),
          );
        } else {
          const q = query(
            collRef,
            where("siteId", "!=", siteId),
            orderBy("createdDate", "desc"),
          );

          return collectionData(q, { idField: "id" }).pipe(
            map((docs) => docs as MM_InventoryModel[]),
          );
        }
      }),
    );
  }

  GetMaterialTypes(): Observable<MM_MaterialTypes[] | null> {
    const collectionRef$ = this.getTenantCollection("mm_material_types");
    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        const q = query(collRef);

        return collectionData(q, { idField: "id" }).pipe(
          map((docs) => docs as MM_MaterialTypes[]),
        );
      }),
    );
  }
  getMMInventoryBannedWaste(): Observable<BannedWasteItemModel[]> {
    const collectionRef$ = this.getTenantCollection(
      "mm_inventory_banned_wastes",
    );
    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        const q = query(collRef);

        return collectionData(q, { idField: "id" }).pipe(
          map((docs) => docs as BannedWasteItemModel[]),
        );
      }),
    );
  }

  getMaterialHazards(): Observable<MM_HazardItem[] | null> {
    const collectionRef$ = this.getTenantCollection("mm_hazards");
    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        // Filter: only documents where "asset" array contains assetName
        const q = query(collRef);

        return collectionData(q, { idField: "id" }).pipe(
          map((docs) => docs as MM_HazardItem[]),
        );
      }),
    );
  }

  getAllRolesFromRoleMaster(): Observable<RoleMasterModel[]> {
    return this.getTenantCollection("roles_master").pipe(
      switchMap((rolesRef) => {
        const allRolesQuery = query(rolesRef);
        return collectionData(allRolesQuery, {
          idField: "id",
        }) as Observable<RoleMasterModel[]>;
      }),
    );
  }

  //     GetAllGuidance = (): Observable<any> => {
  //       return this.http.get<any>(`${ApiEndPoint.getAllGuidance}`);
  //     };

  //       getAllGuidance(): Observable<GuidanceModel[]> {

  // const collectionRef$ = this.getTenantCollection("guidance")
  //     const q = query(collectionRef$, orderBy('updatedDate', 'desc'));
  //     return collectionData(q, { idField: 'id' }).pipe(
  //       map((docs) => docs as GuidanceModel[])
  //     );
  //   }

  getMMControlInventoryList(): Observable<MM_ControlInventoryModel[]> {
    const collectionRef$ = this.getTenantCollection("mm_control_inventory");

    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        const q = query(collRef, orderBy("createdDate", "desc")); // No filters, just fetch all
        return collectionData(q, { idField: "id" }).pipe(
          map((docs) => docs as MM_ControlInventoryModel[]),
        );
      }),
    );
  }

  getMMControlInventoryWithAssets(
    assetName: string,
    offline: boolean,
  ): Observable<MM_ControlInventoryModel[]> {
    if (offline) {
      return this.offlineService.getMMControlInventoryWithAssetsOffline(
        assetName,
      );
    }
    const collectionRef$ = this.getTenantCollection("mm_control_inventory");

    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        // Filter: only documents where "asset" array contains assetName
        const q = query(
          collRef,
          where("asset", "array-contains", assetName),
          orderBy("createdDate", "desc"),
        );

        return collectionData(q, { idField: "id" }).pipe(
          map((docs) => docs as MM_ControlInventoryModel[]),
        );
      }),
    );
  }

  deleteMMControlInventory(id: string): Observable<void> {
    const collectionRef$ = this.getTenantCollection("mm_control_inventory");

    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        const docRef = doc(collRef.firestore, `mm_control_inventory/${id}`);
        return from(deleteDoc(docRef));
      }),
    );
  }

  // getDuplicateMMControlQuery(
  //   data: MM_ControlDuplicateModel,
  // ): Observable<MM_ControlInventoryModel[]> {
  //   const collectionRef$ = this.getTenantCollection("mm_control_inventory");

  //   return collectionRef$.pipe(
  //     switchMap((collRef: CollectionReference) => {
  //       let q = query(
  //         collRef,
  //         where("hazard", "==", data.hazard),
  //         where("controlType", "==", data.controlType),
  //         //where("asset", "==", data.asset),
  //       );
  //       if (data.searchKeys.length > 0) {
  //         q = query(
  //           q,
  //           where("searchKeys", "array-contains-any", data.searchKeys),
  //         );
  //       }
  //       let b = collectionData(q, { idField: "id" }).pipe(
  //         map((docs) =>
  //           (docs as MM_ControlInventoryModel[])
  //             .map((doc) => doc as MM_ControlInventoryModel)
  //             .filter((doc) => doc.id !== data.id),
  //         ),
  //       );

  //       return collectionData(q, { idField: "id" }).pipe(
  //         map((docs) =>
  //           (docs as MM_ControlInventoryModel[])
  //             .map((doc) => doc as MM_ControlInventoryModel)
  //             .filter((doc) => doc.id !== data.id),
  //         ),
  //       );
  //     }),
  //   );
  // }
  // debugCheckDatabaseFormat() {
  //   const collectionRef$ = this.getTenantCollection("mm_control_inventory");

  //   collectionRef$
  //     .pipe(
  //       switchMap((collRef) => {
  //         // We take the first 3 documents just to inspect their structure
  //         const q = query(collRef, limit(3));
  //         return collectionData(q);
  //       }),
  //       take(1), // Only run this once
  //     )
  //     .subscribe((docs) => {
  //       if (docs.length === 0) {
  //       } else {
  //         docs.forEach((doc: any, index: number) => {
  //         });
  //       }
  //     });
  // }

  getDuplicateMMControlQuery(
    data: MM_ControlDuplicateModel,
  ): Observable<MM_ControlInventoryModel[]> {
    const collectionRef$ = this.getTenantCollection("mm_control_inventory");

    return collectionRef$.pipe(
      switchMap((collRef: CollectionReference) => {
        // ✅ Search ONLY by Hazard and Control Type (Widest search)
        const q = query(
          collRef,
          where("hazard", "==", data.hazard),
          where("controlType", "==", data.controlType),
          where("status", "==", "Published"),
        );

        return collectionData(q, { idField: "id" }).pipe(
          map((docs) =>
            (docs as MM_ControlInventoryModel[]).filter(
              (doc) => doc.id !== data.id, // Exclude the current record
            ),
          ),
          // Log the final list after excluding the current record ID
          tap((finalDocs) => {}),
        );
      }),
    );
  }
  getSubAssetMasterDetails(
    titleOrSubType: string,
  ): Observable<SubAssetMasterModel | null> {
    if (!titleOrSubType) return of(null);

    return this.getTenantCollection("sub_asset_master").pipe(
      switchMap((subAssetRef: any) => {
        // Query 1: title matches
        const q1: any = query(
          subAssetRef,
          where("title", "==", titleOrSubType),
        );

        return collectionData<SubAssetMasterModel>(q1, { idField: "id" }).pipe(
          take(1),
          switchMap((titleMatches: SubAssetMasterModel[]) => {
            if (titleMatches.length) {
              return of(titleMatches[0]);
            }

            // If no title match, fetch all docs to filter subType array
            return collectionData<SubAssetMasterModel>(subAssetRef, {
              idField: "id",
            }).pipe(
              take(1),
              map((allDocs: SubAssetMasterModel[]) => {
                const match = allDocs.find((doc) =>
                  doc.subType?.some((st) => st.title === titleOrSubType),
                );
                return match || null;
              }),
            );
          }),
        );
      }),
    );
  }

  getUserProfileImageByEmail(
    email: string,
  ): Observable<ProfileImageModel | null> {
    return this.getTenantCollection("users").pipe(
      switchMap((usersCollection) => {
        const q = query(usersCollection, where("email", "==", email));
        return from(getDocs(q));
      }),
      map((usersSnap) => {
        if (!usersSnap.empty) {
          if (usersSnap.docs.length > 1) {
            console.warn(
              `Multiple users found with email ${email}. Using the first one.`,
            );
          }
          const userDoc = usersSnap.docs[0];
          const data = userDoc?.data();
          return data?.["profileImage"] ?? null;
        }
        return null;
      }),
    );
  }

  GetCountryAdminSites(locationIds: string[]): Observable<SiteModel[]> {
    return this.getTenantCollection("sites").pipe(
      map((sitesRef) =>
        query(
          sitesRef,
          where("locationKeys", "array-contains-any", locationIds),
        ),
      ),
      switchMap((sitesQuery) => collectionData(sitesQuery, { idField: "id" })),
    );
  }

  CheckCountryAdminSitesAccess(
    opcoId: string,
    siteId: string,
  ): Observable<SiteModel[]> {
    return this.getTenantCollection("sites").pipe(
      map((sitesRef) =>
        query(
          sitesRef,
          // where("locationKeys", "array-contains-any", [opcoId, siteId]),
          where("locations.site.unique_id", "==", siteId),
          where("locations.opco.id", "==", opcoId),
        ),
      ),
      switchMap((sitesQuery) => collectionData(sitesQuery, { idField: "id" })),
      take(1),
    );
  }

  getSitesByOpcoId(opcoIds: string[]): Observable<SiteModel[]> {
    if (!opcoIds || opcoIds.length === 0) {
      return of([]);
    }

    const chunkSize = 30;
    const chunks: string[][] = [];
    for (let i = 0; i < opcoIds.length; i += chunkSize) {
      chunks.push(opcoIds.slice(i, i + chunkSize));
    }

    return this.getTenantCollection("sites").pipe(
      switchMap((sitesRef) => {
        const queries = chunks.map((chunk) => {
          const sitesQuery = query(
            sitesRef,
            where("locations.opco.id", "in", chunk),
            where("isActive", "==", true),
          );
          return collectionData(sitesQuery, { idField: "id" }) as Observable<
            SiteModel[]
          >;
        });

        return queries.length > 1
          ? combineLatest(queries).pipe(map((results) => results.flat()))
          : queries[0];
      }),
    );
  }

  // One-time read of the active sites of these opcos from the server. collectionData answers first
  // with the device's offline cache (only the sites seen before), so a firstValueFrom of
  // getSitesByOpcoId can miss sites; this waits for the server (Mapping search, every opco)
  // Same sites from this device's Firestore cache only (instant; empty when never read here)
  getActiveSitesByOpcoIdFromCache(opcoIds: string[]): Observable<SiteModel[]> {
    if (!opcoIds?.length) return of([]);
    const chunks: string[][] = [];
    for (let i = 0; i < opcoIds.length; i += 30) {
      chunks.push(opcoIds.slice(i, i + 30));
    }
    return this.getTenantCollection("sites").pipe(
      switchMap((sitesRef) =>
        forkJoin(
          chunks.map((chunk) =>
            from(
              getDocsFromCache(
                query(sitesRef, where("locations.opco.id", "in", chunk), where("isActive", "==", true)),
              ),
            ),
          ),
        ),
      ),
      map((snapshots) =>
        snapshots.flatMap((snapshot) =>
          snapshot.docs.map((siteDoc) => ({ ...(siteDoc.data() as SiteModel), id: siteDoc.id })),
        ),
      ),
    );
  }

  getActiveSitesByOpcoIdFromServer(opcoIds: string[]): Observable<SiteModel[]> {
    if (!opcoIds?.length) return of([]);
    const chunks: string[][] = [];
    for (let i = 0; i < opcoIds.length; i += 30) {
      chunks.push(opcoIds.slice(i, i + 30));
    }
    return this.getTenantCollection("sites").pipe(
      switchMap((sitesRef) =>
        forkJoin(
          chunks.map((chunk) =>
            from(
              getDocsFromServer(
                query(sitesRef, where("locations.opco.id", "in", chunk), where("isActive", "==", true)),
              ),
            ),
          ),
        ),
      ),
      map((snapshots) =>
        snapshots.flatMap((snapshot) =>
          snapshot.docs.map((siteDoc) => ({ ...(siteDoc.data() as SiteModel), id: siteDoc.id })),
        ),
      ),
    );
  }

  // GetModuleSubmodules(): Observable<ModulesModel[]> {
  //   return this.getTenantCollection("modules").pipe(
  //     map((modulesRef) => query(modulesRef) as Query<ModulesModel>),
  //     switchMap((modulesQuery) =>
  //       collectionData<ModulesModel>(modulesQuery, { idField: "id" }),
  //     ),
  //   );
  // }

  GetModuleSubmodules(): Observable<ModulesWithSubmodulesModel[]> {
    return this.getTenantCollection("modules").pipe(
      map((modulesRef) => query(modulesRef) as Query<ModulesModel>),
      switchMap((modulesQuery) =>
        collectionData<ModulesModel>(modulesQuery, { idField: "id" }).pipe(
          map((modules) => this.buildModulesTree(modules, null)), // build recursive tree starting from parentId=null
        ),
      ),
    );
  }

  // Recursive function to build nested modules tree
  private buildModulesTree(
    modules: ModulesModel[],
    parentId: string | null,
  ): ModulesWithSubmodulesModel[] {
    return modules
      .filter((module) => module.parentId === parentId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((module) => ({
        ...module,
        submodules: this.buildModulesTree(modules, module.id),
      }));
  }

  GetRoleDetails(roleId: string): Observable<RoleMasterModel> {
    return this.getTenantDoc(`roles_master/${roleId}`).pipe(
      switchMap(
        (roleDocRef) =>
          docData(roleDocRef, { idField: "id" }) as Observable<RoleMasterModel>,
      ),
    );
  }

  updateMultipleUserRoles(
    users: { email: string; roles: UserRolesModel[] }[],
    updatedAt: string,
  ): Observable<void[]> {
    return from(users).pipe(
      mergeMap((user) =>
        this.getTenantDoc(`users/${user.email}`).pipe(
          switchMap((userDocRef) =>
            from(updateDoc(userDocRef, { roles: user.roles, updatedAt })),
          ),
        ),
      ),
      toArray(), // collect all results into an array
    );
  }
  getUsersByCountry(
    countryUniqueId: string,
    siteUniqueId: string,
  ): Observable<MapUsersByGroup> {
    if (!countryUniqueId) {
      return of({ noPermission: [], fieldEditor: [], structuralEditor: [] });
    }

    return this.getTenantCollection("users").pipe(
      switchMap((usersRef) => {
        const usersQuery1 = query(
          usersRef,
          where("defaultLocation.country.locationId", "==", countryUniqueId),
          // where("adminRole", "==", "user"),
        );

        const usersQuery2 = query(
          usersRef,
          where("checkedInLocation.country.locationId", "==", countryUniqueId),
          // where("adminRole", "==", "user"),
        );

        const obs1 = collectionData(usersQuery1, { idField: "id" });
        const obs2 = collectionData(usersQuery2, { idField: "id" });

        return combineLatest([obs1, obs2]).pipe(
          map(([users1, users2]: [EditorUserModel[], EditorUserModel[]]) => {
            // Merge + deduplicate by id
            const uniqueUsers = Array.from(
              new Map([...users1, ...users2].map((u) => [u.id, u])).values(),
            ).filter((u) => isActiveUser(u as unknown as { status?: string }));

            // Initialize grouped buckets with Map
            const grouped = new Map<string, EditorUserModel[]>([
              ["noPermission", []],
              ["fieldEditor", []],
              ["structuralEditor", []],
            ]);

            // Single pass grouping
            uniqueUsers.forEach(
              ({
                id,
                email,
                roles = [],
                adminRole,
                updatedAt,
                profileImage,
                firstName = "",
                lastName = "",
              }) => {
                const isStructuralEditor = roles.some(
                  (r: UserRolesModel) =>
                    r.locationId === siteUniqueId &&
                    r.role === "editor" &&
                    r.roleName === "Editor",
                );

                const isFieldEditor = roles.some(
                  (r: UserRolesModel) =>
                    r.locationId === siteUniqueId &&
                    r.role === "field_editor" &&
                    r.roleName === "Field Editor",
                );

                const user = {
                  id,
                  email,
                  roles,
                  adminRole,
                  updatedAt: updatedAtFormatToDDMMYYYY(updatedAt),
                  profileImage,
                  name: `${firstName} ${lastName}`.trim(),
                  isStructuralEditor,
                  isFieldEditor,
                };

                if (isFieldEditor) grouped.get("fieldEditor")!.push(user);
                if (isStructuralEditor)
                  grouped.get("structuralEditor")!.push(user);
                if (!isFieldEditor && !isStructuralEditor)
                  grouped.get("noPermission")!.push(user);
              },
            );

            // Return strongly typed object
            return {
              noPermission: grouped.get("noPermission")!,
              fieldEditor: grouped.get("fieldEditor")!,
              structuralEditor: grouped.get("structuralEditor")!,
            };
          }),
        );
      }),
    );
  }
  // Security mapping permissions (developer's request 2026-10-11): the users who
  // hold country_security_expert for one country. The country is the one the
  // Country check-in resolved to, so the id matched against the role's locationId
  // is the same countries/{unique_id} the data is saved under.
  getSecurityExpertsByCountry(
    countryUniqueId: string,
  ): Observable<EditorUserModel[]> {
    if (!countryUniqueId) return of([]);

    return this.getTenantCollection("users").pipe(
      switchMap((usersRef) =>
        // Held by the role, not by where the user lives: an expert of this country
        // needn't be based in it. countrySecurityExpertRole() is the whole stored
        // entry, which is what Firestore matches an object in an array on.
        collectionData(
          query(
            usersRef,
            where(
              "roles",
              "array-contains",
              countrySecurityExpertRole(countryUniqueId),
            ),
          ),
          { idField: "id" },
        ).pipe(
          map((users: EditorUserModel[]) =>
            users
              .filter((user) =>
                isActiveUser(user as unknown as { status?: string }),
              )
              .map(
                ({
                  id,
                  email,
                  roles = [],
                  adminRole,
                  updatedAt,
                  profileImage,
                  firstName = "",
                  lastName = "",
                }) => ({
                  id,
                  email,
                  roles,
                  adminRole,
                  profileImage,
                  updatedAt: updatedAtFormatToDDMMYYYY(updatedAt),
                  name: `${firstName} ${lastName}`.trim(),
                  isCountrySecurityExpert: roles.some(
                    (role: UserRolesModel) =>
                      role.role === RoleTypeEnum.CountrySecurityExpert &&
                      role.locationId === countryUniqueId,
                  ),
                }),
              )
              .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
          ),
          catchError(() => of([] as EditorUserModel[])),
        ),
      ),
    );
  }

  // Security mapping permission search. A country security expert needn't live in
  // the country, so what is typed is searched across the whole users collection.
  // Firestore has no substring search: each field is matched as a prefix
  // (orderBy + startAt/endAt), which single-field indexes already cover — no
  // composite index to deploy. Names are stored capitalised, emails lower case, so
  // both spellings of the term are tried.
  searchUsersForCountryPermission(
    term: string,
    countryUniqueId: string,
  ): Observable<EditorUserModel[]> {
    const trimmed = term.trim();
    if (trimmed.length < MIN_USER_SEARCH_LENGTH || !countryUniqueId) {
      return of([]);
    }

    const spellings = Array.from(
      new Set([
        trimmed,
        trimmed.toLowerCase(),
        trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase(),
      ]),
    );
    // An email is only ever matched on the email field, a name on the name fields
    const fields = trimmed.includes("@")
      ? ["email"]
      : ["firstName", "lastName", "email"];

    return this.getTenantCollection("users").pipe(
      switchMap((usersRef) => {
        const searches = fields.flatMap((field) =>
          spellings.map((spelling) =>
            collectionData(
              query(
                usersRef,
                orderBy(field),
                startAt(spelling),
                endAt(`${spelling}\uf8ff`),
                limit(USER_SEARCH_LIMIT),
              ),
              { idField: "id" },
            ).pipe(catchError(() => of([] as EditorUserModel[]))),
          ),
        );

        return combineLatest(searches).pipe(
          map((results: EditorUserModel[][]) =>
            Array.from(
              new Map(
                results.flat().map((user) => [user.email, user]),
              ).values(),
            )
              .filter((user) =>
                isActiveUser(user as unknown as { status?: string }),
              )
              .map((user) => toCountryPermissionUser(user, countryUniqueId))
              .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
              .slice(0, USER_SEARCH_LIMIT),
          ),
          catchError(() => of([] as EditorUserModel[])),
        );
      }),
    );
  }

  getCountryAdminsByOpco(opcoId: string): Observable<UserModel[]> {
    if (!opcoId) {
      return of([]);
    }
    return this.getTenantCollection("users").pipe(
      switchMap((usersRef) => {
        const usersQuery = query(
          usersRef,
          where("adminRole", "==", "country_admin"),
        );

        return collectionData(usersQuery, { idField: "id" }).pipe(
          map((users: UserModel[]) => {
            const filtered = users.filter((user) => {
              const hasOpco = (user.countryAccessList || []).some((entry) => {
                return entry?.opco_id === opcoId;
              });
              return hasOpco;
            });
            return filtered.map(
              ({
                id,
                email,
                roles = [],
                updatedAt,
                profileImage,
                firstName = "",
                lastName = "",
              }) => ({
                id,
                email,
                roles,
                updatedAt,
                profileImage,
                firstName,
                lastName,
              }),
            );
          }),
        );
      }),
    );
  }
  getsiteAdminsBySite(SiteId: string): Observable<UserModel[]> {
    if (!SiteId) {
      return of([]);
    }
    return this.getTenantCollection("users").pipe(
      switchMap((usersRef) => {
        const usersQuery = query(usersRef, where("adminRole", "==", "admin"));

        return collectionData(usersQuery, { idField: "id" }).pipe(
          map((users: UserModel[]) => {
            const filtered = users.filter((user) => {
              const hasOpco = (user.plantAssigned || []).some((entry) => {
                return entry?.unique_id === SiteId;
              });
              return hasOpco;
            });
            return filtered.map(
              ({
                id,
                email,
                roles = [],
                updatedAt,
                profileImage,
                firstName = "",
                lastName = "",
              }) => ({
                id,
                email,
                roles,
                updatedAt,
                profileImage,
                firstName,
                lastName,
              }),
            );
          }),
        );
      }),
    );
  }

  getDefectTypeMaster(): Observable<DefectType[]> {
    return this.getTenantCollection("defect_type_master").pipe(
      switchMap(
        (defectTypeRef) =>
          collectionData(defectTypeRef, { idField: "id" }) as Observable<
            DefectType[]
          >,
      ),
    );
  }
  updateSAPAssetsStatus(
    assetIds: string[],
    countryUniqueId: string,
    inUse: boolean,
  ): Observable<void> {
    const BATCH_SIZE = 10;
    const BATCH_DELAY_MS = 100;

    if (!assetIds || assetIds?.length === 0 || !countryUniqueId) {
      return of();
    }

    return from(assetIds).pipe(
      mergeMap((assetId) => {
        const docPath = `sync_asset_master/${countryUniqueId}/sap_assets/${assetId}`;

        return this.getTenantDoc(docPath).pipe(
          mergeMap((docRef) =>
            from(getDoc(docRef)).pipe(
              mergeMap((docSnap) => {
                if (docSnap.exists()) {
                  return from(updateDoc(docRef, { inUse }));
                }
                return of();
              }),
            ),
          ),
          catchError((error) => {
            return of();
          }),
        );
      }, BATCH_SIZE),
      bufferCount(BATCH_SIZE),
      mergeMap((batch, index) =>
        index === 0
          ? of(...batch)
          : timer(BATCH_DELAY_MS).pipe(mergeMap(() => of(...batch))),
      ),
    );
  }
  updateDetectionInAssessment(
    assessmentId: string,
    assetId: string,
    hazardStateControl: AssessmentAssetDetectionModel,
  ): Observable<AssessmentModel> {
    return this.getTenantDoc(`assessments/${assessmentId}`).pipe(
      switchMap((assessmentRef) =>
        from(getDoc(assessmentRef)).pipe(
          switchMap((docSnap) => {
            const data = docSnap?.data() as AssessmentModel;
            if (data && Array.isArray(data.assets)) {
              const assets = [...data.assets];
              const assetIndex = assets.findIndex(
                (asset) => asset?.id === assetId,
              );
              if (assetIndex !== -1) {
                assets[assetIndex] = {
                  ...assets[assetIndex],
                  hazardStateControl: hazardStateControl,
                };
                return from(updateDoc(assessmentRef, { assets })).pipe(
                  switchMap(
                    () =>
                      docData(assessmentRef, {
                        idField: "id",
                      }) as Observable<AssessmentModel>,
                  ),
                );
              }
            }

            return of(data);
          }),
        ),
      ),
    );
  }

  uploadImage(image: any) {
    return new Observable<number | string>((observer) => {
      const storageRef = ref(this.storage, "spincoFile/" + image.name);
      const uploadTask = uploadBytesResumable(storageRef, image);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next(progress);
        },
        (error) => {
          observer.error(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            observer.next(downloadURL);
            observer.complete();
          });
        },
      );
    });
  }

  uploadTriviaFile(file: File) {
    return new Observable<number | string>((observer) => {
      const storageRef = ref(this.storage, "trivia/" + file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next(progress);
        },
        (error) => {
          observer.error(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            observer.next(downloadURL);
            observer.complete();
          });
        },
      );
    });
  }

  uploadFlameImage(file: File, topicName: string): Observable<number | string> {
    return new Observable<number | string>((observer) => {
      const safeTopic = topicName.replace(/[^a-zA-Z0-9_-]/g, "_");
      const storagePath = `flame_images/${safeTopic}/${Date.now()}_${file.name}`;
      const storageRef = ref(this.storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next(progress);
        },
        (error) => {
          observer.error(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            observer.next(downloadURL);
            observer.complete();
          });
        },
      );
    });
  }

  addFlameImageDoc(data: {
    url: string;
    filename: string;
    tags: string[];
    topicName: string;
    category: string;
    uploadedBy: string;
  }): Observable<string> {
    return this.getTenantCollection("flame_images").pipe(
      switchMap((colRef) =>
        from(addDoc(colRef, { ...data, createdAt: Timestamp.now() })),
      ),
      map((docRef) => docRef.id),
    );
  }

  getFlameImages(): Observable<
    {
      id: string;
      url: string;
      filename: string;
      tags: string[];
      topicName: string;
      category: string;
    }[]
  > {
    return this.getTenantCollection("flame_images").pipe(
      switchMap((colRef) => {
        const _query = query(colRef, orderBy("createdAt", "desc"));
        return from(getDocs(_query)).pipe(
          map((snap) =>
            snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                url: data["url"] || "",
                filename: data["filename"] || "",
                tags: data["tags"] || [],
                topicName: data["topicName"] || "",
                category: data["category"] || "",
              };
            }),
          ),
        );
      }),
    );
  }

  getFlameConfiguration(): Observable<any> {
    return this.getTenantCollection("flame_configurations").pipe(
      switchMap((colRef) => {
        const _query = query(colRef, where("active", "==", true), limit(1));
        return from(getDocs(_query)).pipe(
          map((snap) => {
            if (snap.empty) return null;
            return { id: snap.docs[0].id, ...snap.docs[0].data() };
          }),
        );
      }),
    );
  }

  getFlameModules(): Observable<any[]> {
    return this.getTenantCollection("flame_suggested_modules").pipe(
      switchMap((colRef) => {
        return from(getDocs(colRef)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  getFlameModulesList(): Observable<any[]> {
    return this.getTenantCollection("flame_modules").pipe(
      switchMap((colRef) => {
        const _query = query(colRef, where("active", "==", true));
        return from(getDocs(_query)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  flameCategories: FlameCategory[] = [];

  loadFlameCategories(): Observable<FlameCategory[]> {
    if (this.flameCategories.length) {
      return of(this.flameCategories);
    }
    return this.getTenantDoc("flame_configurations/FLAME_CATEGORIES").pipe(
      switchMap((ref) => from(getDoc(ref))),
      map((snap) => {
        const data = snap.data();
        return (data?.["categories"] as FlameCategory[]) || [];
      }),
      tap((categories) => {
        this.flameCategories = categories;
      }),
    );
  }

  getFlameModulesByTopic(topic: string): Observable<any[]> {
    return this.getTenantCollection("flame_modules").pipe(
      switchMap((colRef) => {
        const _query = query(colRef, where("topic", "==", topic));
        return from(getDocs(_query)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  updateFlameModule(
    docId: string,
    data: Record<string, any>,
  ): Observable<void> {
    return this.getTenantCollection("flame_modules").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, docId);
        return from(updateDoc(docRef, data));
      }),
    );
  }

  updateFlameTrivia(
    docId: string,
    data: Record<string, any>,
  ): Observable<void> {
    return this.getTenantCollection("flame_trivia").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, docId);
        return from(updateDoc(docRef, data));
      }),
    );
  }

  addFlameTrivia(data: Record<string, any>): Observable<string> {
    return this.getTenantCollection("flame_trivia").pipe(
      switchMap((colRef) => from(addDoc(colRef, data))),
      map((docRef) => docRef.id),
    );
  }

  addTraining(data: Record<string, any>): Observable<string> {
    return this.getTenantCollection("trainings").pipe(
      switchMap((colRef) => from(addDoc(colRef, { ...data, createdAt: Timestamp.now() }))),
      map((docRef) => docRef.id),
    );
  }

  getTrainings(opcoId?: string, scope?: string): Observable<any[]> {
    return this.getTenantCollection("trainings").pipe(
      switchMap((colRef) => {
        const constraints: QueryConstraint[] = [];
        if (opcoId) constraints.push(where("location.opco_id", "==", opcoId));
        if (scope) constraints.push(where("scope", "==", scope));
        const q = constraints.length ? query(colRef, ...constraints) : query(colRef);
        return from(getDocs(q));
      }),
      map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }

  getDefaultTrainings(): Observable<any[]> {
    return this.getTenantCollection("trainings").pipe(
      switchMap((colRef) => from(getDocs(
        query(colRef, where("scope", "==", "Default"), where("status", "==", "active")),
      ))),
      map((snap) => snap.docs.map((d) => ({ id: d.id, isPinned: true, ...d.data(), trainingStatus: 'ACTIVE_AT_KIOSK' }))),
    );
  }

  getTrainingsBySite(siteUniqueId: string): Observable<any[]> {
    return this.getTenantCollection("trainings").pipe(
      switchMap((colRef) => {
        const q = query(
          colRef,
          where("scope", "==", "Site"),
          where("location.unique_id", "==", siteUniqueId),
          where("status", "==", "active"),
        );
        return from(getDocs(q));
      }),
      map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }

  getTrainingsBySiteCatalog(siteUniqueId: string): Observable<any[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, "training_sites"),
          where("location.unique_id", "==", siteUniqueId),
          where("status", "==", "active"),
        );
        return from(getDocs(q));
      }),
      switchMap((snap) => {
        if (snap.empty) return from([[]]);
        const parentFetches = snap.docs.map((siteDoc) => {
          const trainingRef = siteDoc.ref.parent.parent!;
          return from(getDoc(trainingRef)).pipe(
            map((trainingDoc) => trainingDoc.exists()
              ? { id: trainingDoc.id, ...trainingDoc.data() }
              : null,
            ),
          );
        });
        return forkJoin(parentFetches);
      }),
      map((results: any[]) => results.filter(Boolean)),
    );
  }

  incrementTotalSitesAdopted(trainingId: string): Observable<void> {
    return this.getTenantDoc(`trainings/${trainingId}`).pipe(
      switchMap((docRef) => from(updateDoc(docRef, { total_sites_adopted: increment(1) }))),
    );
  }

  getActivatedTrainingIdsForSite(siteUniqueId: string): Observable<string[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, "training_sites"),
          where("location.unique_id", "==", siteUniqueId),
        );
        return from(getDocs(q));
      }),
      map((snap) => snap.docs.map((d) => d.ref.parent.parent!.id)),
    );
  }

  getTrainingById(trainingId: string): Observable<any> {
    return this.getTenantDoc(`trainings/${trainingId}`).pipe(
      switchMap((docRef) => from(getDoc(docRef))),
      map((snap) => snap.exists() ? { id: snap.id, ...snap.data() } : null),
    );
  }

  addTrainingToSiteCatalogue(trainingId: string, siteUniqueId: string, siteLocation: Record<string, any>): Observable<void> {
    return this.getTenantDoc(`trainings/${trainingId}/training_sites/${siteUniqueId}`).pipe(
      switchMap((docRef) => from(setDoc(docRef, {
        location: siteLocation,
        status: "active",
        addedAt: Timestamp.now(),
      }))),
    );
  }

  inactivateTrainingSite(trainingId: string, siteUniqueId: string, performedBy: { email: string; name: string }): Observable<void> {
    return this.getTenantDoc(`trainings/${trainingId}/training_sites/${siteUniqueId}`).pipe(
      switchMap((docRef) => from(setDoc(docRef, {
        status: 'inactive',
        inactivatedBy: performedBy,
        inactivatedAt: Timestamp.now(),
      }, { merge: true }))),
    );
  }

  addTrainingInstructor(
    trainingId: string,
    siteUniqueId: string,
    userId: string,
    data: Record<string, any>,
  ): Observable<void> {
    // Ensure training_sites parent doc exists so getTrainingSiteIds can find it
    const ensureSiteDoc$ = this.getTenantDoc(`trainings/${trainingId}/training_sites/${siteUniqueId}`).pipe(
      switchMap((siteRef) => from(setDoc(siteRef, { trainingId, siteUniqueId }, { merge: true }))),
    );
    const writeInstructor$ = this.getTenantDoc(
      `trainings/${trainingId}/training_sites/${siteUniqueId}/training_instructors/${userId}`,
    ).pipe(switchMap((docRef) => from(setDoc(docRef, data))));
    return from(ensureSiteDoc$).pipe(switchMap(() => writeInstructor$));
  }

  updateTrainingInstructorStatus(trainingId: string, siteUniqueId: string, userId: string, status: "active" | "inactive"): Observable<void> {
    return this.getTenantDoc(
      `trainings/${trainingId}/training_sites/${siteUniqueId}/training_instructors/${userId}`,
    ).pipe(switchMap((docRef) => from(updateDoc(docRef, { status }))));
  }

  deleteTrainingInstructor(trainingId: string, siteUniqueId: string, userId: string): Observable<void> {
    return this.getTenantDoc(
      `trainings/${trainingId}/training_sites/${siteUniqueId}/training_instructors/${userId}`,
    ).pipe(switchMap((docRef) => from(deleteDoc(docRef))));
  }

  getTrainingSiteIds(trainingId: string): Observable<string[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => from(getDocs(collection(db, `trainings/${trainingId}/training_sites`)))),
      map((snap) => snap.docs.map((d) => d.id)),
    );
  }

  getTrainingSites(trainingId: string): Observable<{ id: string; name: string }[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => from(getDocs(collection(db, `trainings/${trainingId}/training_sites`)))),
      map((snap) => snap.docs.map((d) => ({
        id: d.id,
        name: (d.data() as any)?.location?.name || d.id,
      }))),
    );
  }

  getTrainingInstructors(trainingId: string, siteUniqueId: string): Observable<any[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const colRef = collection(db, `trainings/${trainingId}/training_sites/${siteUniqueId}/training_instructors`);
        return new Observable<any[]>((observer) => {
          const unsub = onSnapshot(colRef, (snap) => {
            observer.next(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          }, (err) => observer.error(err));
          return () => unsub();
        });
      }),
    );
  }

  // Fallback: find instructors across all sites for a training via collection group.
  // Used when no training_sites document exists (e.g. instructors added before the parent-doc fix).
  getTrainingInstructorsByGroup(trainingId: string): Observable<any[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(collectionGroup(db, 'training_instructors'), where('trainingId', '==', trainingId));
        return new Observable<any[]>((observer) => {
          const unsub = onSnapshot(q, (snap) => {
            observer.next(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          }, (err) => observer.error(err));
          return () => unsub();
        });
      }),
    );
  }

  getInstructorCountsBySite(trainingIds: string[], siteUniqueId: string): Observable<Map<string, number>> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const checks = trainingIds.map((tid) => {
          const colRef = collection(db, `trainings/${tid}/training_sites/${siteUniqueId}/training_instructors`);
          return from(getDocs(query(colRef))).pipe(
            map((snap) => ({ tid, count: snap.size })),
            catchError(() => of({ tid, count: 0 })),
          );
        });
        return checks.length ? forkJoin(checks) : of([] as { tid: string; count: number }[]);
      }),
      map((results) => {
        const counts = new Map<string, number>();
        results.forEach(({ tid, count }) => counts.set(tid, count));
        return counts;
      }),
    );
  }

  getSessionCountsBySite(trainingIds: string[], siteUniqueId: string): Observable<Map<string, number>> {
    if (!trainingIds.length) return of(new Map());
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const chunks: string[][] = [];
        for (let i = 0; i < trainingIds.length; i += 10) chunks.push(trainingIds.slice(i, i + 10));
        const queries$ = chunks.map((chunk) =>
          from(getDocs(query(
            collectionGroup(db, 'training_sessions'),
            where('trainingId', 'in', chunk),
            where('siteUniqueId', '==', siteUniqueId),
          ))).pipe(
            map((snap) => snap.docs.map((d) => d.data() as any)),
            catchError(() => of([] as any[])),
          )
        );
        return forkJoin(queries$).pipe(
          map((results) => {
            const countMap = new Map<string, number>();
            results.flat().forEach((s) => {
              if (s.trainingId) countMap.set(s.trainingId, (countMap.get(s.trainingId) || 0) + 1);
            });
            return countMap;
          }),
        );
      }),
    );
  }

  getSessionCountsByTrainingIds(trainingIds: string[]): Observable<Map<string, number>> {
    if (!trainingIds.length) return of(new Map());
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const chunks: string[][] = [];
        for (let i = 0; i < trainingIds.length; i += 10) chunks.push(trainingIds.slice(i, i + 10));
        const queries$ = chunks.map((chunk) =>
          from(getDocs(query(
            collectionGroup(db, 'training_sessions'),
            where('trainingId', 'in', chunk),
          ))).pipe(
            map((snap) => snap.docs.map((d) => d.data() as any)),
            catchError(() => of([] as any[])),
          )
        );
        return forkJoin(queries$).pipe(
          map((results) => {
            const countMap = new Map<string, number>();
            results.flat().forEach((s) => {
              if (s.trainingId) countMap.set(s.trainingId, (countMap.get(s.trainingId) || 0) + 1);
            });
            return countMap;
          }),
        );
      }),
    );
  }

  getInstructorCountsByTrainingIds(trainingIds: string[]): Observable<Map<string, number>> {
    if (!trainingIds.length) return of(new Map());
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const chunks: string[][] = [];
        for (let i = 0; i < trainingIds.length; i += 10) chunks.push(trainingIds.slice(i, i + 10));
        const queries$ = chunks.map((chunk) => {
          const q = query(
            collectionGroup(db, 'training_instructors'),
            where('trainingId', 'in', chunk),
            where('status', '==', 'active'),
          );
          return from(getDocs(q)).pipe(
            map((snap) => snap.docs.map((d) => d.data() as any)),
            catchError(() => of([] as any[])),
          );
        });
        return forkJoin(queries$).pipe(
          map((results) => {
            const countMap = new Map<string, number>();
            results.flat().forEach((inst) => {
              const id = inst.trainingId;
              if (id) countMap.set(id, (countMap.get(id) || 0) + 1);
            });
            return countMap;
          }),
        );
      }),
    );
  }

  getTrainingSessionsThisYear(siteUniqueId: string): Observable<any[]> {
    const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString();
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, 'training_sessions'),
          where('siteUniqueId', '==', siteUniqueId),
          where('session_start', '>=', startOfYear),
        );
        return from(getDocs(q)).pipe(
          map((snap) => snap.docs.map((d) => d.data())),
          catchError((err) => {
            const msg: string = err?.message || '';
            const indexUrl = msg.match(/https:\/\/console\.firebase\.google\.com[^\s]*/)?.[0];
            if (indexUrl) {
              console.error('[SessionStats] Missing Firestore index — create it here:', indexUrl);
            } else {
              console.error('[SessionStats] query failed:', err);
            }
            return of([]);
          }),
        );
      }),
    );
  }

  getTrainingSessionsThisYearByOpco(opcoId: string): Observable<any[]> {
    const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString();
    return this.getTenantCollection('sites').pipe(
      switchMap((colRef) => {
        const sitesQ = query(colRef, where('locations.opco.id', '==', opcoId));
        return from(getDocs(sitesQ)).pipe(
          map((snap) => snap.docs
            .map((d) => (d.data() as any)?.locations?.site?.unique_id as string)
            .filter(Boolean)),
        );
      }),
      switchMap((siteIds) => {
        if (!siteIds.length) return of([]);
        return from(this.tenantService.getMyDb()).pipe(
          switchMap((db) => {
            const chunks: string[][] = [];
            for (let i = 0; i < siteIds.length; i += 10) chunks.push(siteIds.slice(i, i + 10));
            const queries$ = chunks.map((chunk) => {
              const q = query(
                collectionGroup(db, 'training_sessions'),
                where('siteUniqueId', 'in', chunk),
                where('session_start', '>=', startOfYear),
              );
              return from(getDocs(q)).pipe(
                map((snap) => snap.docs.map((d) => d.data())),
                catchError((err) => {
                  const msg: string = err?.message || '';
                  const indexUrl = msg.match(/https:\/\/console\.firebase\.google\.com[^\s]*/)?.[0];
                  if (indexUrl) console.error('[SessionStats] Missing Firestore index — create it here:', indexUrl);
                  else console.error('[SessionStats] query failed:', err);
                  return of([]);
                }),
              );
            });
            return forkJoin(queries$).pipe(map((results) => results.flat()));
          }),
        );
      }),
    );
  }

  getActiveSiteCountByOpco(opcoId: string): Observable<number> {
    return this.getTenantCollection('sites').pipe(
      switchMap((colRef) => {
        const q = query(colRef, where('locations.opco.id', '==', opcoId), where('locations.site.active', '==', 'Yes'));
        return from(getDocs(q)).pipe(
          map((snap) => snap.size),
          catchError((err) => {
            const msg: string = err?.message || '';
            const indexUrl = msg.match(/https:\/\/console\.firebase\.google\.com[^\s]*/)?.[0];
            if (indexUrl) {
              console.error('[Sites] Missing Firestore index — create it here:', indexUrl);
            } else {
              console.error('[Sites] getActiveSiteCountByOpco failed:', err);
            }
            return of(0);
          }),
        );
      }),
    );
  }

  getTrainingSessions(trainingId: string, siteUniqueId: string): Observable<any[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, 'training_sessions'),
          where('trainingId', '==', trainingId),
          where('siteUniqueId', '==', siteUniqueId),
        );
        return new Observable<any[]>((observer) => {
          const unsub = onSnapshot(q, (snap) => {
            observer.next(snap.docs.map((d) => {
              const parts = d.ref.path.split('/');
              const instrIdx = parts.indexOf('training_instructors');
              return {
                id: d.id,
                instructorEmail: instrIdx !== -1 ? parts[instrIdx + 1] : '',
                ...d.data(),
              };
            }));
          }, (err) => observer.error(err));
          return () => unsub();
        });
      }),
    );
  }

  getTrainingSessionsAllSites(trainingId: string): Observable<any[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, 'training_sessions'),
          where('trainingId', '==', trainingId),
        );
        return new Observable<any[]>((observer) => {
          const unsub = onSnapshot(q, (snap) => {
            observer.next(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          }, (err) => observer.error(err));
          return () => unsub();
        });
      }),
    );
  }

  getCountryLibraryTrainings(opcoId: string): Observable<any[]> {
    return this.getTenantCollection("trainings").pipe(
      switchMap((colRef) => {
        const q = query(
          colRef,
          where("scope", "==", "Country"),
          where("location.opco_id", "==", opcoId),
          where("status", "==", "active"),
        );
        return from(getDocs(q));
      }),
      map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }

  getTrainingsForSiteAdmin(opcoId: string, siteUniqueId: string): Observable<any[]> {
    return this.getTenantCollection("trainings").pipe(
      switchMap((colRef) => {
        const countryQ = query(
          colRef,
          where("scope", "==", "Country"),
          where("location.opco_id", "==", opcoId),
          where("status", "==", "active"),
        );
        const siteQ = query(
          colRef,
          where("scope", "==", "Site"),
          where("location.unique_id", "==", siteUniqueId),
          where("status", "==", "active"),
        );
        return forkJoin([from(getDocs(countryQ)), from(getDocs(siteQ))]);
      }),
      map(([countrySnap, siteSnap]) => [
        ...countrySnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        ...siteSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
      ]),
    );
  }

  getTrainingConfig(trainingId: string, opcoId: string): Observable<any> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const primaryRef = doc(db, `trainings/${trainingId}/config/${opcoId}`);
        return from(getDoc(primaryRef)).pipe(
          switchMap((snap) => {
            if (snap.exists()) return of({ id: snap.id, ...snap.data() });
            const fallbackRef = doc(db, `trainings/${trainingId}/config/default`);
            return from(getDoc(fallbackRef)).pipe(
              map((fb) => fb.exists() ? { id: fb.id, ...fb.data() } : null),
            );
          }),
          catchError(() => of(null)),
        );
      }),
    );
  }

  uploadTrainingVideo(file: File, trainingId: string, siteUniqueId: string): Observable<{ progress: number; url?: string }> {
    return from(this.tenantService.getUserStorage()).pipe(
      switchMap((storage) => {
        const path = `training-videos/${trainingId}/${siteUniqueId}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(storageRef, file);
        return new Observable<{ progress: number; url?: string }>((observer) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              observer.next({ progress });
            },
            (error) => observer.error(error),
            () => {
              getDownloadURL(uploadTask.snapshot.ref).then((url) => {
                observer.next({ progress: 100, url });
                observer.complete();
              }).catch((err) => observer.error(err));
            },
          );
        });
      }),
    );
  }

  getSessionsForTrainingAtSite(trainingId: string, siteUniqueId: string): Observable<any[]> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const q = query(
          collectionGroup(db, 'training_sessions'),
          where('trainingId', '==', trainingId),
          where('siteUniqueId', '==', siteUniqueId),
        );
        return from(getDocs(q)).pipe(
          map((snap) => snap.docs.map((d) => d.data())),
          catchError(() => of([])),
        );
      }),
    );
  }

  getSiteTrainingConfig(trainingId: string, siteUniqueId: string): Observable<any> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const ref = doc(db, `trainings/${trainingId}/config/${siteUniqueId}`);
        return from(getDoc(ref)).pipe(
          map((snap) => snap.exists() ? { id: snap.id, ...snap.data() } : null),
          catchError(() => of(null)),
        );
      }),
    );
  }

  saveTrainingConfig(trainingId: string, siteUniqueId: string, data: Record<string, any>): Observable<void> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const ref = doc(db, `trainings/${trainingId}/config/${siteUniqueId}`);
        return from(setDoc(ref, { ...data, updatedAt: new Date().toISOString() }, { merge: true }));
      }),
      map(() => void 0),
    );
  }

  moveTrainingToLibrary(
    trainingId: string,
    opco: { opco_id: string; opco: string },
    performedBy: { email: string; name: string },
    previousScope: string,
  ): Observable<void> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const trainingRef = doc(db, `trainings/${trainingId}`);
        return from(updateDoc(trainingRef, {
          scope: 'Country',
          'location.opco_id': opco.opco_id,
          'location.opco': opco.opco,
          promotedToLibrary: {
            by: performedBy,
            at: new Date().toISOString(),
            previousScope,
          },
        }));
      }),
      map(() => void 0),
    );
  }

  getLocalTrainings(opcoId: string): Observable<any[]> {
    return this.getTenantCollection("trainings").pipe(
      switchMap((colRef) => {
        const q = query(
          colRef,
          where("scope", "in", ["Unit", "Site"]),
          where("location.opco_id", "==", opcoId),
          where("status", "==", "active"),
        );
        return from(getDocs(q));
      }),
      map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }

  migrateTrainings(): Observable<void> {
    return this.getTenantCollection("trainings").pipe(
      switchMap((colRef) => from(getDocs(query(colRef)))),
      switchMap((snap) => {
        const db$ = from(this.tenantService.getMyDb());
        return db$.pipe(
          switchMap((db) => {
            const batch = writeBatch(db);
            snap.docs.forEach((d) => {
              const data = d.data() as Record<string, any>;
              const updates: Record<string, any> = {};
              if (!("total_sites_adopted" in data)) updates["total_sites_adopted"] = 0;
              if (!("total_sessions" in data)) updates["total_sessions"] = 0;
              if (!("status" in data)) updates["status"] = "active";
              if ("adoptedBy" in data) updates["adoptedBy"] = deleteField();
              if (Object.keys(updates).length > 0) batch.update(d.ref, updates);
            });
            return from(batch.commit());
          }),
        );
      }),
      map(() => void 0),
    );
  }

  getFlameTriviaById(docId: string): Observable<any> {
    return this.getTenantCollection("flame_trivia").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, docId);
        return from(getDoc(docRef)).pipe(
          map((snap) =>
            snap.exists() ? { id: snap.id, ...snap.data() } : null,
          ),
        );
      }),
    );
  }

  getFlameTriviaLessonsByTopic(topic: string): Observable<any[]> {
    return this.getTenantCollection("flame_trivia").pipe(
      switchMap((colRef) => {
        const _query = query(
          colRef,
          where("topic", "==", topic),
          where("type", "==", "generated_lessons"),
        );
        return from(getDocs(_query)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  getFlameTriviaLessonsByTopicDocId(topicDocId: string): Observable<any[]> {
    return this.getTenantCollection("flame_trivia").pipe(
      switchMap((colRef) => {
        const _query = query(
          colRef,
          where("topic_doc_id", "==", topicDocId),
          where("type", "==", "generated_lessons"),
        );
        return from(getDocs(_query)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  getFlameTriviaAllLessons(): Observable<any[]> {
    return this.getTenantCollection("flame_trivia").pipe(
      switchMap((colRef) => {
        const _query = query(colRef, where("type", "==", "generated_lessons"));
        return from(getDocs(_query)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  getFlameTrivia(userId: string, topic: string): Observable<any[]> {
    return this.getTenantCollection("flame_trivia").pipe(
      switchMap((colRef) => {
        const _query = query(
          colRef,
          where("user_id", "==", userId),
          where("topic", "==", topic),
          orderBy("created_at", "asc"),
        );
        return from(getDocs(_query)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  getFlameTriviaBySession(sessionId: string): Observable<any[]> {
    return this.getTenantCollection("flame_trivia").pipe(
      switchMap((colRef) => {
        const _query = query(colRef, where("session_id", "==", sessionId));
        return from(getDocs(_query)).pipe(
          map((snap) => {
            const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            return docs.sort((a: any, b: any) => {
              const dateA = a.created_at || "";
              const dateB = b.created_at || "";
              return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
            });
          }),
        );
      }),
    );
  }

  getFlameQuestions(triviaId: string): Observable<any[]> {
    return this.getTenantCollection(
      `flame_trivia/${triviaId}/flame_questions`,
    ).pipe(
      switchMap((colRef) => {
        return from(getDocs(colRef)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  updateFlameQuestion(
    triviaId: string,
    questionId: string,
    data: Record<string, any>,
  ): Observable<void> {
    return this.getTenantCollection(
      `flame_trivia/${triviaId}/flame_questions`,
    ).pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, questionId);
        return from(updateDoc(docRef, data));
      }),
    );
  }

  getFlameContentReports(questionId: string): Observable<any[]> {
    return this.getTenantCollection("flame_content_reports").pipe(
      switchMap((colRef) => {
        const q = query(colRef, where("questionId", "==", questionId));
        return from(getDocs(q)).pipe(
          map((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        );
      }),
    );
  }

  updateFlameContentReport(
    reportId: string,
    data: Record<string, any>,
  ): Observable<void> {
    return this.getTenantCollection("flame_content_reports").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, reportId);
        return from(updateDoc(docRef, data));
      }),
    );
  }

  deleteFlameTrivia(triviaId: string): Observable<void> {
    return from(this.tenantService.getMyDb()).pipe(
      switchMap((db) => {
        const parentRef = doc(db, `flame_trivia/${triviaId}`);
        const subColRef = collection(parentRef, "flame_questions");
        return from(getDocs(subColRef)).pipe(
          switchMap((snap) => {
            const batch = writeBatch(db);
            snap.docs.forEach((d) => batch.delete(d.ref));
            batch.delete(parentRef);
            return from(batch.commit());
          }),
        );
      }),
    );
  }

  GetMMInventoryDataByDocId(docId: string): Observable<MM_InventoryModel> {
    return this.getTenantDoc(`mm_inventory/${docId}`).pipe(
      switchMap(
        (docRef) =>
          docData(docRef, { idField: "id" }) as Observable<MM_InventoryModel>,
      ),
    );
  }

  getCountryExternalSystemDataStatus(countryId: string): Observable<boolean> {
    if (!countryId) return of(false);

    const path = `sync_asset_master/${countryId}/sap_assets`;

    return this.getTenantCollection(path).pipe(
      switchMap((colRef) => {
        const _query = query(colRef, limit(1));
        return from(getDocs(_query)).pipe(
          map((snap) => !snap.empty),
          catchError(() => of(false)),
        );
      }),
    );
  }

  addNewPerformanceData(inputData: any): Observable<void> {
    return this.getTenantCollection("performance_high_low_lights").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, inputData?.id);
        return from(setDoc(docRef, inputData));
      }),
      map(() => void 0),
      catchError((err) => throwError(() => err)),
    );
  }
  deletePerformanceData(id: string): Observable<any> {
    return this.getTenantCollection("performance_high_low_lights").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, id);
        // Get existing doc data
        return from(getDoc(docRef)).pipe(
          switchMap((snapshot) => {
            if (!snapshot.exists()) {
              throw new Error(`Document with ID ${id} does not exist`);
            }

            const existingData = snapshot?.data();

            // Delete doc
            return from(deleteDoc(docRef)).pipe(
              map(() => {
                return existingData;
              }),
            );
          }),
        );
      }),
      catchError((err) => {
        console.error("Error in deletePerformanceData:", err);
        return throwError(() => err);
      }),
    );
  }
  retrievePerformanceData(
    performanceDt: string,
    role: string,
    isEditorAccess: boolean,
  ) {
    return this.getTenantCollection("performance_high_low_lights").pipe(
      switchMap((sitesRef) => {
        const sitesQuery = query(sitesRef, where("id", "==", performanceDt));

        return new Observable<PerformanceData[]>((observer) => {
          const unsubscribe = onSnapshot(
            sitesQuery,
            (querySnapshot) => {
              const data: PerformanceData[] = querySnapshot.docs.map((doc) => ({
                id: doc?.id,
                ...(doc?.data() as Omit<PerformanceData, "id">),
              }));

              let filteredData: PerformanceData[];

              if (isEditorAccess) {
                filteredData =
                  role === "Expert"
                    ? data
                    : data.filter(
                        (item) =>
                          item.status === "Draft" ||
                          item.status === "Rejected" ||
                          item.status === "Pending for approval" ||
                          item.status === "published",
                      );
              } else {
                filteredData =
                  role === "Expert"
                    ? data
                    : data.filter(
                        (item) =>
                          item.status === "Draft" ||
                          item.status === "Pending for approval" ||
                          item.status === "published" ||
                          item.status === "Rejected",
                      );
              }
              observer.next(filteredData);
            },
            (error) => {
              observer.error(error);
            },
          );

          // cleanup when unsubscribed
          return () => unsubscribe();
        });
      }),
    );
  }
  updatePerformanceLowHighLightsStatus(
    id: string,
    status: string,
  ): Observable<any> {
    return this.getTenantDoc(`performance_high_low_lights/${id}`).pipe(
      switchMap((docRef) =>
        from(
          updateDoc(docRef, {
            status,
            updatedAt: new Date().toISOString(),
          }),
        ).pipe(switchMap(() => docData(docRef, { idField: "id" }))),
      ),
    );
  }
  getMMControlInventoryDataForMMControlSheet(subAssetNames: string[]) {
    return this.getTenantCollection("mm_control_inventory").pipe(
      switchMap((colRef: CollectionReference) => {
        return collectionData(colRef, { idField: "id" }).pipe(
          map((docs) =>
            (docs as MM_ControlInventoryModel[]).filter((doc) =>
              (doc.subAsset as unknown as controlSubAssetItem[])?.some((sub) =>
                subAssetNames?.includes(sub.materialType),
              ),
            ),
          ),
        );
      }),
    );
  }

  activateSAPAssets(
    assetId: string,
    countryUniqueId: string,
  ): Observable<void> {
    if (!assetId || !countryUniqueId) {
      return of();
    }

    const docPath = `sync_asset_master/${countryUniqueId}/sap_assets/${assetId}`;

    return this.getTenantDoc(docPath).pipe(
      switchMap((docRef) => from(updateDoc(docRef, { inUse: true }))),
    );
  }

  getEiUserInfo(email: string, role?: string): Observable<EIUserModel[]> {
    if (!email) return of([]);

    return this.getTenantCollection("ei_users").pipe(
      switchMap((usersRef) => {
        const q = role
          ? query(
              usersRef,
              where("email", "==", email),
              where("role", "==", role),
            )
          : query(usersRef, where("email", "==", email));

        return from(getDocs(q));
      }),
      map((snapshot) =>
        snapshot.docs.map((doc) => {
          const data = doc?.data() as EIUserModel;
          return {
            ...data,
            id: doc?.id,
          };
        }),
      ),
    );
  }

  getEIUserReviewApproveCount(email: string, status: string) {
    if (status == "Submitted to Reviewer") {
      return this.getTenantCollection("energy_isolation_procedure").pipe(
        switchMap((usersRef) => {
          const q = query(
            usersRef,
            where("status", "==", status),
            where("reviewerInfo.reviewerEmail", "==", email),
          );

          return from(getCountFromServer(q)).pipe(
            map((snapshot) => snapshot?.data()?.count),
          );
        }),
      );
    } else {
      return this.getTenantCollection("energy_isolation_procedure").pipe(
        switchMap((usersRef) => {
          const q = query(
            usersRef,
            where("status", "==", status),
            where("approverInfo.approverEmail", "==", email),
          );

          return from(getCountFromServer(q)).pipe(
            map((snapshot) => snapshot?.data()?.count),
          );
        }),
      );
    }
  }
  getEiProcedureDetails(
    id: string,
  ): Observable<EnergyIsolationProcedureModel | null> {
    if (!id) return of(null);
    return this.getTenantDoc(`energy_isolation_procedure/${id}`).pipe(
      switchMap((docRef) =>
        docData(docRef, { idField: "id" }).pipe(
          map((data) =>
            data ? (data as EnergyIsolationProcedureModel) : null,
          ),
        ),
      ),
    );
  }
  getApproveReviewProcedure(
    email: string,
    status: string,
  ): Observable<EnergyIsolationProcedureModel[]> {
    return this.getTenantCollection("energy_isolation_procedure").pipe(
      switchMap((procedureRef) => {
        const q =
          status === "Submitted to Reviewer"
            ? query(
                procedureRef,
                where("status", "==", status),
                where("reviewerInfo.reviewerEmail", "==", email),
              )
            : query(
                procedureRef,
                where("status", "==", status),
                where("approverInfo.approverEmail", "==", email),
              );

        return from(getDocs(q));
      }),

      map((snapshot) =>
        snapshot.docs.map((doc) => ({
          ...(doc?.data() as unknown as EnergyIsolationProcedureModel),
          id: doc?.id,
        })),
      ),
    );
  }

  getSiteInformation(siteLocationId: string): Observable<SiteDownloadModel[]> {
    if (!siteLocationId) return of([]);

    const guidanceSubtype$ = this.getTenantCollection("guidance").pipe(
      switchMap(
        (ref) =>
          collectionData(ref, { idField: "id" }) as Observable<GuidanceItem[]>,
      ),
    );

    const defectType$ = this.getTenantCollection("defect_type_master").pipe(
      switchMap(
        (ref) =>
          collectionData(ref, { idField: "id" }) as Observable<DefectType[]>,
      ),
    );

    return this.getTenantCollection("sites").pipe(
      switchMap(
        (sitesRef) =>
          collectionData(
            query(
              sitesRef,
              where("isActive", "==", true),
              where("locations.site.unique_id", "==", siteLocationId),
            ),
            { idField: "id" },
          ) as Observable<SiteModel[]>,
      ),

      switchMap((sites) => {
        if (!sites.length) return of([]);

        const sites$ = sites.map((site) => {
          const assets$ = this.getTenantCollection(
            `sites/${site.id}/assets`,
          ).pipe(
            switchMap(
              (ref) =>
                collectionData(ref, { idField: "id" }) as Observable<
                  AssetModel[]
                >,
            ),
          );

          const assessments$ = this.getTenantCollection("assessments").pipe(
            switchMap(
              (assessmentRef) =>
                collectionData(
                  query(assessmentRef, where("siteId", "==", site.id)),
                  { idField: "id" },
                ) as Observable<AssessmentModel[]>,
            ),

            switchMap((assessments) => {
              if (!assessments.length) {
                return of([]);
              }

              const assessmentDetails$ = assessments.map((assessment) => {
                const findings$ = this.getTenantCollection(
                  `assessments/${assessment.id}/finding`,
                ).pipe(
                  switchMap(
                    (ref) =>
                      collectionData(
                        query(ref, where("assessmentId", "==", assessment.id)),
                        { idField: "id" },
                      ) as Observable<FindingModel[]>,
                  ),
                );

                const actions$ = this.getTenantCollection(
                  `assessments/${assessment.id}/action`,
                ).pipe(
                  switchMap(
                    (ref) =>
                      collectionData(
                        query(ref, where("assessmentId", "==", assessment.id)),
                        { idField: "id" },
                      ) as Observable<ActionModel[]>,
                  ),
                );

                return combineLatest([findings$, actions$]).pipe(
                  map(([findings, actions]) => ({
                    assessment,
                    findings,
                    actions,
                  })),
                );
              });

              return combineLatest(assessmentDetails$);
            }),
          );

          return combineLatest([assets$, assessments$]).pipe(
            map(([assets, assessments]) => ({
              site: {
                ...site,
                assets,
              },
              assessments,
            })),
          );
        });

        return combineLatest(sites$);
      }),

      switchMap((siteBasedData) =>
        combineLatest([of(siteBasedData), guidanceSubtype$, defectType$]).pipe(
          map(([sites, guidanceSubtype, defectType]) =>
            sites.map((siteData) => ({
              ...siteData,
              guidanceSubtype,
              defectType,
            })),
          ),
        ),
      ),
    );
  }

  getMMLinkedAssetsMaterials(
    siteId: string,
    assetUID: string,
  ): Observable<MM_InventoryModel[]> {
    return this.getTenantCollection("mm_inventory").pipe(
      switchMap((colRef: CollectionReference) => {
        const q = query(colRef, where("siteId", "==", siteId));
        return collectionData(q, { idField: "id" }).pipe(
          map((docs) => {
            return (docs as MM_InventoryModel[]).filter((doc) =>
              doc?.assetLink?.assets?.some((asset) => asset?.uid === assetUID),
            );
          }),
        );
      }),
    );
  }

  GetMMInventoryMasterData(): Observable<MMInventoryMasterModel[]> {
    return this.getTenantCollection("mm_inventory_master").pipe(
      switchMap(
        (colRef) =>
          collectionData(colRef, { idField: "id" }) as Observable<
            MMInventoryMasterModel[]
          >,
      ),
      map((data) =>
        data.filter((item) => item.usage?.toLowerCase() === "active"),
      ),
    );
  }

  GetMMInventoryStateUnitsData(): Observable<MM_StateInfo[]> {
    return this.getTenantCollection("input_help").pipe(
      switchMap((colRef: CollectionReference) => {
        const q = query(colRef, where("type", "==", "state_unit"));
        return collectionData(q, { idField: "id" }) as Observable<
          MM_StateInfo[]
        >;
      }),
    );
  }

  GetMMInventoryOCR_ExtractedData(
    fileId: string,
  ): Observable<OCRExtractedDataModel | null> {
    return this.getTenantCollection("mm_inventory_ocr_extracted_data").pipe(
      switchMap((colRef: CollectionReference) => {
        const q = query(colRef, where("fileId", "==", fileId));

        return collectionData(q, { idField: "id" }).pipe(
          map((data: OCRExtractedDataModel[]) =>
            data?.length ? data[0] : null,
          ),
        );
      }),
    );
  }

  GetMMInventoryOCRPropertyMappingData(): Observable<
    MM_OCRPropertiesLevelMapping[]
  > {
    return this.getTenantCollection("mm_inventory_ocr_property_map").pipe(
      switchMap(
        (colRef) =>
          collectionData(colRef, { idField: "id" }) as Observable<
            MM_OCRPropertiesLevelMapping[]
          >,
      ),
    );
  }

  // Add a value to not_mapped_data collection
  MM_OCR_AddNotMappedData(propertyId: string, value: string): Observable<void> {
    return this.getTenantCollection("mm_inventory_ocr_not_mapped_data").pipe(
      switchMap(async (colRef) => {
        // 1️⃣ Check if a document already exists for this propertyId
        const q = query(colRef, where("propertyId", "==", propertyId));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          // 2️⃣ Document exists → update array
          const docRef = snapshot?.docs[0]?.ref;
          await updateDoc(docRef, {
            values: arrayUnion(value), // add to array, avoid duplicates
            updatedAt: new Date(),
          });
        } else {
          // 3️⃣ Document does not exist → create new
          await addDoc(colRef, {
            propertyId,
            values: [value],
            createdAt: new Date(),
          });
        }

        return void 0;
      }),
    );
  }
  GetMMInventoryOCRNotMappedData(): Observable<ocrNotMappedDataModel[]> {
    return this.getTenantCollection("mm_inventory_ocr_not_mapped_data").pipe(
      switchMap(
        (colRef) =>
          collectionData(colRef, { idField: "id" }) as Observable<
            ocrNotMappedDataModel[]
          >,
      ),
    );
  }

  GetMMHazardHealthData(
    state: string,
  ): Observable<MM_Hazard_HealthModel[] | null> {
    return this.getTenantCollection("mm_hazard_health").pipe(
      switchMap((colRef: CollectionReference<DocumentData>) => {
        const q = query(colRef, where("state", "==", state));

        return collectionData(q, { idField: "id" }).pipe(
          map((data: DocumentData[]) => {
            // Cast each document to MM_Hazard_HealthModel
            const typedData = data as MM_Hazard_HealthModel[];
            return typedData.length ? typedData : null;
          }),
        );
      }),
    );
  }
  getAllDefectTypeNames(offline: boolean = false): Observable<string[]> {
    if (offline) {
      return from(this.storageService.get("guidanceInformation")).pipe(
        map((guidance) => {
          const defectTypes = guidance?.defectType ?? [];

          return defectTypes.map((defect: DefectType) => defect.defectTypeName);
        }),
      );
    }
    return this.getTenantCollection("defect_type_master").pipe(
      switchMap(
        (defectRef) =>
          collectionData(defectRef, { idField: "id" }) as Observable<
            DefectType[]
          >,
      ),

      map((defects) => defects.map((defect) => defect.defectTypeName) || []),
    );
  }
  getPerformanceEmails(): Observable<any[]> {
    return this.getTenantCollection("performance_report_email").pipe(
      switchMap((collectionRef) =>
        collectionData(collectionRef, { idField: "id" }),
      ),
    );
  }

  /**
   * Replaces the admin-managed recipient entries of one location key in
   * performance_report_email. Runs in a transaction so self-subscribed entries
   * (subscribed_user === true) added concurrently by the subscription function
   * are re-read and kept. Only this one field path is written — the doc holds
   * every location and is too large to rewrite wholesale.
   */
  async savePerformanceReportRecipients(
    docId: string,
    locationKey: string,
    entries: ReportRecipientEntry[],
  ): Promise<void> {
    const db = await this.tenantService.getMyDb();
    const docRef = doc(db, "performance_report_email", docId);
    // FieldPath (not a dotted string) so keys like "AUS & NZ" or "EUR-C&E" are
    // treated as a single field name.
    const field = new FieldPath(locationKey);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      if (!snap.exists()) throw new Error(`performance_report_email/${docId} not found`);
      const current = snap.get(field);
      const subscribed = (Array.isArray(current) ? current : []).filter(
        (e: ReportRecipientEntry) => e?.subscribed_user === true,
      );
      tx.update(docRef, field, [...entries, ...subscribed]);
    });
  }

  updatePerformanceStatus(
    id: string,
    updatePayload: Partial<any>,
  ): Observable<void> {
    return this.getTenantCollection("performance_high_low_lights").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, id);
        return from(updateDoc(docRef, updatePayload));
      }),
      map(() => {
        return void 0;
      }),
      catchError((err) => {
        console.error("Firestore UPDATE FAILED", err);
        return throwError(() => err);
      }),
    );
  }

  // Scoped to one target month — the collection grows with every email sent,
  // and the report screen only ever needs the selected month's entries.
  getEmailHistory(targetMonth: string): Observable<EmailHistoryModel[]> {
    return this.getTenantCollection("performance_email_histry").pipe(
      switchMap((colRef) =>
        collectionData(query(colRef, where("targetMonth", "==", targetMonth)), {
          idField: "id",
        }),
      ),
      map((history) => history as EmailHistoryModel[]),
    );
  }

  listPerformanceReports(
    path: string,
  ): Observable<{ name: string; url: string }[]> {
    const storage = this.getPerformanceStorage();
    const listRef = ref(storage, path);
    return from(listAll(listRef)).pipe(
      switchMap((res) => {
        if (res.items.length === 0) return of([]);
        const promises = res.items.map(async (item) => {
          const url = await getDownloadURL(item);
          return { name: item.name, url };
        });
        return from(Promise.all(promises));
      }),
      catchError((err: any) => {
        console.error("DEBUG: listPerformanceReports failed", err);
        return of([]);
      }),
    );
  }

  //  CODE FOR FETCHING PERFORMANCE REPORT
  private getPerformanceStorage() {
    return getStorage(this.storage.app, `gs://${environment.bucket}`);
  }

  uploadToPerformanceBucket(path: string, file: File): Observable<string> {
    const storage = this.getPerformanceStorage();
    const storageRef = ref(storage, `${path}/${file.name}`);
    return from(uploadBytes(storageRef, file)).pipe(
      switchMap((snapshot) => from(getDownloadURL(snapshot.ref))),
      catchError((err: any) => {
        console.error("DEBUG: uploadToPerformanceBucket failed", err);
        return throwError(() => err);
      }),
    );
  }
  saveEmailHistory(
    historyData: EmailHistoryPayload,
    sentDate: Date,
  ): Observable<void> {
    const payload = {
      ...historyData,
      sentDate,
    };

    // One doc per month/tab/location (was addDoc → a new doc on every call,
    // which grew the collection by thousands of duplicates per month).
    // "/" is not allowed in doc IDs, so it's replaced.
    const docId = [historyData.targetMonth, historyData.region, historyData.location]
      .map((part) => String(part ?? "").toLowerCase().replace(/\//g, "_"))
      .join("__");

    return this.getTenantCollection("performance_email_histry").pipe(
      switchMap((colRef) => from(setDoc(doc(colRef, docId), payload))),
      map(() => void 0),
      catchError((err) => throwError(() => err)),
    );
  }

  getPerformanceHighLowLights(reportMonthYear: string): Observable<any> {
    return this.getTenantDoc(
      `performance_high_low_lights/${reportMonthYear}`,
    ).pipe(switchMap((docRef) => docData(docRef, { idField: "id" })));
  }

  getPerformanceTabCompletions(
    month: string,
  ): Observable<Record<
    string,
    { email: string; name: string; submitted_at: string }
  > | null> {
    return this.getTenantDoc(`performance_tab_submissions/${month}`).pipe(
      switchMap((docRef) => docData(docRef)),
      map(
        (data) =>
          (data as Record<
            string,
            { email: string; name: string; submitted_at: string }
          >) ?? null,
      ),
    );
  }

  deletePerformanceTabCompletion(
    month: string,
    tabKey: string,
  ): Observable<void> {
    return this.getTenantDoc(`performance_tab_submissions/${month}`).pipe(
      switchMap((docRef) =>
        from(updateDoc(docRef, { [tabKey]: deleteField() })),
      ),
    );
  }

  checkReportsByMonth(month: string): Observable<Record<string, boolean>> {
    return from(this.tenantService.getUserStorage(environment.bucket)).pipe(
      switchMap((storage) => from(listAll(ref(storage, month)))),
      switchMap((monthResult) => {
        const adminPromises = monthResult.prefixes.map(async (adminRef) => {
          const adminContent = await listAll(adminRef);
          if (adminRef.name?.toUpperCase() === "GROUP") {
            if (adminContent.items.some((item) => item?.name?.endsWith(".pdf")))
              return { [adminRef.name]: true };
            for (const subRef of adminContent.prefixes) {
              const subContent = await listAll(subRef);
              if (subContent.items.some((item) => item?.name?.endsWith(".pdf")))
                return { [adminRef.name]: true };
            }
            return { [adminRef.name]: false };
          }
          return {
            [adminRef.name]: adminContent.items.some((item) =>
              item?.name?.endsWith(".pdf"),
            ),
          };
        });
        return from(Promise.all(adminPromises));
      }),
      map((results) => {
        const output: Record<string, boolean> = {};
        results.forEach((obj) => Object.assign(output, obj));
        return output;
      }),
    );
  }

  async getDownloadUrlForLocation(
    month: string,
    location: string,
  ): Promise<string> {
    const storage = await this.tenantService.getUserStorage(environment.bucket);
    const locKey = location.toUpperCase();
    if (locKey === "GROUP") {
      const groupContent = await listAll(ref(storage, `${month}/GROUP`));
      const directPdf = groupContent.items.find((item) =>
        item?.name?.endsWith(".pdf"),
      );
      if (directPdf) return getDownloadURL(directPdf);
      for (const subRef of groupContent.prefixes) {
        const subContent = await listAll(subRef);
        const subPdf = subContent.items.find((item) =>
          item?.name?.endsWith(".pdf"),
        );
        if (subPdf) return getDownloadURL(subPdf);
      }
      throw new Error("No PDF found for GROUP");
    }
    const locContent = await listAll(ref(storage, `${month}/${locKey}`));
    const pdfRef = locContent.items.find((item) =>
      item?.name?.endsWith(".pdf"),
    );
    if (!pdfRef) throw new Error(`No PDF found for ${locKey}`);
    return getDownloadURL(pdfRef);
  }

  getReportsByMonth(month: string): Observable<Record<string, string>> {
    return from(this.tenantService.getUserStorage(environment.bucket)).pipe(
      // Go directly to month folder
      switchMap((storage) => from(listAll(ref(storage, month)))),

      // Loop Admin_Unit folders
      switchMap((monthResult) => {
        const adminPromises = monthResult.prefixes.map(async (adminRef) => {
          const adminContent = await listAll(adminRef);
          if (adminRef.name?.toUpperCase() === "GROUP") {
            const directPdf = adminContent?.items?.find((item) =>
              item?.name?.endsWith?.(".pdf"),
            );
            if (directPdf) {
              const url = await getDownloadURL(directPdf);
              return { [adminRef.name]: url };
            }
            for (const subRef of adminContent.prefixes) {
              const subContent = await listAll(subRef);
              const subPdf = subContent?.items?.find((item) =>
                item?.name?.endsWith?.(".pdf"),
              );
              if (subPdf) {
                const url = await getDownloadURL(subPdf);
                return { [adminRef.name]: url };
              }
            }
            return {};
          }

          const pdfRef = adminContent?.items?.find((item) =>
            item?.name?.endsWith?.(".pdf"),
          );

          if (!pdfRef) return {};

          const url = await getDownloadURL(pdfRef);

          return { [adminRef.name]: url };
        });

        return from(Promise.all(adminPromises));
      }),

      // Merge into single object
      map((results) => {
        const output: Record<string, string> = {};
        results?.forEach((obj) => Object.assign(output, obj));
        return output;
      }),
    );
  }

  async getContentImageUrls(): Promise<{
    likeImg: string;
    dislikeImg: string;
    predictAuPng: string;
    fedFlag: string;
    bogIntelligence: string;
    appleLogo: string;
    androidLogo: string;
    qrIos: string;
    qrAndroid: string;
  }> {
    const storage = getStorage(this.storage.app);
    const paths = {
      likeImg: "performance-report-email-assets/like.png",
      dislikeImg: "performance-report-email-assets/dislike.png",
      predictAuPng: "performance-report-email-assets/predict_au.png",
      fedFlag: "performance-report-email-assets/flag-img .png",
      bogIntelligence: "performance-report-email-assets/bog_intelligence.PNG",
      appleLogo: "performance-report-email-assets/apple-logo-3.png",
      androidLogo: "performance-report-email-assets/android-logo-3.png",
      qrIos: "performance-report-email-assets/qr_bog_ios.png",
      qrAndroid: "performance-report-email-assets/qr_bog_android.png",
    };
    const entries = await Promise.all(
      Object.entries(paths).map(async ([key, path]) => {
        const url = await getDownloadURL(ref(storage, path));
        return [key, url] as [string, string];
      }),
    );
    return Object.fromEntries(entries) as {
      likeImg: string;
      dislikeImg: string;
      predictAuPng: string;
      fedFlag: string;
      bogIntelligence: string;
      appleLogo: string;
      androidLogo: string;
      qrIos: string;
      qrAndroid: string;
    };
  }

  getFilesForLocation(
    month: string,
    location: string,
  ): Observable<{ name: string; url: string }[]> {
    const locKey = location.toUpperCase();
    return from(this.tenantService.getUserStorage(environment.bucket)).pipe(
      switchMap(async (storage) => {
        const locRef = ref(storage, `${month}/${locKey}`);
        const content = await listAll(locRef);
        const files: { name: string; url: string }[] = [];
        for (const item of content.items) {
          files.push({ name: item?.name, url: await getDownloadURL(item) });
        }
        if (locKey === "GROUP") {
          // Exclude NON_SHIELD/ — those PDFs are a separate-audience variant of the
          // report, not a regular attachment option (matches the backend's
          // getAllFilesInFolder, which excludes this subfolder from its default listing).
          for (const subRef of content.prefixes) {
            if (subRef.name === "NON_SHIELD") continue;
            const subContent = await listAll(subRef);
            for (const item of subContent.items) {
              files.push({ name: item?.name, url: await getDownloadURL(item) });
            }
          }
        }
        return files;
      }),
      catchError((err) => {
        console.error("getFilesForLocation failed", err);
        return of([] as { name: string; url: string }[]);
      }),
    );
  }

  listTop5Files(
    month: string,
  ): Observable<{ name: string; url: string; fullPath: string }[]> {
    return from(this.tenantService.getUserStorage(environment.bucket)).pipe(
      switchMap(async (storage) => {
        const folderRef = ref(storage, `TOP_5/${month}`);
        const content = await listAll(folderRef);
        const pdfs = content.items.filter((item) =>
          item.name.toLowerCase().endsWith(".pdf"),
        );
        return Promise.all(
          pdfs.map(async (item) => ({
            name: item.name,
            url: await getDownloadURL(item),
            fullPath: item.fullPath,
          })),
        );
      }),
      catchError((err) => {
        console.error("listTop5Files failed", err);
        return of([] as { name: string; url: string; fullPath: string }[]);
      }),
    );
  }

  uploadTop5File(
    file: File,
    month: string,
  ): Observable<{ name: string; url: string; fullPath: string }> {
    return from(this.tenantService.getUserStorage(environment.bucket)).pipe(
      switchMap(async (storage) => {
        const fileRef = ref(storage, `TOP_5/${month}/${file.name}`);
        const snapshot = await uploadBytes(fileRef, file);
        const url = await getDownloadURL(snapshot.ref);
        return { name: file.name, url, fullPath: snapshot.ref.fullPath };
      }),
    );
  }

  deleteTop5File(fullPath: string): Observable<void> {
    return from(this.tenantService.getUserStorage(environment.bucket)).pipe(
      switchMap((storage) => {
        const fileRef = ref(storage, fullPath);
        return from(deleteObject(fileRef));
      }),
    );
  }

  getAllReportFilesByMonth(
    month: string,
  ): Observable<Record<string, { name: string; url: string }[]>> {
    return from(this.tenantService.getUserStorage(environment.bucket)).pipe(
      switchMap((storage) => from(listAll(ref(storage, month)))),
      switchMap((monthResult) => {
        const adminPromises = monthResult.prefixes.map(async (adminRef) => {
          const adminContent = await listAll(adminRef);
          if (adminRef.name?.toUpperCase() === "GROUP") {
            const allFiles: { name: string; url: string }[] = [];
            for (const item of adminContent.items) {
              allFiles.push({
                name: item.name,
                url: await getDownloadURL(item),
              });
            }
            for (const subRef of adminContent.prefixes) {
              const subContent = await listAll(subRef);
              for (const item of subContent.items) {
                allFiles.push({
                  name: item.name,
                  url: await getDownloadURL(item),
                });
              }
            }
            if (!allFiles.length)
              return {} as Record<string, { name: string; url: string }[]>;
            return { [adminRef.name]: allFiles };
          }

          if (!adminContent.items.length)
            return {} as Record<string, { name: string; url: string }[]>;
          const filePromises = adminContent.items.map(async (item) => ({
            name: item.name,
            url: await getDownloadURL(item),
          }));
          const files = await Promise.all(filePromises);
          return { [adminRef.name]: files };
        });
        return from(Promise.all(adminPromises));
      }),
      map((results) => {
        const output: Record<string, { name: string; url: string }[]> = {};
        results.forEach((obj) => Object.assign(output, obj));
        return output;
      }),
      catchError((err) => {
        console.error("getAllReportFilesByMonth failed", err);
        return of({} as Record<string, { name: string; url: string }[]>);
      }),
    );
  }

  acquireMMLock(
    controlId: string,
    user: { id: string; name: string },
  ): Observable<boolean> {
    const lockDocRefPath = `mm_control_locks/${controlId}`;

    return this.getTenantDoc(lockDocRefPath).pipe(
      switchMap((ref) => getDoc(ref)),
      switchMap((lockSnap) => {
        const now = Date.now();
        const LOCK_TIMEOUT_MS = 1000 * 60 * 5;

        if (lockSnap.exists()) {
          const lockData = lockSnap.data() as MMLockModel;

          // Safe way to get timestamp - check if it's a Timestamp first
          let lockTime = 0;
          if (lockData.timestamp instanceof Timestamp) {
            lockTime = lockData.timestamp.toMillis();
          }

          const isExpired = now - lockTime > LOCK_TIMEOUT_MS;

          if (lockData.userId !== user.id && !isExpired) {
            return of(false);
          }
        }

        // When writing, we use serverTimestamp() which is FieldValue
        const newLock: MMLockModel = {
          userId: user.id,
          userName: user.name,
          timestamp: serverTimestamp(), // This is FieldValue
          controlId: controlId,
        };

        return this.getTenantDoc(lockDocRefPath).pipe(
          switchMap((ref) => from(setDoc(ref, newLock))),
          map(() => true),
          catchError((err) => {
            return of(false);
          }),
        );
      }),
      catchError((err) => {
        return of(false);
      }),
    );
  }

  releaseMMLock(controlId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getTenantDoc(`mm_control_locks/${controlId}`).subscribe({
        next: (ref) => {
          deleteDoc(ref).then(resolve).catch(reject);
        },
        error: reject,
      });
    });
  }

  listenToMMLock(controlId: string): Observable<MMLockModel | null> {
    return this.getTenantDoc(`mm_control_locks/${controlId}`).pipe(
      switchMap((ref) => docData(ref)),
      map((data) => (data ? (data as MMLockModel) : null)),
    );
  }

  refreshMMLock(
    controlId: string,
    user: { id: string; name: string },
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getTenantDoc(`mm_control_locks/${controlId}`).subscribe({
        next: (ref) => {
          updateDoc(ref, {
            timestamp: serverTimestamp(),
            userId: user.id,
            userName: user.name,
          })
            .then(resolve)
            .catch(reject);
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  checkMMLock(controlId: string): Observable<MMLockModel | null> {
    const docRef = this.getTenantDoc(`mm_control_locks/${controlId}`);
    return from(docRef).pipe(
      switchMap((ref) => docData(ref)),
      take(1),
      map((data) => (data ? (data as MMLockModel) : null)),
    );
  }

  // ============================================
  // MM REGISTRATION EDIT LOCK
  // ============================================
  acquireMMRegistrationLock(
    materialId: string,
    user: { id: string; name: string },
  ): Observable<boolean> {
    const lockDocRefPath = `mm_registration_locks/${materialId}`;

    return this.getTenantDoc(lockDocRefPath).pipe(
      switchMap((ref) => getDoc(ref)),
      switchMap((lockSnap) => {
        const now = Date.now();
        const LOCK_TIMEOUT_MS = 1000 * 60 * 2; // 2 minutes

        if (lockSnap.exists()) {
          const lockData = lockSnap.data() as MMLockModel;

          let lockTime = 0;
          if (lockData.timestamp instanceof Timestamp) {
            lockTime = lockData.timestamp.toMillis();
          }

          const isExpired = now - lockTime > LOCK_TIMEOUT_MS;

          if (lockData?.userId !== user?.id && !isExpired) {
            return of(false);
          }
        }

        const newLock: MMLockModel = {
          userId: user?.id,
          userName: user?.name,
          timestamp: serverTimestamp(),
          controlId: materialId, // Reusing controlId field as materialId
        };

        return this.getTenantDoc(lockDocRefPath).pipe(
          switchMap((ref) => from(setDoc(ref, newLock))),
          map(() => true),
          catchError(() => of(false)),
        );
      }),
      catchError(() => of(false)),
    );
  }

  releaseMMRegistrationLock(materialId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getTenantDoc(`mm_registration_locks/${materialId}`).subscribe({
        next: (ref) => {
          deleteDoc(ref).then(resolve).catch(reject);
        },
        error: reject,
      });
    });
  }

  listenToMMRegistrationLock(
    materialId: string,
  ): Observable<MMLockModel | null> {
    return this.getTenantDoc(`mm_registration_locks/${materialId}`).pipe(
      switchMap((ref) => docData(ref)),
      map((data) => (data ? (data as MMLockModel) : null)),
    );
  }

  refreshMMRegistrationLock(
    materialId: string,
    user: { id: string; name: string },
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getTenantDoc(`mm_registration_locks/${materialId}`).subscribe({
        next: (ref) => {
          updateDoc(ref, {
            timestamp: serverTimestamp(),
            userId: user.id,
            userName: user.name,
          })
            .then(resolve)
            .catch(reject);
        },
        error: reject,
      });
    });
  }

  checkMMRegistrationLock(materialId: string): Observable<MMLockModel | null> {
    const docRef = this.getTenantDoc(`mm_registration_locks/${materialId}`);
    return from(docRef).pipe(
      switchMap((ref) => docData(ref)),
      take(1),
      map((data) => (data ? (data as MMLockModel) : null)),
    );
  }

  getConnectionLocationMaster(
    countryId: string,
  ): Observable<ConnectionLocationMasterModel[]> {
    return this.getTenantCollection("connection_location_master").pipe(
      switchMap((colRef) => {
        const q = query(colRef, where("countryId", "==", countryId));
        return collectionData(q, { idField: "id" }) as Observable<
          ConnectionLocationMasterModel[]
        >;
      }),
    );
  }

  getMMInventoryDetailsByDocumentId(
    id: string,
    isExpert: boolean = false,
  ): Observable<MM_InventoryModel | null> {
    const collectionName = isExpert
      ? "mm_inventory_master_materials"
      : "mm_inventory";
    return this.getTenantCollection(collectionName).pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, id);

        return docData(docRef, {
          idField: "id",
        }) as Observable<MM_InventoryModel>;
      }),
    );
  }

  updateMMInventoryData(
    id: string,
    updatePayload: Partial<MM_InventoryModel>,
  ): Observable<void> {
    return this.getTenantCollection("mm_inventory").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, id);
        return from(updateDoc(docRef, updatePayload));
      }),
      map(() => void 0),
      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }
  // getHazardsInfo(): Observable<HazardsInfoDoc> {
  //   const hazardsRef$ = this.getTenantCollection("input_help");

  //   return hazardsRef$.pipe(
  //     switchMap(
  //       (ref) =>
  //         collectionData(query(ref, where("__name__", "==", "hazards_info")), {
  //           idField: "id",
  //         }) as Observable<HazardsInfoDoc[]>,
  //     ),
  //     map((data) => data[0]),
  //   );
  // }
  // firebase.service.ts

  getHazardsInfo(): Observable<HazardsInfoDoc> {
    const hazardsRef$ = this.getTenantCollection("mm_inventory_hazards");

    return hazardsRef$.pipe(
      switchMap(
        (ref) =>
          collectionData(ref, { idField: "id" }) as Observable<
            HazardsInventoryItem[]
          >,
      ),
      map((data: HazardsInventoryItem[]) => {
        const fireHazards = data?.filter((h) => h.hazardType === "fire") || [];
        const healthHazards =
          data?.filter((h) => h?.hazardType === "health") || [];
        const environmentHazards =
          data?.filter((h) => h?.hazardType === "environment") || [];

        return {
          fireHazards: fireHazards.map(
            (h: HazardsInventoryItem): HazardDisplayItemModel => ({
              title: h?.name,
              iconPath: h?.iconUrl,
            }),
          ),
          healthHazards: healthHazards.map(
            (h: HazardsInventoryItem): HazardDisplayItemModel => ({
              title: h?.name,
              iconPath: h?.iconUrl,
            }),
          ),
          environmentHazards: environmentHazards.map(
            (h: HazardsInventoryItem): HazardDisplayItemModel => ({
              title: h?.name,
              iconPath: h?.iconUrl,
            }),
          ),
        };
      }),
    );
  }
  getFileFromInputHelp(fileName: string): Observable<PdfInputHelpFile | null> {
    return this.getTenantCollection("input_help").pipe(
      switchMap((collectionRef) => {
        const q = query(collectionRef, where("fileName", "==", fileName));
        return collectionData(q, { idField: "id" }) as Observable<
          PdfInputHelpFile[]
        >;
      }),
      map((files: PdfInputHelpFile[]) => {
        if (files?.length === 0) return null;
        const file = files[0];
        return {
          fileName: file?.fileName,
          fileSrc: file?.fileSrc,
        };
      }),
    );
  }

  getMMReqUpdateDataByDocId(
    materialId: string,
  ): Observable<MM_RiskLevelRequestUpdateInfo_Model | null> {
    return this.getTenantCollection("mm_inventory_req_update").pipe(
      switchMap((ref) => {
        // ref is CollectionReference<DocumentData>
        const typedRef =
          ref as CollectionReference<MM_RiskLevelRequestUpdateInfo_Model>;

        const q = query(typedRef, where("materialId", "==", materialId));

        return collectionData(q, { idField: "id" }).pipe(
          map((docs) => (docs.length ? docs[0] : null)),
        );
      }),
    );
  }

  addUpdateMMInventoryReqUpdateData(
    data: Partial<MM_RiskLevelRequestUpdateInfo_Model>,
  ): Observable<void> {
    return this.getTenantCollection("mm_inventory_req_update").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, data.id);
        return from(setDoc(docRef, data));
      }),
      map(() => void 0),
      catchError((err) => throwError(() => err)),
    );
  }

  deleteMMInventoryReqUpdateData(id: string): Observable<void> {
    return this.getTenantCollection("mm_inventory_req_update").pipe(
      switchMap((colRef) => {
        const docRef = doc(colRef, id);
        return from(deleteDoc(docRef)).pipe(
          map(() => void 0),
          catchError((err) => throwError(() => err)),
        );
      }),
    );
  }

  GetCertifiedMMInventoryData(): Observable<MM_InventoryModel[]> {
    return this.getTenantCollection("mm_inventory").pipe(
      switchMap((colRef) => {
        const filters = query(
          colRef,
          where("isCertified", "==", true),
          where("isGlobalMaterial", "==", true),
          where("status", "in", ["Draft", "Published"]),
          orderBy("createdDate", "desc"),
        );

        return collectionData(filters, { idField: "id" }) as Observable<
          MM_InventoryModel[]
        >;
      }),
    );
  }

  CheckUniqueMaterialName(
    materialNameSlug: string,
    isMaster: boolean,
    siteId?: string,
    currentDocId?: string,
  ) {
    const collectionName = isMaster
      ? "mm_inventory_master_materials"
      : "mm_inventory";

    return this.getTenantCollection(collectionName).pipe(
      switchMap((colRef) => {
        let filters = query(
          colRef,
          where(
            "identification.materialDetails.materialNameSlug",
            "==",
            materialNameSlug,
          ),
          limit(1),
        );

        if (siteId && !isMaster) {
          filters = query(filters, where("siteId", "==", siteId));
        }
        return from(getDocs(filters));
      }),
      map((querySnap) => {
        if (querySnap.empty) return false;
        // ✅ Ignore current document and REJECTED materials
        const filteredDocs = querySnap?.docs.filter((doc) => {
          const data = doc.data() as MM_InventoryModel;
          const isCurrentDoc = doc?.id === currentDocId;
          const isRejected = data?.status === "Rejected";
          const isDraft = data?.status === "Draft";
          return !isCurrentDoc && !isRejected && !isDraft;
        });
        return filteredDocs?.length > 0;
      }),
    );
  }

  getMappedAssetsByTypeAndTitle(
    locationId: string,
    type: string,
    title: string,
  ): Observable<AssetModel[]> {
    if (!locationId || !type || !title) return of([]);

    return this.getTenantCollection(`sites/${locationId}/assets`).pipe(
      map((assetsRef) =>
        query(
          assetsRef,
          where("isActive", "==", true),
          where("assetInfo.assetType", "==", type),
        ),
      ),
      switchMap(
        (assetQuery) =>
          collectionData(assetQuery, {
            idField: "id",
          }) as Observable<AssetModel[]>,
      ),
      map((assets: AssetModel[]) =>
        assets.filter((asset) => {
          const sapId = asset?.assetInfo?.sapId?.trim();
          const assetCode = asset?.assetInfo?.assetCode?.trim();
          const hasValidSapId = sapId && sapId !== "" && sapId !== "--";

          const hasValidAssetCode =
            assetCode && assetCode !== "" && assetCode !== "--";
          return hasValidSapId || hasValidAssetCode;
        }),
      ),
    );
  }

  getEiRatingInfo(
    email: string,
    procedure_id: string,
    version: number,
  ): Observable<{
    ratingInfo: EIRatingUpdateModel | null;
  }> {
    return this.getTenantDoc(
      `ei_ratings/${email}_${procedure_id}_${version}`,
    ).pipe(
      switchMap((ei_ratingsDocRef) =>
        from(getDoc(ei_ratingsDocRef)).pipe(
          map((docSnapshot) => {
            if (docSnapshot.exists()) {
              return {
                ratingInfo: docSnapshot.data() as EIRatingUpdateModel,
              };
            } else {
              return {
                ratingInfo: null,
              };
            }
          }),
        ),
      ),
    );
  }

  // all EI ratings given by one user, keyed by doc id (`${email}_${procedure_id}_${version}`) —
  // used by the EI list export so "Your rating" can be filled with one query instead of a read per row
  getUserEiRatings(
    email: string,
  ): Observable<Map<string, EIRatingUpdateModel>> {
    if (!email) return of(new Map());

    return this.getTenantCollection("ei_ratings").pipe(
      switchMap((ratingsRef) =>
        from(getDocs(query(ratingsRef, where("user_email", "==", email)))),
      ),
      map(
        (snapshot) =>
          new Map(
            snapshot.docs.map((doc) => [
              doc.id,
              doc.data() as EIRatingUpdateModel,
            ]),
          ),
      ),
    );
  }

  getLiveNotificationCount(email: string): Observable<number> {
    return this.getTenantDoc(`notifications/${email}`).pipe(
      switchMap((notificationsDocRef) =>
        docData(notificationsDocRef).pipe(
          map((data) => {
            const notifications = data?.["send"] || [];
            let count = 0;
            for (let data of notifications) {
              if (!data.read) {
                count++;
              }
            }
            return count;
          }),
        ),
      ),
    );
  }
  getEIUserByEmail(email: string, role?: string): Observable<EIUserDataModel> {
    if (!email) {
      return of({ users: [], total: 0 });
    }
    return this.getTenantCollection("users").pipe(
      // 1. GET USER
      switchMap((usersRef) => from(getDoc(doc(usersRef, email)))),
      switchMap((userSnap) => {
        const userData = userSnap.data() as UserModel;
        if (!userData) {
          return of({ users: [], total: 0 });
        }
        // 2. COLLECT SITE IDS
        const plantIds: string[] =
          userData?.plantAssigned
            ?.map((plant) => plant?.unique_id)
            .filter((id): id is string => !!id) || [];
        const roleSiteIds: string[] =
          userData?.roles
            ?.filter(
              (roleObj) =>
                roleObj.role === "site_author" ||
                roleObj.role === "site_approver",
            )
            .map((roleObj) => roleObj.locationId)
            .filter((id): id is string => !!id) || [];
        const allSiteIds: string[] = [
          ...new Set([...plantIds, ...roleSiteIds]),
        ];
        // 3. GET SITES (USING YOUR EXISTING SERVICE)
        return this.getUserSites(allSiteIds).pipe(
          switchMap((sites: SiteModel[]) => {
            const siteMap = new Map<string, SiteModel>(
              sites.map((s: SiteModel) => [
                s?.locations?.site?.unique_id as string,
                s,
              ]),
            );
            let result: EIUserModel[] = [];
            // 4. SITE ADMIN
            userData?.plantAssigned?.forEach((plant: PlantAssignedModel) => {
              const site = siteMap.get(plant?.unique_id ?? "");

              if (site) {
                result.push({
                  role: "Site Admin",
                  region: site.locations?.region?.location,
                  country:
                    (site.locations?.opco as { name?: string })?.name ?? "",
                  site_name: site.locations?.site?.location,
                  email,
                });
              }
            });
            // 5. SITE AUTHOR / APPROVER
            userData?.roles?.forEach((roleObj: UserRolesModel) => {
              if (
                roleObj.role === "site_author" ||
                roleObj.role === "site_approver"
              ) {
                const site = siteMap.get(roleObj?.locationId ?? "");

                if (site) {
                  result.push({
                    role:
                      roleObj.role === "site_author"
                        ? "Site Author/Reviewer"
                        : "Site Approver",
                    region: site.locations?.region?.location,
                    country:
                      (site.locations?.opco as { name?: string })?.name ?? "",
                    site_name: site.locations?.site?.location,
                    email,
                  });
                }
              }
            });
            // 6. COUNTRY ADMIN (INLINE FIREBASE QUERIES)
            const opcoIds =
              userData?.countryAccessList?.map((country) => country.opco_id) ||
              [];

            if (!opcoIds.length) {
              return of({
                users: result,
                total: result.length,
              });
            }
            const uniqueSet = new Set<string>();
            const sitesRef = collection(this.firestore, "sites");
            const countryQueries = opcoIds.map((id: string) =>
              getDocs(
                query(sitesRef, where("locations.opco.id", "==", id), limit(1)),
              ),
            );
            return from(Promise.all(countryQueries)).pipe(
              map((snapshots) => {
                const countryAdmins: EIUserModel[] = [];
                snapshots.forEach((snap) => {
                  if (snap.empty) return;
                  const data = snap.docs[0].data() as SiteModel;
                  const region = data?.locations?.region?.location;
                  const opco = data?.locations?.opco as
                    | { name?: string }
                    | undefined;
                  const country = opco?.name ?? "";
                  const key = `${region}_${country}`;
                  if (uniqueSet.has(key)) return;
                  uniqueSet.add(key);
                  countryAdmins.push({
                    role: "Country Admin",
                    region,
                    country,
                    email,
                  });
                });
                const finalUsers = [...result, ...countryAdmins];
                const filteredFinalUsers = role
                  ? finalUsers.filter((user) => user.role === role)
                  : finalUsers;
                return {
                  users: filteredFinalUsers,
                  total: filteredFinalUsers.length,
                };
              }),
            );
          }),
        );
      }),
    );
  }

  // --- Flame Topic Suggester Session ---
  private topicSuggesterSessionId: string | null = null;

  setTopicSuggesterSessionId(sessionId: string) {
    this.topicSuggesterSessionId = sessionId;
  }

  getTopicSuggesterSessionId(): string | null {
    return this.topicSuggesterSessionId;
  }

  clearTopicSuggesterSessionId() {
    this.topicSuggesterSessionId = null;
  }

  //==================== Material Master Firebase Methods ====================
  getAllMaterialMasterRegistrations(): Observable<MaterialRegistrationItem[]> {
    return this.getTenantCollection("mm_inventory_master").pipe(
      switchMap((colRef) => {
        const q = query(colRef, orderBy("createdAt", "desc"));
        return collectionData(q, { idField: "id" }).pipe(
          map((data) =>
            (data as MaterialRegistrationItem[])?.map((item) => ({
              id: item?.id,
              type: item?.type,
              state: item?.state,
              usage: item?.usage,
              family: item?.family,
              subType: item?.subType,
              createdAt: item?.createdAt,
              hazardClass: item?.hazardClass,
              createdByEmail: item?.createdByEmail,
              reachCasReference: item?.reach_cas_reference,
              comments: item?.comments_use,
            })),
          ),
        );
      }),
    );
  }

  getMaterialMasterRegistrationById(
    id: string,
  ): Observable<MaterialRegistrationItem> {
    return this.getTenantDoc(`mm_inventory_master/${id}`).pipe(
      switchMap(
        (docRef) =>
          docData(docRef, {
            idField: "id",
          }) as Observable<MaterialRegistrationItem>,
      ),
    );
  }
  getAllBannedWastes(): Observable<BannedWasteItem[]> {
    return this.getTenantCollection("mm_inventory_banned_wastes").pipe(
      switchMap((colRef) => {
        const q = query(colRef, orderBy("createdAt", "desc"));

        return collectionData(q, { idField: "id" }).pipe(
          map((data) =>
            (data as BannedWasteItem[])?.map((item) => ({
              id: item?.id,
              title: item?.title,
              isPresent: item?.isPresent,
              isShowPopup: item?.isShowPopup,
              popupText: item?.popupText,
              isDisableSubmit: item?.isDisableSubmit,
              createdAt: item?.createdAt,
              createdByEmail: item?.createdByEmail,
            })),
          ),
        );
      }),
    );
  }

  getBannedWasteById(id: string): Observable<BannedWasteItem> {
    return this.getTenantDoc(`mm_inventory_banned_wastes/${id}`).pipe(
      switchMap(
        (docRef) =>
          docData(docRef, { idField: "id" }) as Observable<BannedWasteItem>,
      ),
    );
  }

  getHazardsDocument(): Observable<HazardsDocument> {
    // Assuming you have a single document with ID "master" in mm_hazards collection
    return this.getTenantDoc(`mm_hazards/master`).pipe(
      switchMap(
        (docRef) =>
          docData(docRef, { idField: "id" }) as Observable<HazardsDocument>,
      ),
    );
  }

  getAllMaterialControls(): Observable<MaterialControlConfigModel[]> {
    return this.getTenantCollection("mm_control_config").pipe(
      switchMap((colRef) => {
        const q = query(colRef);
        return collectionData(q, { idField: "id" }) as Observable<
          MaterialControlConfigModel[]
        >;
      }),
    );
  }

  getMaterialControlById(id: string): Observable<MaterialControlConfigModel> {
    return this.getTenantDoc(`mm_control_config/${id}`).pipe(
      switchMap(
        (docRef) =>
          docData(docRef, {
            idField: "id",
          }) as Observable<MaterialControlConfigModel>,
      ),
    );
  }
  getAllOCRLabels(): Observable<OCRLabelMapping[]> {
    return this.getTenantCollection("mm_inventory_ocr_labels").pipe(
      switchMap((colRef) => {
        const q = query(colRef, orderBy("createdAt", "desc"));

        return collectionData(q, { idField: "id" }) as Observable<
          OCRLabelMapping[]
        >;
      }),
    );
  }
  getAllHealthHazards(): Observable<MM_Hazard_HealthModel[]> {
    return this.getTenantCollection("mm_hazard_health").pipe(
      switchMap((colRef: CollectionReference<DocumentData>) => {
        const q = query(colRef);
        return collectionData(q, { idField: "id" }).pipe(
          map((data: DocumentData[]) => data as MM_Hazard_HealthModel[]),
        );
      }),
    );
  }
  getMMEnvironmentHazardData(
    state?: string,
  ): Observable<MM_Hazard_EnvironmentModel[] | null> {
    return this.getTenantCollection("mm_hazards_environment").pipe(
      switchMap((colRef: CollectionReference<DocumentData>) => {
        const constraints: QueryConstraint[] = [];
        if (state) {
          constraints.push(where("state", "==", state));
        }
        const q = query(colRef, ...constraints);
        return collectionData(q, { idField: "id" }).pipe(
          map((data: DocumentData[]) => {
            const typedData = data as MM_Hazard_EnvironmentModel[];
            return typedData.length ? typedData : [];
          }),
        );
      }),
    );
  }
  getAllHazardsInventoryMsterData(): Observable<HazardsInventoryItem[]> {
    return this.getTenantCollection("mm_inventory_hazards").pipe(
      switchMap((colRef) => {
        const q = query(colRef);
        return collectionData(q, { idField: "id" }) as Observable<
          HazardsInventoryItem[]
        >;
      }),
    );
  }
  getBannedWasteConfig(): Observable<BannedWasteItem[]> {
    const ref$ = this.getTenantCollection("mm_inventory_banned_wastes");

    return ref$.pipe(
      switchMap(
        (ref) =>
          collectionData(ref, { idField: "id" }) as Observable<
            BannedWasteItem[]
          >,
      ),
    );
  }
  async deleteSimulatedAssets(siteId: string, assetUids: string[]) {
    try {
      const db = await this.tenantService.getMyDb();
      const assetsRef = collection(db, `sites/${siteId}/assets`);
      const chunkSize = 10;
      for (let i = 0; i < assetUids?.length; i += chunkSize) {
        const chunk = assetUids.slice(i, i + chunkSize);
        const que = query(assetsRef, where("uid", "in", chunk));
        const snap = await getDocs(que);
        const batch = writeBatch(db);
        snap.docs.forEach((d) => batch.delete(d.ref));

        await batch.commit();
      }
    } catch (err) {
      console.error(err);
    }
  }

  getGuidanceAndDefectData(): Observable<Partial<GuidanceOfflineModel>> {
    const guidanceSubtype$ = this.getTenantCollection("guidance").pipe(
      switchMap(
        (ref) =>
          collectionData(ref, { idField: "id" }) as Observable<GuidanceItem[]>,
      ),
    );

    const defectType$ = this.getTenantCollection("defect_type_master").pipe(
      switchMap(
        (ref) =>
          collectionData(ref, { idField: "id" }) as Observable<DefectType[]>,
      ),
    );

    return combineLatest([guidanceSubtype$, defectType$]).pipe(
      map(([guidanceSubtype, defectType]) => ({
        guidanceSubtype,
        defectType,
      })),
    );
  }

  getActiveRegionsCountryGroupBogPro(): Observable<{
    regions: string[];
    countryGroups: string[];
  }> {
    return new Observable((observer) => {
      const EXCLUDED_COUNTRIES = [
        "China",
        "LH CC",
        "KENYA",
        "Jordan",
        "Lebanon",
        "Trading",
        "Slovenia",
        "LATAM – Americas Digital Center (ADC)",
      ];

      this.getTenantCollection("sites").subscribe((sitesRef) => {
        const sitesQuery = query(
          sitesRef,
          where("isActive", "==", true),
          where("locations.site.active", "==", "Yes"),
        );

        onSnapshot(
          sitesQuery,
          (querySnapshot) => {
            const regionLocationSet = new Set<string>();
            const countryGroupSet = new Set<string>();

            querySnapshot.docs.forEach((doc) => {
              const data = doc.data();
              const regionLocation =
                data?.["locations"]?.["region"]?.["location"];
              const countryGroup = data?.["locations"]?.["country"]?.["group"];
              const countryLocation =
                data?.["locations"]?.["country"]?.["location"];

              if (EXCLUDED_COUNTRIES.includes(countryLocation)) return;
              if (regionLocation) regionLocationSet.add(regionLocation);
              if (countryGroup) countryGroupSet.add(countryGroup);
            });

            const regions = Array.from(regionLocationSet).sort();
            const countryGroups = Array.from(countryGroupSet).sort();

            observer.next({ regions, countryGroups });
          },
          (error) => observer.error(error),
        );
      });
    });
  }

  getProductLines(): Observable<GetProductLinesResponseModel> {
    return this.httpService.GetProductLines();
  }

  addProductLine(
    data: ProductLineMasterTypeModel,
  ): Observable<AddProductLineResponseModel> {
    return this.httpService.AddProductLine(data);
  }

  updateProductLine(
    data: ProductLineMasterTypeModel & { id: string },
  ): Observable<UpdateProductLineResponseModel> {
    return this.httpService.UpdateProductLine(data);
  }

  deleteProductLine(id: string): Observable<DeleteProductLineResponseModel> {
    return this.httpService.DeleteProductLine({ id });
  }

  // ── Site Master: Transports Explosives CRUD ─────────────────────────────────

  getTransportsExplosives(): Observable<GetTransportsExplosivesResponseModel> {
    return this.httpService.GetTransportsExplosives();
  }

  addTransportsExplosive(
    data: TransportsExplosivesMasterTypeModel,
  ): Observable<AddTransportsExplosiveResponseModel> {
    return this.httpService.AddTransportsExplosive(data);
  }

  updateTransportsExplosive(
    data: TransportsExplosivesMasterTypeModel & { id: string },
  ): Observable<UpdateTransportsExplosiveResponseModel> {
    return this.httpService.UpdateTransportsExplosive(data);
  }

  deleteTransportsExplosive(
    id: string,
  ): Observable<DeleteTransportsExplosiveResponseModel> {
    return this.httpService.DeleteTransportsExplosive({ id });
  }

  // ── Site Master: Zone Type CRUD ─────────────────────────────────────────────

  getZoneTypes(): Observable<GetZoneTypesResponseModel> {
    return this.httpService.GetZoneTypes();
  }

  addZoneType(data: ZoneMasterTypeModel): Observable<AddZoneTypeResponseModel> {
    return this.httpService.AddZoneType(data);
  }

  updateZoneType(
    data: ZoneMasterTypeModel & { id: string },
  ): Observable<UpdateZoneTypeResponseModel> {
    return this.httpService.UpdateZoneType(data);
  }

  deleteZoneType(id: string): Observable<DeleteZoneTypeResponseModel> {
    return this.httpService.DeleteZoneType({ id });
  }

  updateFindingFields(
    assessmentId: string,
    findingId: string,
    data: Record<string, any>,
  ): Observable<void> {
    return of();
  }

  updateActionFields(
    assessmentId: string,
    actionId: string,
    data: Record<string, any>,
  ): Observable<void> {
    return of();
  }

  getFindingById(assessmentId: string, findingId: string): Observable<any> {
    return this.getTenantDoc(
      `assessments/${assessmentId}/finding/${findingId}`,
    ).pipe(switchMap((ref) => docData(ref, { idField: "id" })));
  }

  getActionsForFinding(
    assessmentId: string,
    findingId: string,
  ): Observable<any[]> {
    return this.getTenantCollection(`assessments/${assessmentId}/action`).pipe(
      switchMap(
        (colRef) =>
          collectionData(query(colRef, where("findingId", "==", findingId)), {
            idField: "id",
          }) as Observable<any[]>,
      ),
    );
  }
}
